<template>
  <div data-testid="transaction-matching-config-new" class="transaction-matching-config-v2">
    <v-card class="shadow-off" loader-height="2" :loading="loading" :disabled="loading">
      <v-card-text class="d-flex flex-column gap-4">
          <div data-testid="transaction-matching-config-new__header" class="d-flex align-start">
            <div class="d-flex flex-column gap-2">
              <div data-testid="transaction-matching-config-new__breadcrumbs" class="d-flex">
                <p data-testid="transaction-matching-config-new__breadcrumb-home" class="mb-1">Home</p>
                <p class="px-2 mb-1">/</p>
                <p data-testid="transaction-matching-config-new__breadcrumb-metadata" class="mb-1 cursor-pointer" @click.stop="$nova.gotoLink({path: backlink})"> {{ metadata && metadata.description }}</p>
                <p class="px-2 mb-1">/</p>
                <p data-testid="transaction-matching-config-new__breadcrumb-analysis" class="mb-1">{{ analysisDetails && analysisDetails.name }}</p>
              </div>
              <div class="d-flex align-center">
                <h3 data-testid="transaction-matching-config-new__title" class="font-weight-medium ma-0 noTransform">Analysis : {{analysisDetails.name || ""}}</h3>
                <v-tooltip bottom content-class="tooltip-bottom">
                  <template v-slot:activator="{ on, attrs }">
                      <v-btn data-testid="transaction-matching-config-new__refresh-button" v-bind="attrs" v-on="on" class="shadow-off" icon @click="refreshData()">
                          <v-icon data-testid="transaction-matching-config-new__refresh-icon" size="24">mdi-refresh</v-icon>
                      </v-btn>
                  </template>
                  <span>Refresh</span>
                </v-tooltip>
              </div>
            </div>
            <v-spacer></v-spacer>
            <div class="d-flex flex-column align-end gap-2">
              <div class="d-flex align-center gap-2">
                <v-tooltip left :disabled="!isRunDisabled" content-class="tooltip-left">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                        data-testid="transaction-matching-config-new__start-run-button"
                        color="primary"
                        v-if="Object.keys(rundetails).length === 0"
                        class="ml-2 shadow-off"
                        @click="startRun()"
                        :disabled="isRunDisabled || !analysisDetails || !analysisDetails._id"
                      >
                          <v-icon data-testid="transaction-matching-config-new__start-run-icon" class="mr-1">mdi-play</v-icon><span>Start</span>
                      </v-btn>
                      <v-btn
                        data-testid="transaction-matching-config-new__re-run-button"
                        color="primary"
                        v-else
                        class="ml-2 shadow-off"
                        @click="rerunConfirmDialog = true"
                        :disabled="isRunDisabled || !analysisDetails || !analysisDetails._id"
                      >
                        <v-icon data-testid="transaction-matching-config-new__re-run-icon" class="mr-1">mdi-play</v-icon><span>Start</span>
                      </v-btn>
                    </span>
                  </template>
                  <span>Data is being processed with recent updates/sync.<br />Please wait until completion</span>
                </v-tooltip>
                <div v-if="shouldShowRunStatus" class="d-flex align-center">
                  <v-btn data-testid="transaction-matching-config-new__status-finished" color="lightgreen" v-if="statusCheck === TM_STATUS.FINISHED" class="shadow-off green--text" >
                      <v-icon small class="mr-1">mdi-check-circle-outline</v-icon><span>Finished</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__status-failed" color="lightred" v-else-if="statusCheck === TM_STATUS.FAILED" class="shadow-off error--text" >
                      <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon><span>Failed</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__status-in-progress" color="lightblue" v-else-if="statusCheck === TM_STATUS.IN_PROGRESS" class="shadow-off blue--text" >
                      <v-icon small class="mr-1">mdi-sync</v-icon><span>In Progress</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__status-in-progress" color="lightblue" v-else-if="statusCheck === TM_STATUS.NOT_STARTED" class="shadow-off blue--text" >
                      <v-icon small class="mr-1">mdi-sync</v-icon><span>In Progress</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__status-in-progress" color="lightblue" v-else-if="statusCheck === TM_STATUS.MANUAL_MATCHING_IN_PROGRESS" class="shadow-off blue--text" >
                      <v-icon small class="mr-1">mdi-sync</v-icon><span>Manual Match/Tag In-Progress</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__status-in-progress" color="lightblue" v-else class="shadow-off blue--text" >
                      <v-icon small class="mr-1">mdi-sync</v-icon><span>In Progress</span>
                  </v-btn>
                </div>
                <div v-if="shouldShowActionStatus" class="d-flex align-center">
                  <v-btn data-testid="transaction-matching-config-new__action-status-queued" color="lightyellow" v-if="activeActionStage === TM_ACTION_STAGES.QUEUED" class="shadow-off orange--text" >
                      <v-icon small class="mr-1">mdi-clock-outline</v-icon><span>Queued</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__action-status-in-progress" color="lightblue" v-else-if="activeActionStage === TM_ACTION_STAGES.IN_PROGRESS" class="shadow-off blue--text" >
                      <v-icon small class="mr-1">mdi-sync</v-icon><span>In Progress</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__action-status-completed" color="lightgreen" v-else-if="activeActionStage === TM_ACTION_STAGES.COMPLETED" class="shadow-off green--text" >
                      <v-icon small class="mr-1">mdi-check-circle-outline</v-icon><span>Finished</span>
                  </v-btn>
                  <v-btn data-testid="transaction-matching-config-new__action-status-failed" color="lightred" v-else-if="activeActionStage === TM_ACTION_STAGES.FAILED" class="shadow-off error--text" >
                      <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon><span>Failed</span>
                  </v-btn>
                </div>
                <div v-if="shouldShowStageInfoSection">
                  <div v-if="shouldShowStageInfoControls" class="d-flex">
                    <v-spacer></v-spacer>
                    <v-menu data-testid="transaction-matching-config-new__stage-info-menu" open-on-hover transition="scale-transition" elevation-0 max-width="300" offset-y v-if="shouldShowStageInfoMenu">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn data-testid="transaction-matching-config-new__stage-info-button" text icon color="lighten-2" v-bind="attrs" v-on="on">
                                <v-icon data-testid="transaction-matching-config-new__stage-info-icon">mdi-information</v-icon>
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-text data-testid="transaction-matching-config-new__stage-message" class="text-6 font-weight-bold">
                                {{ stageInfoMessage }}
                            </v-card-text>
                        </v-card>
                    </v-menu>
                  </div>
                </div>
              </div>
              <div>
                <activity-log
                  :resource-id="analysisDetails.bpid"
                  :row-description="analysisDetails.name"
                  data-testid="tasklist__activity-log"
                />
              </div>
            </div>
          </div>
          <div data-testid="transaction-matching-config-new__loading-skeleton" v-if="!initialDataLoaded">
              <v-skeleton-loader type="table-heading, list-item-two-line, article, article"></v-skeleton-loader>
          </div>
          <div data-testid="transaction-matching-config-new__no-bot-details" class="py-8" v-else-if="!botdetails._id">
              <div class="body-1 mb-4">No {{$constants.BOT_NAME}} details found</div>
          </div>
          <div data-testid="transaction-matching-config-new__bot-not-run" v-else-if="metadata.stage === 3 && Object.keys(rundetails).length === 0" class="pa-8 title text-center grey--text">{{$constants.BOT_NAME}} did not run </div>
          <div v-else>
              <div v-if="metadata.stage !== 3">
                <v-alert data-testid="transaction-matching-config-new__alert-save-rules" v-if="!savedParameters && hasParameters" type="info" text dense colored-border border="left">Save the Rules to proceed</v-alert>
                <v-alert data-testid="transaction-matching-config-new__alert-upload-files" v-else-if="!savedFiles && hasFiles" type="info" text dense colored-border border="left">Upload all the required files to start the analysis</v-alert>
              </div>
              
              <v-stepper data-testid="transaction-matching-config-new__stepper" v-model="e1" class="mt-0" elevation="0" non-linear>
                <v-stepper-header data-testid="transaction-matching-config-new__stepper-header" class="shadow-off h-50 stepper-header--custom" >
                    <v-stepper-step data-testid="transaction-matching-config-new__step-input-data" editable step="1" class="pa-2 pt-2 pb-2" color="caccounting">Input Data</v-stepper-step>
                    <v-divider class="ma-2"></v-divider>
                    <v-stepper-step data-testid="transaction-matching-config-new__step-apply-filter" editable step="2" class="pa-2 pt-2 pb-2" color="caccounting">Apply Filter</v-stepper-step>
                    <v-divider class="ma-2"></v-divider>
                    <v-stepper-step data-testid="transaction-matching-config-new__step-mapping-masters" step="3" editable class="pa-2 pt-2 pb-2" color="caccounting" @click="addfilterConfimation()">Mapping Masters</v-stepper-step>
                    <v-divider class="ma-2"></v-divider>
                    <v-stepper-step data-testid="transaction-matching-config-new__step-matching-routines" step="4" editable class="pa-2 pt-2 pb-2" color="caccounting">Matching Routines</v-stepper-step>
                    <v-divider class="ma-2"></v-divider>
                    <v-stepper-step data-testid="transaction-matching-config-new__step-results" step="5" editable class="pa-2 pt-2 pb-2" color="caccounting" @click="annexergenerateAll(analysisDetails.filecount,selectedresulttab,resultTab = 0);addrulesConfimation()"> Results </v-stepper-step>
                    <v-divider class="ma-2" v-if="analysisDetails.isFIFORequired || analysisDetails.isReconciliationRequired"></v-divider>
                    <v-stepper-step data-testid="transaction-matching-config-new__step-fifo" step="6" editable class="pa-2 pt-2 pb-2" color="caccounting" v-if="analysisDetails.isFIFORequired" @click="fifiCollection()"> FIFO </v-stepper-step>
                    <v-divider class="ma-2" v-if="analysisDetails.isFIFORequired && analysisDetails.isReconciliationRequired"></v-divider>
                    <v-stepper-step data-testid="transaction-matching-config-new__step-reconciliation" :step="analysisDetails.isFIFORequired ? 7 : 6" editable class="pa-2 pt-2 pb-2" color="caccounting" v-if="analysisDetails.isReconciliationRequired" @click="loadReconciliation()">Reconciliation</v-stepper-step>
                </v-stepper-header>
                <v-stepper-items>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-1" step="1" class="shadow-off pt-2 pb-0 pl-0 pr-0">
                      <div v-if="isV2Mode">
                        <StepInputDataV2 :files="setupList.file" :filevalues="uploaddetails" :editable="true" :storepath="'/v2/bot/analysis/storefile/' + analysisDetails.bpid" :fileproerr="fileprocesserrors" :fileprostage="fileprocessstage" :fileuppercent="fileuploadpercent" :filesdataPros="filesdata" :fileconfig="fileConfigInfo" @startloading="loading = true" @stoploading="loading = false" @storedData="refreshFileData()" :fileuploadeCount="analysisDetails.filecount" :botId="analysisDetails.bpid" />
                      </div>
                      <StepFileUploads v-else :setupList="setupList" :uploaddetails="uploaddetails" :analysisDetails="analysisDetails" :fileprocesserrors="fileprocesserrors" :fileprocessstage="fileprocessstage" :fileuploadpercent="fileuploadpercent" :filesdata="filesdata" :fileConfigInfo="fileConfigInfo" @startloading="loading = true" @stoploading="loading = false" @storedData="refreshFileData()" @continue="e1 = 2" />
                    </v-stepper-content>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-2" step="2" class="shadow-off pt-2 pb-0 pl-0 pr-0">
                      <StepFilterConfigV2
                        v-if="isV2Mode"
                        :formatedData="formatedData"
                        :headers="headers"
                        :setupList="setupList"
                        :filtercolums="filtercolums"
                        :fileConfigInfo="fileConfigInfo"
                        :operatorType="operatorType"
                        :filterrowType="filterrowTypeV2"
                        :dialogLoading="addfilterdialogloading"
                        @add-filter-v2="addfilterV2"
                        @update-filter-v2="updatefilterV2"
                        @delete-filter-v2="deletefilterV2"
                        @toggle-filter-status="toggleFilterStatusV2"
                        @save-order-v2="saveOrderV2"
                        @reorder-filters="handleReorderFilters"
                        @back="e1 = 1"
                        @continue="addfilterConfimation()"
                      />
                      <StepFilterConfig
                        v-else
                        :formatedData="formatedData" :headers="headers" :setupList="setupList"
                        @addfilterbox="addfilterbox" @save-order="saveOrderV2"
                        @editfilterboxPush="editfilterboxPush" @deletefilterboxPush="deletefilterboxPush"
                        @reorder-filters="handleReorderFilters" @back="e1 = 1" @continue="addfilterConfimation()"
                      />
                    </v-stepper-content>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-3" step="3" class="shadow-off pt-2 pb-0 pl-0 pr-0">
                      <StepMiddlewareConfigV2
                        v-if="isV2Mode"
                        :formatedDataMiddeleware="formatedDataMiddeleware"
                        :setupList="setupList"
                        :fileConfigInfo="fileConfigInfo"
                        @addMiddlewarebox="addMiddlewarebox"
                        @editmiddlewarebox="editmiddlewarebox"
                        @deletemiddlewarebox="deletemiddlewarebox"
                        @changeMid="changeMid"
                      />
                      <StepMiddlewareConfig
                        v-else
                        :formatedDataMiddeleware="formatedDataMiddeleware"
                        :middlewareHeader="middlewareHeader"
                        :setupList="setupList"
                        :isV2Mode="isV2Mode"
                        @addMiddlewarebox="addMiddlewarebox"
                        @editmiddlewarebox="editmiddlewarebox"
                        @deletemiddlewarebox="deletemiddlewarebox"
                        @changeMid="changeMid"
                        @back="e1 = 2"
                        @continue="e1 = 4"
                      />
                    </v-stepper-content>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-4" step="4" class="shadow-off pt-2 pb-0 pl-0 pr-0">
                      <div v-if="isV2Mode">
                        <StepMatchingRoutinesV2
                          :filenames="filenames"
                          :columns="columns"
                          :rulesdata="rulesdata"
                          :fileMapshow="fileMapshow"
                          @toggle-file-map="fileMapshow = !fileMapshow"
                          @saveInputParameters="saveInputParameters"
                          @saveRuleOrderV2="saveRuleOrderV2"
                          @openAddEditRuleDialog="openAddEditRuleDialog"
                          @deleteRule="deleteRuleV2"
                          @reorder-rules="handleReorderRules"
                          @back="e1 = 3"
                          @continue="e1 = 5"
                        />
                      </div>
                      <StepRulesConfig v-else :filenames="filenames" :columns="columns" :rulesdata="rulesdata" :fileMapshow="fileMapshow" @saveInputParameters="saveInputParameters" @openAddEditRuleDialog="openAddEditRuleDialog" @deleteRule="(index) => rulesdata.splice(index, 1)" @reorder-rules="handleReorderRules" @toggle-file-map="fileMapshow = !fileMapshow" @back="e1 = 3" @continue="e1 = 5" />
                    </v-stepper-content>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-5" step="5" class="shadow-off pt-2 pb-0 pl-0 pr-0">
                        <TransactionMatchingResultsV2
                            v-if="isV2Mode"
                            :status-check="statusCheck"
                            :analysis-details="analysisDetails"
                            :run-details="rundetailsV2"
                            :file-config-info="fileConfigInfo"
                            :file-list="setupList.file || []"
                            :loading="loading"
                            :download-history="downloadExcelHistory"
                            :loading-history="loadingExcelHistory"
                            :result-types="formData.results || []"
                            @refresh-data="refreshData"
                            @generate-result="GenerateResult"
                            @refresh-download-history="fetchDownloadHistoryBackground"
                            @download-history-file="downloadHistoryFile"
                        />
                        <TransactionMatchingResults
                            v-else
                            :status-check="statusCheck"
                            :analysis-details="analysisDetails"
                            :rundetails="rundetails"
                            :verificationlist="verificationlist"
                            :selected-result-tab.sync="selectedresulttab"
                            :annexurelist="annexurelist"
                            :result-tab.sync="resultTab"
                            :result-tab-item="resultTabItem"
                            :ailoadingimage="ailoadingimage"
                            :stagefilter-a-i="stagefilterAI"
                            :tabletype="tabletype"
                            :loading="loading"
                            :download-excel-history="downloadExcelHistory"
                            :loading-excel-history="loadingExcelHistory"
                            :executionbtn-a-i="executionbtnAI"
                            :table1twofileglobal-info="table1twofileglobalInfo"
                            :table1twoglobal-list="table1twoglobalList"
                            :table1pagecounttwofile="table1pagecounttwofile"
                            :table1twofile.sync="table1twofile"
                            :table1collectiontwofile="table1collectiontwofile"
                            :table-one-items-two-file="tableOneItemtwofile()"
                            :table-one-itemtwofile-keys="tableOneItemtwofileKeys"
                            :stagetable1twofile.sync="stagetable1twofile"
                            :table2twofileglobal-info="table2twofileglobalInfo"
                            :table2twoglobal-list="table2twoglobalList"
                            :table2pagecounttwofile="table2pagecounttwofile"
                            :table2twofile.sync="table2twofile"
                            :table2collectiontwofile="table2collectiontwofile"
                            :table-two-items-two-file="tableTwoItemtwofile()"
                            :table-two-itemtwofile-keys="tableTwoItemtwofileKeys"
                            :stagetable2twofile.sync="stagetable2twofile"
                            :globale-collection="GlobaleCollection"
                            :pagecount="pagecount"
                            :page.sync="page"
                            :page-items="pageItems()"
                            :page-items-keys="pageItemsKeys"
                            :stagefilter.sync="stagefilter"
                            :selectedcollection="selectedcollection"
                            :combo-annexer="comboAnnexer"
                            :has-consolidated-annexure="hasConsolidatedAnnexure"
                            :summery-combo="summeryCombo"
                            :selected-summery-filter.sync="selectedSummeryFilter"
                            :annexercombo-keys="annexercomboKeys"
                            :filter-combo.sync="filterCombo"
                            :filter-array="filterArray"
                            :selectcollection="selectcollection"
                            :selectkey="selectkey"
                            :pagecountnew="pagecountnew"
                            :pagenew.sync="pagenew"
                            :page-items-new="pageItemsNew()"
                            :table1global-info="table1globalInfo"
                            :table1global-list="table1globalList"
                            :table1pagecount="table1pagecount"
                            :table1.sync="table1"
                            :table-one-item="tableOneItem()"
                            :table-one-item-keys="tableOneItemKeys"
                            :table1collection="table1collection"
                            :stagefiltertable1.sync="stagefiltertable1"
                            :table2global-info="table2globalInfo"
                            :table2global-list="table2globalList"
                            :table2pagecount="table2pagecount"
                            :table2.sync="table2"
                            :table-two-item="tableTwoItem()"
                            :table-two-item-keys="tableTwoItemKeys"
                            :table2collection="table2collection"
                            :stagefiltertable2.sync="stagefiltertable2"
                            :stagefilter-new.sync="stagefilterNew"
                            :file-config-info="fileConfigInfo"
                            @generate-annexure-list="generateAnnexurelist"
                            @collection-get="collectionGet"
                            @matched-a-i-item="MatchedAIitem"
                            @generate-result="GenerateResult"
                            @fetch-download-history-background="fetchDownloadHistoryBackground"
                            @download-history-file="downloadHistoryFile"
                            @bulk-manual-matching="bulkManualMatching"
                            @bulk-manual-matching-consolidated="bulkManualMatchingConsolidated"
                            @setup-a-i="setupAI"
                            @start-run-a-i-execution="startRunAIExecution"
                            @clear-filter-twofile="clearFiltertwofile"
                            @pagination-table1-two-file="pagenationtable1twofile"
                            @table1-global-two-file="table1globaltwofile"
                            @move-transaction="moveTransaction"
                            @pagination-table2-two-file="pagenationtable2twofile"
                            @table2-global-two-file="table2globaltwofile"
                            @open-match-dialog="openMatchDialog"
                            @clear-filterall="clearFilterall"
                            @pagination-ai="pagenationAI"
                            @global-filter-pop="globalfilterpop"
                            @combo-collection-get="comboCollectionGet"
                            @bulk-manual-matching-onefile="bulkManualMatchingOnefile"
                            @reomve-gobalefilter="reomveGobalefilter"
                            @pagination-single-api="pagenationsingleAPI"
                            @clear-filter="clearFilter"
                            @pagination-table1="pagenationtable1"
                            @table1-global="table1global"
                            @move-transaction-new="moveTransactionNew"
                            @pagination-table2="pagenationtable2"
                            @table2-global="table2global"
                            @open-match-dialog-new="openMatchDialogNew"
                            @global-filter-pop-new="globalfilterpopNew"
                            @download-consolidated-excel="downloadConsolidatedExcel"
                            @refresh-data="refreshData"
                        />
                    </v-stepper-content>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-6" step="6" class="shadow-off pt-2 pb-0 pl-0 pr-0" v-if="analysisDetails.isFIFORequired">
                        <StepFifoAnalysis :fifoalaysis="analysisDetails.isFIFORequired" :loading="loading" :pagecountfifo="pagecountfifo" :pagefifo.sync="pagefifo" :selectallfifo.sync="selectallfifo" :selectFifo.sync="selectFifo" :items="pageItemsfifo()" @startRunFifo="startRunFifo" @startRunFifoExecution="startRunFifoExecution(analysisDetails.todate,analysisDetails.fromdate)" @downloadResult="(type) => GenerateResult(type)" @toggleSelectAll="toggleSelectAll" />
                    </v-stepper-content>
                    <v-stepper-content data-testid="transaction-matching-config-new__step-content-reconciliation" :step="analysisDetails.isFIFORequired ? 7 : 6" class="shadow-off pt-2 pb-0 pl-0 pr-0" v-if="analysisDetails.isReconciliationRequired">
                        <transaction-matching-reconciliation
                            ref="reconciliationComponent"
                            :file-count="analysisDetails.filecount"
                            :run-details="rundetails"
                            :analysis-details="analysisDetails"
                            :verification-list="verificationlist"
                            @open-reconciliation-dialogue="openRecoincilationDialogue"
                            @download-result="downloadResult"
                        />
                    </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
          </div>
      </v-card-text>
    </v-card>

    <AddRuleDialogV2
      v-if="isV2Mode"
      v-model="addeditdialog"
      :addeditdata="addeditdata"
      :loading="addeditloading"
      :rulenaturelist="rulenaturelist"
      :ruletypelist="ruletypelist"
      :columns="columns"
      :selectedrulecolumn="selectedrulecolumn"
      :addeditdatacols="addeditdatacols"
      :strmatchtype="strmatchtype"
      :filenames="filenames"
      :isAggregationMandatory="isAggregationMandatory"
      :aggregationtype="aggregationtype"
      :aggregationtypesection="aggregationtypesection"
      :aggregationError="aggregationError"
      :setupList="setupList"
      @save-rule-v2="updateRuleV2"
    />
    <RuleConfigDrawer
        v-else
        v-model="addeditdialog"
        :addeditdata="addeditdata"
        :loading="addeditloading"
        :rulenaturelist="rulenaturelist"
        :ruletypelist="ruletypelist"
        :columns="columns"
        :selectedrulecolumn="selectedrulecolumn"
        :addeditdatacols="addeditdatacols"
        :strmatchtype="strmatchtype"
        :filenames="filenames"
        :isAggregationMandatory="isAggregationMandatory"
        :aggregationtype="aggregationtype"
        :aggregationtypesection="aggregationtypesection"
        :aggregationError="aggregationError"
        @update:selectedrulecolumn="selectedrulecolumn = $event"
        @save-rule="updateRule"
    />
    <MatchSelectionDrawers
        :comparedialog.sync="comparedialog"
        :comparedialogNew.sync="comparedialogNew"
        :compareloading="compareloading"
        :compareloadingNew="compareloadingNew"
        :twofileselectiteamComparing="twofileselectiteamComparing"
        :selectiteamComparing="selectiteamComparing"
        :searchvalue.sync="searchvalue"
        :searchvalueNew.sync="searchvalueNew"
        :selectedids.sync="selectedids"
        :selectedidsNew.sync="selectedidsNew"
        :manualglobaltwofileList="manualglobaltwofileList"
        :manualglobalList="manualglobalList"
        :manualglobalInfotwofile="manualglobalInfotwofile"
        :manualglobalInfo="manualglobalInfo"
        :pagecountnewmatchnew="pagecountnewmatchnew"
        :pagenewmatchnew.sync="pagenewmatchnew"
        :loading="loading"
        :selectcollectiontwofile="selectcollectiontwofile"
        :GlobaleCollectionManual="GlobaleCollectionManual"
        :formateddataforfilter="formateddataforfilter"
        :formateddataforfilterNew="formateddataforfilterNew"
        :filterselectkey="filterselectkey"
        :filterselectkeyNew="filterselectkeyNew"
        :comparingitem="comparingitem"
        :comparingitemNew="comparingitemNew"
        :singleglobal="singleglobal"
        @filter-two="filterMatchItemsNewfilterTwo()"
        @clear-filter-two="clearFilterManualtwofile(manualglobalInfotwofile,'manual')"
        @paginate-two="pagenationIManuel(selectcollectiontwofile, $event)"
        @filter-column-two="manualGlobaltwofile($event, selectcollectiontwofile,'manual','global')"
        @match-two="moveTransaction(comparingitem, 'match',selectcollectiontwofile)"
        @filter-one="filterMatchItemsNewfilter()"
        @clear-filter-one="clearFilterManual(manualglobalInfo,'manual')"
        @paginate-one="pagenationtsingleManuel(GlobaleCollectionManual, $event)"
        @filter-column-one="manualGlobal($event, singleglobal,'manual','global')"
        @match-one="moveTransactionNew(comparingitemNew, 'match',singleglobal)"
    />
    <ReconciliationDrawer
        data-testid="transaction-matching-config-new__add-reconciliation-drawer"
        :dialog.sync="recoincilationDialogue"
        :recoincilationItem="recoincilationItem"
        :recoincilationErrorItem="recoincilationErrorItem"
        @add="addRecoincilationItem(recoincilationCollection)"
        @update:recoincilationItem="recoincilationItem = $event"
    />
    <FilterDefinitionDrawer
        data-testid="transaction-matching-config-new__add-update-filter-drawer"
        :dialog.sync="addfilterdialog"
        :loading="addfilterdialogloading"
        :heading="isEditing ? 'Update Filter' : 'Add Filter'"
        :item="filterobject"
        :errorItem="filterobjecterror"
        :excludeTypes="filterrowType"
        :tables="setupList.file"
        :columns="filtercolums"
        :fileConfigInfo="fileConfigInfo"
        :operators="operatorType"
        :isEditing="isEditing"
        @update="updatefilterPush(filterobject._id)"
        @add="addfilterPush()"
    />
    <UnsavedChangesDrawer
        data-testid="transaction-matching-config-new__filter-confirmation-drawer"
        v-model="addfilterconfim"
        :dailogboxOnly="true"
        heading="Confirmation"
        width="400"
        :loading="addfilterconfimloading"
        message="Please save your changes made on the Filter screen"
        @save="addfilter('continue')"
        @cancel="getfilterlistCancel()"
    />
    <UnsavedChangesDrawer
        data-testid="transaction-matching-config-new__rules-confirmation-drawer"
        v-model="addrulesconfim"
        heading="Confirmation"
        width="400"
        :loading="addrulesconfimloading"
        message="Please save your changes made on the matching routines screen"
        @save="saveInputParameters()"
        @cancel="getruleslistCancel()"
    />
    <MappingMasterDrawer
        data-testid="transaction-matching-config-new__mapping-master-drawer"
        :dialog.sync="addMiddlewaredialog"
        :loading="addMiddlewaredialogloading"
        :item="middlewareObject"
        :errorItem="middlewareObjecterror"
        :files="setupList.file"
        :fileConfigInfo="fileConfigInfo"
        :fileValue="middFile"
        :isV2Mode="isV2Mode"
        @update:fileValue="middFile = $event"
        @update="updateMiddleware"
        @add="addMiddleware"
    />
    <FifoExecutionResultsDrawer
        data-testid="transaction-matching-config-new__fifo-execution-results-drawer"
        :dialog.sync="runfifoppop"
        :results="fifoexecutionResult"
    />
    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-properties-drawer"
        :dialog.sync="selectfilterdialog"
        heading="Filter properties"
        :loading="selectfilterdialogloading"
        :filterInfo="{ column: selectFilter, type: typeOffilter }"
        :filterObject="globalfilterObject"
        :errorObject="globalfilterObjecterror"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="filterArray"
        @clear="reomveGobalefilter(selectcollection,selectkey,mathcFilter,matchTypeFilter)"
        @add="runfilter(globalfilterObject.operation,globalfilterObject.range,globalfilterObject.range1,globalfilterObject.range2,selectFilter,typeOffilter,selectcollection,selectkey,mathcFilter,matchTypeFilter)"
        @delete="deleteGobalefilter(filterArray, $event, selectcollection,selectkey,mathcFilter,matchTypeFilter)"
    />
    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-table2-drawer"
        :dialog.sync="globalfilter2"
        heading="Filter Properties Table 2"
        :loading="globalfilter2loading"
        :filterInfo="{ column: table2globalInfo.selectecolumn, type: table2globalInfo.dataoftype === 'String' ? 'Text' : table2globalInfo.dataoftype }"
        :filterObject="globalfilterObjecttable2"
        :errorObject="globalfilterObjecterrortable2"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="table2globalList"
        @clear="clearFilter(table2globalInfo,'table2')"
        @add="runtable2filter(globalfilterObjecttable2,table2globalInfo)"
        @delete="deletetable2(table2globalList,$event,table2globalInfo)"
    />
    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-table1-twofile-drawer"
        :dialog.sync="globalfilter1twofile"
        heading="Filter Properties Table 1"
        :loading="globalfilter1twofileloading"
        :filterInfo="{ column: table1twofileglobalInfo.selectecolumn, type: table1twofileglobalInfo.dataoftype === 'String' ? 'Text' : table1twofileglobalInfo.dataoftype }"
        :filterObject="globaltwofilter1"
        :errorObject="globaltwofilter1error"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="table1twoglobalList"
        @clear="clearFiltertwofile(table1twofileglobalInfo,'table1')"
        @add="runtable1filtertwofile(globaltwofilter1,table1twofileglobalInfo)"
        @delete="deletetable1twofile(table1twoglobalList,$event,table1twofileglobalInfo)"
    />
    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-table2-twofile-drawer"
        :dialog.sync="globalfilter2twofile"
        heading="Filter Properties Table 2"
        :loading="globalfilter2twofileloading"
        :filterInfo="{ column: table2twofileglobalInfo.selectecolumn, type: table2twofileglobalInfo.dataoftype === 'String' ? 'Text' : table2twofileglobalInfo.dataoftype }"
        :filterObject="globaltwofilter2"
        :errorObject="globaltwofilter2error"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="table2twoglobalList"
        @clear="clearFiltertwofile(table2twofileglobalInfo,'table2')"
        @add="runtable2filtertwofile(globaltwofilter2,table2twofileglobalInfo)"
        @delete="deletetable2twofile(table2twoglobalList,$event,table2twofileglobalInfo)"
    />

    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-table1-drawer"
        :dialog.sync="globalfilter1"
        heading="Filter Properties Table 1"
        :loading="globalfilter1loading"
        :filterInfo="{ column: table1globalInfo.selectecolumn, type: table1globalInfo.dataoftype === 'String' ? 'Text' : table1globalInfo.dataoftype }"
        :filterObject="globalfilterObjecttable1"
        :errorObject="globalfilterObjecterrortable1"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="table1globalList"
        @clear="clearFilter(table1globalInfo,'table1')"
        @add="runtable1filter(globalfilterObjecttable1,table1globalInfo)"
        @delete="deletetable1(table1globalList,$event,table1globalInfo)"
    />

    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-manual-drawer"
        :dialog.sync="globalfiltermanual"
        heading="Filter Properties Manual"
        :loading="globalfiltermanualloading"
        :filterInfo="{ column: manualglobalInfo.selectecolumn, type: manualglobalInfo.dataoftype === 'String' ? 'Text' : manualglobalInfo.dataoftype }"
        :filterObject="globalfilterobjectmanual"
        :errorObject="globalfilterobjectmanualerror"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="manualglobalList"
        @clear="clearFilterManual(manualglobalInfo,'manual')"
        @add="runmaunalfilter(globalfilterobjectmanual,manualglobalInfo)"
        @delete="deletemanual(manualglobalList,$event,manualglobalInfo)"
    />

    <FilterPropertiesDrawer
        data-testid="transaction-matching-config-new__filter-manual-twofile-drawer"
        :dialog.sync="globalfiltertwofilemanual"
        heading="Filter Properties Manual"
        :loading="globalfiltermanualtwofileloading"
        :filterInfo="{ column: manualglobalInfotwofile.selectecolumn, type: manualglobalInfotwofile.dataoftype === 'String' ? 'Text' : manualglobalInfotwofile.dataoftype }"
        :filterObject="globalfilterobjectmanualtwofile"
        :errorObject="globalfilterobjectmanualerrortwofile"
        :itemsNumber="filteritemNumber"
        :itemsString="filteritemString"
        :itemsDate="filteritemDate"
        :filterList="manualglobaltwofileList"
        @clear="clearFilterManualtwofile(manualglobalInfotwofile,'manual');globalfiltertwofilemanual=false"
        @add="runmaunalfiltertwofile(globalfilterobjectmanualtwofile,manualglobalInfotwofile)"
        @delete="deletemanualtwofile(manualglobaltwofileList,$event,manualglobalInfotwofile)"
    />


    <AISetupDrawer
        data-testid="transaction-matching-config-new__ai-setup-drawer"
        :dialog.sync="setupAIdilog"
        :loading="setupAIdilogloading"
        :filenames="filenames"
        :columns="copycolumns"
        :setupObj="setupaiObj"
        @reset="resetAI()"
        @delete="(payload) => deleteAIsetup(payload.name, payload.index, payload.type)"
        @run="startRunAI(setupaiObj)"
    />

    <ManualMatchDrawer
        data-testid="transaction-matching-config-new__bulk-match-twofile-drawer"
        v-model="bulkMatchtwofile"
        :files="fileuplaoded"
        :resetTrigger="resetFlag"
        @update:files="FilesUpload"
        @submit="manualmatchingFunTwofile()"
    />

    <ManualMatchDrawer
        data-testid="transaction-matching-config-new__bulk-match-onefile-drawer"
        v-model="bulkMatchonefile"
        :files="fileuplaoded"
        :resetTrigger="resetFlag"
        @update:files="FilesUploadOne"
        @submit="manualmatchingFunOnefile()"
    />


    <lb-dialog
        v-if="rerunConfirmDialog"
        v-model="rerunConfirmDialog"
        heading="Confirm Restart"
        width="480"
        persistent
        data-testid="transaction-matching-config-new__rerun-confirm-dialog"
    >
        <template v-slot:body>
            <div class="">
                <span>You are about to Restart the Transaction Matching agent with updated rules.<br>
                Existing matches may be recalculated and results may change.<br /><br />
                <strong>Do you want to continue?</strong></span>
            </div>
        </template>
        <template v-slot:actions>
            <v-spacer />
            <v-btn text @click="rerunConfirmDialog = false" class="secondaryBtn" data-testid="transaction-matching-config-new__rerun-cancel-btn">No</v-btn>
            <v-btn color="primary" class="ml-2" @click="rerunConfirmDialog = false; startRun()" data-testid="transaction-matching-config-new__rerun-confirm-btn"><v-icon data-testid="transaction-matching-config-new__start-run-icon" class="mr-1">mdi-play</v-icon>Yes</v-btn>
        </template>
    </lb-dialog>
  </div>
</template>

<script>
import TransactionMatchingReconciliation from './TransactionMatchingReconciliation.vue'

import StepFileUploads from './components/TransationMatching/StepFileUploads.vue';
import StepInputDataV2 from './components/TransactionMatchingV2/StepInputDataV2.vue';
import AddRuleDialogV2 from './components/TransactionMatchingV2/AddRuleDialogV2.vue';
import StepMatchingRoutinesV2 from './components/TransactionMatchingV2/StepMatchingRoutinesV2.vue';
import TransactionMatchingResultsV2 from './components/TransactionMatchingV2/TransactionMatchingResultsV2.vue';
import StepFilterConfig from './components/TransationMatching/StepFilterConfig.vue';
import StepFilterConfigV2 from './components/TransactionMatchingV2/StepFilterConfigV2.vue';
import StepMiddlewareConfig from './components/TransationMatching/StepMiddlewareConfig.vue';
import StepMiddlewareConfigV2 from './components/TransactionMatchingV2/StepMiddlewareConfigV2.vue';
import StepRulesConfig from './components/TransationMatching/StepRulesConfig.vue';
import TransactionMatchingResults from './components/TransationMatching/TransactionMatchingResults.vue';
import StepFifoAnalysis from './components/TransationMatching/StepFifoAnalysis.vue';
import RuleConfigDrawer from './components/TransationMatching/RuleConfigDrawer.vue';
import MatchSelectionDrawers from './components/TransationMatching/MatchSelectionDrawers.vue';
import ReconciliationDrawer from './components/TransationMatching/ReconciliationDrawer.vue';
import MappingMasterDrawer from './components/TransationMatching/MappingMasterDrawer.vue';
import FilterDefinitionDrawer from './components/TransationMatching/FilterDefinitionDrawer.vue';
import FifoExecutionResultsDrawer from './components/TransationMatching/FifoExecutionResultsDrawer.vue';
import FilterPropertiesDrawer from './components/TransationMatching/FilterPropertiesDrawer.vue';
import AISetupDrawer from './components/TransationMatching/AISetupDrawer.vue';
import UnsavedChangesDrawer from './components/TransationMatching/UnsavedChangesDrawer.vue';
import ManualMatchDrawer from './components/TransationMatching/ManualMatchDrawer.vue';
import ActivityLog from '@/modules/financialclose/components/ActivityLog/ActivityLog.vue';
import { mapGetters, mapActions } from 'vuex';
import { TM_STATUS, TM_ACTION_STAGES, TM_STATUS_TRACKED_ACTIONS, MS_PER_HOUR, FILE_STAGES, TM_RECONCILIATION } from '../../utils/constants.js';

     export default {
       data() {
         return {
           isV2Mode: false,
           TM_STATUS,
           TM_RECONCILIATION,
           TM_ACTION_STAGES,
           e1: 1,
           botid: "6474e49c00b1f12f6a2a8065",
           project: "",
           loading: false,
           initialDataLoaded: false,
           id: "",
           tab: 0,
           metadata: {},
           botproject: {},
           botdetails: {},
           setupList: {},
           verificationlist: [],
           statusCheck: TM_STATUS.NOT_STARTED,
           runDetailUpdatedAt: "",
           polingstatges:'',
           intervalId: null,
           stageMassage:'',
           hasParameters: false,
           savedParameters: false,
           inputparametersvalues: {},
           uploaddetails: {},
           fileUploadStatusFiles: [],
           fileuploaddata: {},
           fileprocesserrors: {},
           fileuploadpercent: {},
           fileprocessstage: {},
           filesdata: {},
           processedFiles: false,
           savedFiles: false,
           hasFiles: false,
           rundetails: {},
           annexurelist: [],
           selectedresulttab: 0,
           selectedannexure: "",
           currentannexuredata: [],
           originalcurrentannexuredata: [],
           annexurenames: {},
           annexuredata: {},
           fileConfigInfo: {},
           rundetailsall:[],
           stagefilter: [],
           stagetable1twofile:[],
           stagetable2twofile:[],
           downloadExcelMenu: false,
           downloadExcelMenuOnefile: false,
           downloadConsolidatedExcelMenu: false,
           downloadAIExcelMenuOnefile: false,
           excelHistoryPollingInterval: null,
           fileStatusPollingInterval: null,
           stagefilterNew: [],
           stagefiltertable1:[],
           stagefiltertable2:[],
           stagefilterAI:[],
           columns: {},
           copycolumns:{},
           filenames: [],
           colors: [],
           addeditdialog: false,
           addeditloading: false,
           startRunConfirmDialog: false,
           rerunConfirmDialog: false,
           addeditdatakey: {},
           addeditdata: {},
           addeditdatacols: {},
           selectedrulecolumn: [],
           aggregationtypesection: {},
           aggregationError: '',
           dataforfilter: [],
           formateddataforfilter: [],
           formateddataforfilterNew: [],
           originalFormatedData: [],
           originalFormatedDatatwo: [],
           searchvalue: "",
           search:'',
           searchvalueNew: "",
           collectionnames: {},
           comparedialog: false,
           compareloading: false,
           comparedialogNew: false,
           compareloadingNew: false,
           comparingitem: {},
           comparingitemNew: {},
           selectedids: [],
           selectedidsNew: [],
           filterselectkey: "",
           filterselectkeyNew: "",
           rulenaturelist: [{
               name: "Automated",
               value: "Automated"
             },
             {
               name: "Suggested",
               value: "Suggested"
             }
           ],
           ruletypelist: [{
               name: "One to One (1:1)",
               value: "1:1"
             },
             {
               name: "One to Many (1:M)",
               value: "1:M"
             },
             {
               name: "Many to One (M:1)",
               value: "M:1"
             },
             {
               name: "Many to Many (M:M)",
               value: "M:M"
             },
           ],
           strmatchtype: [{
               name: "Exact",
               value: "exact"
             },
             {
               name: "Contains",
               value: "contains"
             },
             {
               name: "Begins with",
               value: "begins with"
             },
             {
               name: "Ends with",
               value: "ends with"
             },
             {
               name: "Approximate Match",
               value: "approximate match"
             },
           ],
           aggregationtype: [{
               name: "Mean",
               value: "mean"
             },
             {
               name: "Sum",
               value: "sum"
             },
             {
               name: "Min",
               value: "min"
             },
             {
               name: "Max",
               value: "max"
             },
             {
               name: "Count",
               value: "count"
             },
             {
               name: "Size",
               value: "size"
             },
             {
               name: "First",
               value: "first"
             },
             {
               name: "Last",
               value: "last"
             },
             {
               name: "Nunique",
               value: "nunique"
             },
             {
               name: "Standard Deviation",
               value: "std"
             },
             {
               name: "Variance",
               value: "var"
             },
             {
               name: "Median",
               value: "median"
             },
             {
               name: "Quantile",
               value: "quantile"
             },
           ],
           rulesdata: [],
           rulesdataCopy:[],
           typeOfUser: 'Paid',
           recoincilationHeaders: [{
               value: "description",
               text: "Description",
               sortable: true,
               filterable: true,
               lock: true,
               visible: false,
             },
             {
               value: "subamount",
               text: "Sub Amount",
               sortable: false,
               filterable: false,
               datatype: "number",
               default: "0",
               lock: true,
               visible: false,
               alignment: "text-center",
             },
             {
               value: "finalamount",
               text: "Final Amount",
               sortable: false,
               filterable: false,
               datatype: "number",
               default: "0",
               lock: true,
               visible: false,
               alignment: "text-center",
             },
             {
               value: "attachmentfile",
               text: "File Attachment(s)",
               sortable: false,
               filterable: false,
               datatype: "action",
               lock: true,
               visible: false,
               alignment: "text-center",
             },
             {
               value: "action",
               text: "Action",
               sortable: false,
               datatype: "action",
               lock: true,
               visible: false,
               alignment: "text-center",
             }
           ],
           recoincilationDialogue: false,
           recoincilationItem: {},
           recoincilationErrorItem: {},
           headers: [{
               value: "indexno",
               text: "#",
               sortable: false,
               filterable: false,
             },
             {
               value: "tag",
               text: "Tag",
               sortable: true,
               filterable: true,
             },
             {
               value: "description",
               text: "Description",
               sortable: true,
               filterable: true,
             },
             {
               value: "operator",
               text: "Operator",
             },
             {
               value: "tableid",
               text: "Table",
             },
             {
               value: "column",
               text: "Column",
             },
             {
               value: "filterword",
               text: "Filterword",
             },
             {
               value: "action",
               text: "Action",
             },
           ],
           fileMapshow: false,
           addfilterdialog: false,
           addfilterdialogloading: false,
           addfilterconfim:false,
           addfilterconfimloading:false,
           addrulesconfim:false,
           addrulesconfimloading:false,
           filterobject: {
             tableid: [],
             column: [],
             operator: [],
             description: undefined,
             excludetype: 'Exclude',
             tag: '',
             status: '',
             filterword: '',
           },
           filterrowType: ["Exclude", "Delete"],
           filterrowTypeV2: ["Exclude", "Delete", "Report"],
           operatorType: ["Equal", "Contains", "Not Contains", "Start With", "End With", "Not Equal", "Greater than", "Less than", "Less than or Equal to", "Greater than or Equal to"],
           filtercolums: [],
           filterList: [],
           filterListCopy:[],
           formatedData: [],
           formatedDataMiddeleware: [],
           addMiddlewaredialog: false,
           addMiddlewaredialogloading: false,
           middlewareObject: {},
           middlewareHeader: [{
               value: "indexno",
               text: "S No.",
               sortable: false,
               filterable: false,
               visible:true
             },
             {
               value: "middlewarename",
               text: "Middleware",
               sortable: false,
               filterable: false,
               visible:true
             },
             {
               value: "basefile",
               text: "Base File",
               sortable: false,
               filterable: false,
               visible:true
     
             },
             {
               value: "action",
               text: "Action",
               sortable: false,
               filterable: false,
               visible:true
             },
     
           ],
           middlewareValues: [],
           analysisDetails: {},
           middFile: null,
           middlewareObjecterror: {},
           filterobjecterror: {},
           page: 1,
           pagecount: 1,
           perpage: 50,
           total: 0,
           pagenew: 1,
           pagecountnew: 1,
           perpagenew: 50,
           totalnew: 0,
           pagefifo: 1,
           pagecountfifo: 1,
           perpagefifo: 50,
           totalfifo: 0,
           pagenewmatchnew: 1,
           pagecountnewmatchnew: 1,
           perpagenewmatchnew: 50,
           totalnewmatchnew: 0,
           pagenewmatchold: 1,
           pagecountnewmatchold: 1,
           perpagenewmatchold: 50,
           totalnewmatchold: 0,
           draggedItemIndex: null,
           isEditing: false,
           currentIndex: null,
           resultTabItem: [{
             name: 'Summary',
             value: 'Summary'
           }, {
             name: 'Matched',
             value: 'Matched'
           }, {
             name: 'Un Matched',
             value: 'Unmatched'
           },{
             name: 'AI',
             value: 'AI',
             icon: 'mdi-settings',
             className: 'noTransform'
           }],
           resultTab: 0,
           comboAnnexer: [],
           collectionNameList: [],
           newAnnexersdata: [],
           newannexerCombodata: [],
           selectedCombo: 0,
           filterCombo: '',
           RecoincilationTab: '',
           recoincilationSummaryData: [],
           recoincilationCollection: '',
           tabletype: 'Summary',
           originalAnnexersdata: [],
           selectiteamComparing: [],
           twofileselectiteamComparing: [],
           filtertwofile: '',
           filtermorefile: '',
           fifoalaysis: false,
           selectFifo: [],
           selectallfifo:false,
           analysisTypeItem: [{
               name: "Intercompany Reconciliation"
             },
             {
               name: "Accounts receivable Reconciliation"
             },
             {
               name: "Accounts payable Reconciliation"
             }
           ],
           fifoannexData: [],
           electFifo: [],
           runfifoppop: false,
           executionbtn: false,
           executionbtnAI: false,
           fifoexecutionResult: [],
           aiexecutionResult:[],
           sortColumn: {},
           sortDesc: false,
           selectFilter: '',
     
           globalfiltermanualloading:false,
           globalfiltermanual:false,
           globalfilterobjectmanual: {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           },
           globalfilterobjectmanualerror: {
             operation: '',
             range: '',
             range1: '',
             range2: '',
             tag:'',
           },
           manualglobalInfo:{},
           manualglobalArray:[],
           manualglobalList:[],
           manualtypedata:'',
           manualselectColumn:'',
           manualCollection:'',
     
     
           globalfiltermanualtwofileloading:false,
           globalfiltertwofilemanual:false,
           manualglobalInfotwofile:{},
           globalfilterobjectmanualtwofile: {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           },
           globalfilterobjectmanualerrortwofile: {
             operation: '',
             range: '',
             range1: '',
             range2: '',
             tag:'',
           },
           manualCollectiontwofile:'',
           manualselectColumntwofile:'',
           manualtypedatatwofile:'',
           manualglobaltwofileArray:[],
           manualglobaltwofileList:[],
     
           table1globalInfo:{},
           table1globalCollections:{},
           table1selectColumn: '',
           table1typedata: '',
           globalfilter1:false,
           globalfilter1loading:false,
           globalfilterObjecttable1: {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           },
           globalfilterObjecterrortable1: {
             operation: '',
             range: '',
             range1: '',
             range2: '',
             tag:'',
           },
           table1globalArray:[],
           table1globalList:[],
     
     
     
           table2globalInfo:{},
           table2globalCollections:{},
           table2selectColumn: '',
           table2typedata: '',
           globalfilter2:false,
           globalfilter2loading:false,
           globalfilterObjecttable2: {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           },
           globalfilterObjecterrortable2: {
             operation: '',
             range: '',
             range1: '',
             range2: '',
             tag:'',
           },
           table2globalArray:[],
           table2globalList:[],
     
     
     
           selectfilterdialog: false,
           selectfilterdialogloading: false,
           globalfilterObject: {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           },
           globalfilterObjecterror: {
             operation: '',
             range: '',
             range1: '',
             range2: '',
             tag:'',
           },
           typeOffilter: '',
           filteritemNumber: ["Equals", "Does Not Equals", "Greater Then", "Greater Then Or Equal To", "Less Then", "Less Then Or Equal To", "Between"],
           filteritemString: ["Equals", "Does Not Equals", "Contains", "Does Not Contains"],
           filteritemDate: ["Equals", "Before", "After", "Between"],
           filterArray: [],
           globalArray: [],
           selectcollection: '',
           selectkey:'',
           oneormorefile:false,
           selectedCombotwo:0,
           selectedcollection:'',
           setupAIdilog:false,
           setupAIdilogloading:false,
           setupaiObj:{},
           ailoadingimage:false,
           matchCollection:'',
           mathcFilter:'KKK',
           matchTypeFilter:'JJJ',
           GlobaleCollection:'',
           GlobaleCollectionManual:'',
           GlobaleId:'',
           globalfilterquery:'',
           querydetails:'',
           summeryCombo:[],
           summaryObj:{},
           selectedSummery:0,
           selectedSummeryFilter:'',
           selectedCollectionAll:{},
           tableOne:[],
           tableOneCopy:[],
           tableTwo:[],
           tableTwoCopy:[],
           table1:1,
           table1pagecount:1,
           table1perpag:50,
           table1total:0,
     
           table2:1,
           table2pagecount:1,
           table2perpag:50,
           table2total:0,
           select1table:'',
           select2table:'',
           table1collection:'',
           table2collection:'',
           tablecommoncollection:'',
     
           singleglobal:{},
           selectAllcollection:{},
     
           tableOnetwofile:[],
           tableOneCopytwofile:[],
     
           table1twofile:1,
           table1pagecounttwofile:1,
           table1perpagtwofile:50,
           table1totaltwofile:0,
     
           table1twofileglobalInfo:{},
           globaltwofilter1:{},
           globaltwofilter1error:{},
           selectedtable1collection:{},
           globalfilter1twofile:false,
           globalfilter1twofileloading:false,
           datatypetable1:'',
           table1twoselectColumn:'',
           table1twoglobalList:[],
           table1twoglobalArray:[],
           
     
           tableTwotwofile:[],
           tableTwoCopytwofile:[],
           table2twofile:1,
           table2pagecounttwofile:1,
           table2perpagtwofile:50,
           table2totaltwofile:0,
     
           table2twofileglobalInfo:{},
           globaltwofilter2:{},
           globaltwofilter2error:{},
           selectedtable2collection:{},
           globalfilter2twofile:false,
           globalfilter2twofileloading:false,
           datatypetable2:'',
           table2twoselectColumn:'',
           table2twoglobalList:[],
           table2twoglobalArray:[],
     
           table1collectiontwofile:{},
           table2collectiontwofile:{},
           selectcollectiontwofile:{},

           bulkMatchtwofile:false,
           bulkMatchonefile:false,
           resetFlag:false,
           fileuplaoded: null,
           file:null,
     
     
         };
       },
       components: {
         TransactionMatchingReconciliation,
         TransactionMatchingResults,
         StepFileUploads,
         StepInputDataV2,
         StepFilterConfig,
         StepFilterConfigV2,
         StepMiddlewareConfig,
         StepMiddlewareConfigV2,
         StepRulesConfig,
         StepMatchingRoutinesV2,
         StepFifoAnalysis,
         RuleConfigDrawer,
         MatchSelectionDrawers,
         ReconciliationDrawer,
         MappingMasterDrawer,
         FilterDefinitionDrawer,
         FifoExecutionResultsDrawer,
         FilterPropertiesDrawer,
         AISetupDrawer,
         UnsavedChangesDrawer,
         ManualMatchDrawer,
         AddRuleDialogV2,
         TransactionMatchingResultsV2,
         ActivityLog,
       },
       created() {
         this.refreshData();
         this.startExcelHistoryPolling();
         this.startFileStatusPolling();
       },
       activated() {
         this.refreshData();
         this.startExcelHistoryPolling();
         this.startFileStatusPolling();
       },
       deactivated() {
         this.stopExcelHistoryPolling();
         this.stopFileStatusPolling();
       },
       beforeDestroy() {
         this.stopExcelHistoryPolling();
         this.stopFileStatusPolling();
       },
       beforeRouteLeave(to, from, next) {
         this.stopExcelHistoryPolling();
         this.stopFileStatusPolling();
         next();
       },
       methods: {
        ...mapActions('transactionMatching', ['bulkManualMatchTwoFile', 'fetchAnalysisDetails', 'getFilterList', 'upsertFilter', 'deleteFilter', 'toggleFilterStatus', 'reorderFilters', 'fetchBotDetails', 'loadAnalysisConfig', 'fetchFormData', 'fetchUploadFilenames']),
        /**
         * FBC cycle + optional linked task ids for bot/analysis fcAccess
         * (botCycleFromBody / botLinkedTaskFromBody). Do not invent task ids.
         */
        botAccessFields() {
          const pid = this.analysisDetails.pid || this.id || this.$route.params.id || '';
          const linkedtasks = (this.analysisDetails.linkedtasks || [])
            .map((t) => (t && t._id) || t)
            .filter(Boolean);
          const fields = { pid };
          if (linkedtasks.length) fields.linkedtasks = linkedtasks;
          return fields;
        },
        /** { pid, data: { ...fields, ...access } } — nested data.pid required when body.data is present. */
        botAccessBody(dataFields = {}) {
          const access = this.botAccessFields();
          return {
            pid: access.pid,
            data: { ...dataFields, ...access },
          };
        },
        /**
         * Sort items by index property if all items have it
         * @param {Array} items - Array of items to sort
         * @returns {Array} Sorted array or original array if not all have index
         */
        indexOrder(items) {
          if (items.length > 0 && items.every(item => item.index !== undefined && item.index !== null)) {
            return items.sort((a, b) => a.index - b.index);
          }
          return items;
        },
         init() {
           if (this.$store.state.subscriptions.financialclose.plan_detail.name) {
             this.typeOfUser = this.$store.state.subscriptions.financialclose.plan_detail.name || "Paid";
           }
         },
         /**
          * Download consolidated excel with filters and sort from child component
          * Receives filters and sort payload from the emitted event
          * @param {Object} filtersAndSort - { collection, filters, sort } from TMResultsDataTable
          */
         downloadConsolidatedExcel(filtersAndSort) {
           if (!filtersAndSort || !filtersAndSort.collection) {
             this.$store.commit('sbError', 'Unable to download excel');
             return;
           }
           
           this.loading = true;
           // Build request data
           const requestData = {
             section: 'TMResults',
           };

           if (filtersAndSort.collection) {
             requestData.collection = filtersAndSort.collection;
           }

           if (filtersAndSort.filters?.groups?.length > 0) {
             requestData.filters = filtersAndSort.filters;
           }

           if (filtersAndSort.sort?.length > 0) {
             requestData.sort = filtersAndSort.sort;
           }
           
           // Call download endpoint
           this.axios.post(
             `/v3/bot/analysis/downloadfile/${this.botdetails._id}`,
             { data: [requestData] }
           )
           .then(response => {
             if (response.data.status === 'success') {
               const data = response.data.data || {};
               this.fetchDownloadHistoryBackground();
               if (data.stage === 0 || data.stage === 4) {
                 this.$store.commit('sbSuccess', 'File download initiated successfully');
               } else if (data.stage === 2) {
                 this.$store.commit('sbError', 'There was an error while generating file');
               } else if (data.upload) {
                 this.$nova.downloadFile(data.upload);
                 this.$store.commit('sbSuccess', 'Excel downloaded successfully');
               } else {
                 this.$store.commit('sbError', 'No file found to download');
               }
             } else {
               throw new Error(response.data.message || 'Error downloading file');
             }
           })
           .catch(err => {
             console.error('[TransactionMatchingConfigNew]: Download error', err);
             this.$store.commit('sbError', err.message || err || 'Unknown error during download');
           })
           .finally(() => {
             this.loading = false;
           });
         },
        /**
         * Lightweight refresh for file operations (upload/delete).
         * Only reloads file-related state without tearing down the entire UI,
         * so filters and other step states are preserved.
         */
        async refreshFileData() {
          this.loading = true;
          try {
            const data = await this.loadAnalysisConfig({ tmaid: this.anaid, pid: this.id, bid: this.botid });
            if (data && Object.keys(data).length > 0) {
              this.uploaddetails = data.uploaddetails || {};
              this.setupList = data.setup || {};
              this.fileConfigInfo = this.setupList?.fileconfig || {};
              if (data.actiondetails) {
                this.analysisDetails = { ...this.analysisDetails, actiondetails: data.actiondetails };
              }
              await this.fetchFileUploadStatus();
            }
          } catch (err) {
            console.error('[TransactionMatchingConfigNew]: refreshFileData error', err);
            this.$store.commit('sbError', err?.message || 'Failed to refresh file data');
          } finally {
            this.loading = false;
          }
        },
        /**
         * Refresh all data on component initialization or manual refresh.
         * Does not set initialDataLoaded=false so the Results stepper stays mounted
         * and the active annexure tab is retained.
         */
        async refreshData() {
           this.init();
           this.id = this.$route.params.id || "";
           this.userid = this.$store.state.user.id;
           this.anaid = this.$route.params.anaid;
           this.currentannexuredata = [];
           this.annexuredata = {};
           this.loading = true;
           
           try {
             await this.getData();
           } catch (err) {
             console.error('[TransactionMatchingConfigNew]: Error during data refresh', err);
           } finally {
             this.loading = false;
             this.initialDataLoaded = true;
           }
         },
        /**
         * Fetch initial bot details and metadata
         * Waits for dependent data (loadBot, getTransctionmatching, getRecoincialtionData) to complete
         * @returns {Promise} Resolves when all data is fetched
         */
        async getData() {
           this.project = this.$route.params.id;
           try {
             try {
               await this.fetchFormData();
             } catch (err) {
                console.warn('[TransactionMatchingConfig]: fetchFormData failed, continuing', err);
             }
             const data = await this.fetchBotDetails({ id: this.id });
             if (data && Object.keys(data).length > 0) {
               this.metadata = data.metadata || {};
               this.stage = this.metadata.stage;
               this.resultTab = 0;
               this.tabletype = 'Summary';
               this.selectedannexure = '';
               this.executionbtnAI = false;
               
               // Load bot first so isV2Mode is determined before filter fetch
               await Promise.all([
                 this.getTransctionmatching(),
                 this.loadBot({ skipRulesRefresh: true })
               ]);

               // analysisDetails is now ready, so rule normalization can safely fetch keyed items.
               await this.loadRulesV2();

               // Fetch filters after bot/config state is ready.
               this.getfilterlist();
             } else {
               throw new Error("Error fetching bot details");
             }
           } catch (err) {
             this.$store.commit("sbError", err.message || err || "Unknown error!");
             console.error('[TransactionMatchingConfigNew]: getData error', err);
           }
         },
         formateData() {
           let ObjectTemp = {};
           let ArrayTemp = [];
           this.filterList.forEach((item, index) => {
             ObjectTemp = {
               indexno: index,
               key: item.key || '',
               _id: item._id,
               description: item.description,
               excludetype: item.excludetype,
               operator: item.operator,
               tableid: item.tableid,
               tag: item.tag,
               column: item.column,
               filterword: item.filterword,
               status: item.status,
               filetype: item.filetype || '',
               fileConfig1: item.fileConfig1 || '',
               fileConfig2: item.fileConfig2 || '',
               isKnockOffFilter: item.isKnockOffFilter === true || item.isKnockOffFilter === 'true',
             };
             ArrayTemp.push(ObjectTemp);
           });
           this.formatedData = ArrayTemp;
         },
         resolveMiddlewareUploadId(middlewarefile) {
           let raw = middlewarefile;
           if (Array.isArray(middlewarefile)) raw = middlewarefile[0];
           if (raw && typeof raw === 'object') return String(raw._id || raw.id || '');
           return raw ? String(raw) : '';
         },
         async enrichMiddlewareUploadNames(middlewareList = []) {
           if (!middlewareList.length) return middlewareList;

           const fileIds = middlewareList
             .map((item) => this.resolveMiddlewareUploadId(item.middlewarefile))
             .filter(Boolean);
           if (!fileIds.length) return middlewareList;

           const nameById = await this.fetchUploadFilenames({ fileIds });

           return middlewareList.map((item) => {
             const uploadId = this.resolveMiddlewareUploadId(item.middlewarefile);
             const uploadFileName = nameById[uploadId] || '';
             return {
               ...item,
               uploadFileName,
               filename: uploadFileName || item.filename || item.originalname || '',
             };
           });
         },
         formatemiddlewareData() {
           let ObjectTemp = {};
           let ArrayTemp = [];
           this.middlewareValues.forEach((item, index) => {
             ObjectTemp = {
               indexno: index + 1,
               _id: item._id,
               middlewarename: item.name,
               basefile: item.basefile,
               status: item.status,
               middlewarefile: item.middlewarefile,
               middlewarefilepath: item.middlewarefilepath || item.filepath || '',
               uploadFileName: item.uploadFileName || '',
               filename: item.uploadFileName || item.filename || item.originalname || '',
               fileConfig1: item.fileConfig1 || '',
               fileConfig2: item.fileConfig2 || '',
             };
             ArrayTemp.push(ObjectTemp);
           });
           this.formatedDataMiddeleware = ArrayTemp;
         },
         checkChangesRules(){
           if (this.rulesdata.length !== this.rulesdataCopy.length || JSON.stringify(this.rulesdata) !== JSON.stringify(this.rulesdataCopy)) {
           this.addrulesconfim = true;
           } else {
           this.e1 = 3;
           
           }
         },
         checkChangesFilter() {
           if (this.filterList.length !== this.filterListCopy.length || JSON.stringify(this.filterList) !== JSON.stringify(this.filterListCopy)) {
           this.addfilterconfim = true;
           } else {
           this.e1 = 3;
           }
         },
         getfilterlistCancel(){
           this.filterList = [...this.filterListCopy];
           this.formateData();
           this.addfilterconfim = false;
         },
         getruleslistCancel(){
           this.rulesdata = [...this.rulesdataCopy];
           this.addrulesconfim = false;
         },
         /**
          * Fetch filter list via store action.
          * Also triggers backend key generation for legacy data without keys.
          */
         async getfilterlist() {
           if (!this.analysisDetails || !this.analysisDetails.bpid) {
             console.error('[Filter]: getfilterlist — Missing necessary analysis details');
             return;
           }
           this.loading = true;
           try {
             const data = await this.getFilterList({ bpid: this.analysisDetails.bpid });
             if (data && data.length > 0) {
               this.filterList = data;
               this.filterListCopy = JSON.parse(JSON.stringify(data));
               this.formateData();
             } else {
               this.filterList = [];
               this.filterListCopy = [];
               this.formatedData = [];
             }
           } catch (err) {
             console.error('[Filter]: getfilterlist — error', err);
           } finally {
             this.loading = false;
           }
         },
         async getmiddlewarelist() {
           if (!this.analysisDetails || !this.analysisDetails.pid || !this.analysisDetails._id || !this.analysisDetails.bpid) {
             console.error("Missing necessary analysis details");
             return;
           }
           this.loading = true;
           try {
             const data = await this.$store.dispatch('transactionMatching/getMiddlewareList', {
               pid: this.analysisDetails.pid,
               tmaid: this.analysisDetails._id,
               bpid: this.analysisDetails.bpid,
             });
             if (data && data.length > 0) {
               this.middlewareValues = await this.enrichMiddlewareUploadNames(data);
               this.formatemiddlewareData();
             } else {
               this.middlewareValues = [];
               this.formatedDataMiddeleware = [];
             }
           } catch (err) {
             console.error('[TransactionMatchingConfigNew]: getmiddlewarelist error', err);
           } finally {
             this.loading = false;
           }
         },
        /**
         * Fetch transaction matching analysis details
         * @returns {Promise} Resolves when data is fetched
         */
        async getTransctionmatching() {
           try {
             this.analysisDetails = await this.fetchAnalysisDetails({
               id: this.id,
               anaid: this.anaid,
             });
             if (this.analysisDetails && this.analysisDetails._id) {
               this.fifoalaysis = this.fifocheck(this.analysisDetails.typeofanalysis);
               // Filter list is now fetched from getData() after isV2Mode is determined
               this.getmiddlewarelist();
             }
           } catch (err) {
             this.$store.commit("sbError", err.message || err || "Unknown error!");
             console.error('[TransactionMatchingConfigNew]: getTransctionmatching error', err);
           }
         },
         fifocheck(item) {
           return this.analysisTypeItem.some(x => x.name === item);
         },
         async openMatchDialog(d,obj,table) {
           this.selectcollectiontwofile = obj;
           this.comparingitem = {
             ...d
           };
           this.twofileselectiteamComparing = [this.comparingitem];
           this.selectedids = [];
           this.searchvalue = "";
           this.comparedialog = true;
           
           if (table === 'table1') {
             this.filterselectkey = "Table2_SNo";
           } else {
             this.filterselectkey = "Table1_SNo"
           }
           this.ailoadingimage = true;
           await this.axios.post("/v2/bot/analysis/readannexure/"+ obj.botid + "/c/" + obj.collection + "/v/" + obj.id, {
                 filter: { limit: 50, page: 1 }
               })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.formateddataforfilter = dt.data.data[0].data;
                 this.storeTotalnewmatchnew(dt.data.data[0].totalCount[0]?.count);
                 this.pagenewmatchnew = 1;
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         },
         filterMatchItemsNewfilterTwo() {
           this.compareloadingNew = true;
           let sv = (this.searchvalue || "").toString().toLowerCase(); 
           let ndt = [];
     
           if (sv) {
             for (const i of this.originalFormatedDatatwo) {
               if (JSON.stringify(i || {}).toLowerCase().includes(sv)) {
                 ndt.push(i);
               }
             }
             this.formateddataforfilter = ndt;
           } else {
             this.formateddataforfilter= [...this.originalFormatedDatatwo];
           }
     
           this.compareloadingNew = false;
         },
       async openMatchDialogNew(d,obj,table) {
           this.comparingitemNew = {
             ...d
           };
           let selectedverification = this.verificationlist[0] || {};
           this.selectiteamComparing = [this.comparingitemNew]
           this.comparedialogNew = true;
           this.selectedidsNew = [];
           this.searchvalueNew = "";
           this.pagenewmatchnew = 1;
           let data = {
             collection: obj.collection
           }
           this.compareloadingNew = true;
           if (table === 'table1') {
             this.filterselectkeyNew = "Table2_SNo";
           } else {
             this.filterselectkeyNew = "Table1_SNo"
           }
           await this.axios
             .post("/v2/financialclose/analysis/getdatabycombocode/" + obj.key, {
               data:data,
               filter:{limit: 50,page:1 },
             })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.formateddataforfilterNew = dt.data.data[0].data;
                 this.storeTotalnewmatchnew(dt.data.data[0]?.totalCount[0]?.count);
                 this.singleglobal = {collection:obj.collection,id:selectedverification._id,key:obj.key,manual:"ManualMatch",type:"New"}
                 
                 this.GlobaleCollectionManual = {collection:obj.collection,id:selectedverification._id,key:obj.key,manual:"ManualMatch",type:"New"}
               } else throw new Error(dt.data.message || "Error Get Table Data");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.compareloadingNew = false;
             });
         },
         filterMatchItemsNewfilter() {
           this.compareloadingNew = true;
           let sv = (this.searchvalueNew || "").toString().toLowerCase(); // Convert to lowercase here
           let ndt = [];
     
           if (sv) {
             for (const i of this.originalFormatedData) { // Use the original data to filter
               if (JSON.stringify(i || {}).toLowerCase().includes(sv)) {
                 ndt.push(i);
               }
             }
             this.formateddataforfilterNew = ndt;
           } else {
             // If the search value is empty, restore the original data
             this.formateddataforfilterNew = [...this.originalFormatedData];
           }
     
           this.compareloadingNew = false;
         },
         fifiCollection() {
           let selectedverification = this.verificationlist[0] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           this.comboAnnexer = rundata.annexure || [];
           if (this.comboAnnexer.length > 0) {
             this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Unmatched"));
             this.fifofetching(this.collectionNameList, selectedverification._id)
           } else {
             this.$store.commit("sbError", "Analysis has not yet been run");
           }
         },
         fifofetching(c, v) {
           this.loading = true;
           let data = {
             annexure1: {
               bid: this.botdetails._id,
               collection: c[0].collection,
               verification: v
             },
             annexure2: {
               bid: this.botdetails._id,
               collection: c[1].collection,
               verification: v
             },
             ...this.botAccessFields(),
           }
           this.axios
             .post("/v2/bot/analysis/fifo", {
               pid: data.pid,
               data
             })
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.fifoannexData = ele.data.data;
                 this.loadBot();
               } else {
                 this.$store.commit("sbError", ele.data.message || "Unknown error!");
               }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.loading = false;
             });
         },
         loadReconciliation() {
           // Call the child component's initialization method when step becomes active
           this.$nextTick(() => {
             if (this.$refs.reconciliationComponent) {
               this.$refs.reconciliationComponent.initializeReconciliation();
             }
           });
         },
         annexergenerateAll(filetype){
           let selectedverification = this.verificationlist[0] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           if (this.rundetailsall.length === 0) {
             return this.$store.commit("sbError", "The Analysis Has Not Been Run Yet");
           }
           
           if (rundata.stage === 2 && rundata.annexure.length === 0) {
             return this.$store.commit("sbError", rundata.message);
           } else {
             this.comboAnnexer = rundata.annexure || [];
             if (filetype === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
             this.tabletype = 'Summary';
             this.resultTab = 0;
             this.collectionNameList = this.comboAnnexer.filter(item => item.collection && item.collection.includes('annexure_summary'));
             this.twoFileannexureSummary(this.collectionNameList[0].collection, selectedverification._id);
           } else {
             this.tabletype = 'Summary';
             this.collectionNameList = this.comboAnnexer.filter(item => item.collection && item.collection.includes('annexure_summary'));
             this.generateAnnexurelistSummeryCombo(this.collectionNameList[0]?.collection,"Result");
             this.generateAnnexurelistSummery(this.collectionNameList[0].collection, selectedverification._id)
           }
           }
         },
         collectionGet(type, tab){
           
           
           let aiCollection = '';
           let selectedverification = this.verificationlist[0] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           
           if (rundata.stage === 2 && rundata.annexure.length === 0) {
             this.$store.commit("sbError", rundata.message);
           } else {
             this.comboAnnexer = rundata.annexure || [];
             switch (type) {
             case 'Summary':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Summary"));
               this.tabletype = 'Summary';
               break;
             case 'Matched':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Matched"));
               this.tabletype = 'Matched';
               this.filterselectkey = "Table1_SNo"
               break;
             case 'Unmatched':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Unmatched"));
               this.tabletype = 'Unmatched';
               this.filterselectkey = "Table2_SNo"
               break;
             case 'AI':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("AI"));
               this.tabletype = 'AI';
               break;
             default:
               this.collectionNameList = this.comboAnnexer.filter(item => item.name === "Summary");
               this.tabletype = 'Summary';
             }
             if (type === 'Summary' && tab === 0) {
               this.selectedcollection = this.collectionNameList[0].collection;
               this.twoFileannexureSummary(this.collectionNameList[0].collection, selectedverification._id)
            } else {
              // Check if ALL collections have no data message
              const allHaveNoData = this.collectionNameList.every(item => item.message === "no data found in annexure");
              // Proceed if at least one collection has data
              if (!allHaveNoData) {
              if (this.tabletype === 'AI') {
                aiCollection = this.collectionNameList.find((x) => x.collection.includes('annexure_AI'));
                if (aiCollection) {
                  this.generateAnnexurelistAI(aiCollection.collection,selectedverification._id);
                } else{
                  this.$store.commit("sbError",  "AI Annexure Not Generated");
                  this.newAnnexersdata = [];
                  this.currentannexuredata = [];
                }
              } else {
              this.selectedCombotwo = 0;
              this.selectedcollection = this.collectionNameList[0].collection;
              this.twofileAllcollection(this.collectionNameList,selectedverification._id)
              
              this.twoFileannexureSummary(this.collectionNameList[0].collection, selectedverification._id)
              }
              
              } else {
              this.selectedCombotwo = '';
              this.currentannexuredata = [];
              // Clear table data to prevent showing stale data from previous tab
              this.tableOnetwofile = [];
              this.tableOneCopytwofile = [];
              this.tableTwotwofile = [];
              this.tableTwoCopytwofile = [];
              this.table1Totaltwofile(0);
              this.table2Totaltwofile(0);
              this.newAnnexersdata = [];
              return this.$store.commit("sbError", 'No data found in any annexure');
              }               
             }
           }
         },
         comboCollectionGet(type, tab) {
           let aiCollection = '';
           this.filtermorefile = '';
           let selectedverification = this.verificationlist[0] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           this.comboAnnexer = rundata.annexure || [];
           switch (type) {
             case 'Summary':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Summary"));
               this.tabletype = 'Summary';
               break;
             case 'Consolidated':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Consolidated Annexure"));
               this.tabletype = 'Consolidated';
               break;
             case 'Matched':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Matched"));
               this.tabletype = 'Matched';
               break;
             case 'Unmatched':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("Unmatched"));
               this.tabletype = 'Unmatched';
               break;
             case 'AI':
               this.collectionNameList = this.comboAnnexer.filter(item => item.name.includes("AI"));
               this.tabletype = 'AI';
               break;
             default:
               this.collectionNameList = this.comboAnnexer.filter(item => item.name === "Summary");
               this.tabletype = 'Summary';
           }
     
           if (type === 'Summary' && tab === 0) {
             this.generateAnnexurelistSummery(this.collectionNameList[0].collection, selectedverification._id);
             this.generateAnnexurelistSummeryCombo(this.collectionNameList[0]?.collection,"Result");
           } else {
             const hasNoDataMessage = this.collectionNameList.some(item => item.message === "no data found in annexure");
             if (!hasNoDataMessage) {
               if (this.tabletype === 'AI') {
                 aiCollection = this.collectionNameList.find((x) => x.collection.includes('annexure_AI'));
                 if (aiCollection) {
                   this.generateAnnexurelistAI(aiCollection.collection,selectedverification._id);
                   
                 } else{
                   this.$store.commit("sbError",  "AI Annexure Not Generated");
                   this.newAnnexersdata = [];
                 }
               } else {
                 this.generateAnnexurelistCompo(this.collectionNameList, 0)
               }
             } else {
               this.newAnnexersdata = [];
               this.newannexerCombodata = [];
               // Clear table data to prevent showing stale data from previous tab
               this.tableOnetwofile = [];
               this.tableOneCopytwofile = [];
               this.tableTwotwofile = [];
               this.tableTwoCopytwofile = [];
               this.table1Totaltwofile(0);
               this.table2Totaltwofile(0);
               this.currentannexuredata = [];
               return this.$store.commit("sbError", 'No data found in annexure');
             }
           }
         },
         generateAnnexurelistSummeryCombo(collection,type){
           this.selectedannexure = collection;
           this.ailoadingimage = true;
           let objectsucc = true;
           this.summaryObj  = {};
           if (type === 'Result') {
            this.summaryObj = {'combo-code':'Summary'};
           } else {
            this.summaryObj = {'combo-code':'Reconciliation '};
           }
           
           this.axios.post("/v2/bot/analysis/getalluniquecombocode/"+ collection)
             .then((dt) => {
               if (dt.data.status === "success") {
                   
                 if (objectsucc) {
                   this.summeryCombo = dt.data.data;
                   this.summeryCombo.unshift(this.summaryObj)
                 }
                 
               } else { throw new Error(dt.data.message || "Error Get Combo Code") ; }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
               objectsucc = false;
             });
         },
         generateAnnexurelistSummeryList(collection,key) {	
           let data = { collection : collection}
           this.selectedannexure = collection;
           this.ailoadingimage = true;
           this.axios.post("/v2/bot/analysis/readcombocodewisesummary/"+key,{data:data})
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.newAnnexersdata = this.indexOrder(dt.data.data);
                 this.originalAnnexersdata = [...this.newAnnexersdata];
                 this.newannexerCombodata = [];
               } else { throw new Error(dt.data.message || "Error Get Combo Code") ; }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         },
         generateAnnexurelistSummery(collection, id) {	
           this.selectedannexure = collection;
           this.ailoadingimage = true;
           this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + collection + "/v/" + id)
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.newAnnexersdata = this.indexOrder(dt.data.data);
                 this.originalAnnexersdata = [...this.newAnnexersdata];
                 this.newannexerCombodata = [];
               } else { throw new Error(dt.data.message || "Error Get Combo Code") ; }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         },
         async pagenationtable1twofile(obj,page){
           if (this.table1twoglobalList.length > 0) {
             this.ailoadingimage = true;
             const filter = {
             data: this.table1twoglobalList,
             limit: 50,
             page: page,
           };
           await this.axios.post("/v2/bot/analysis/readannexurewithfilter/"+ obj.collection, {filter})
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.tableOnetwofile =  dt.data.data[0]?.data,
                 this.table1Totaltwofile(dt.data.data[0]?.totalCount[0]?.count);					
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           } else {
             this.ailoadingimage = true;
           await this.axios.post("/v2/bot/analysis/readannexure/"+ obj.botid + "/c/" + obj.collection + "/v/" + obj.id, {
                 filter: { limit: 50, page: page }
               })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.tableOnetwofile =  dt.data.data[0]?.data,
                 this.table1Totaltwofile(dt.data.data[0]?.totalCount[0]?.count);					
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           }
           
         },
         async pagenationtable2twofile(obj,page){
     
           if (this.table2twoglobalList.length > 0) {
           this.ailoadingimage = true;
             const filter = {
             data: this.table2twoglobalList,
             limit: 50,
             page: page,
           };
           await this.axios.post("/v2/bot/analysis/readannexurewithfilter/"+ obj.collection, {filter})
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.tableTwotwofile =  dt.data.data[0]?.data,
                 this.table2Totaltwofile(dt.data.data[0]?.totalCount[0]?.count);					
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           } else {
           this.ailoadingimage = true;
           await this.axios.post("/v2/bot/analysis/readannexure/"+ obj.botid + "/c/" + obj.collection + "/v/" + obj.id, {
                 filter: { limit: 50, page: page }
               })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.tableTwotwofile =  dt.data.data[0]?.data,
                 this.table2Totaltwofile(dt.data.data[0]?.totalCount[0]?.count);					
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           }
           
         },
         async pagenationtable1(obj,page){
     
           if (this.table1globalList.length > 0) {
           const query = obj.key ? { "Combo-Code": obj.key } : null;
           const filter = {
             data: this.table1globalList,
             limit: 50,
             page: page,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${obj.collection}`;
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query	} : { filter } );
             if (response.data.status === 'success') {
               this.tableOne = response.data.data[0]?.data;
               this.table1Total(response.data.data[0]?.totalCount[0]?.count)
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globalfilterObjecttable1 = {};
             this.globalfilterObjecterrortable1 = {};
     
           }
             
           } else {
     
           this.ailoadingimage = true;
           let data = {
             collection: obj.collection
           }
           await this.axios
             .post("/v2/financialclose/analysis/getdatabycombocode/" + obj.key, {
               data:data,
               filter:{limit: 50,page:page },
             })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.tableOne = dt.data.data[0]?.data;
                 this.table1Total(dt.data.data[0]?.totalCount[0]?.count)
                 
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           }
           
         },
         async pagenationtable2(obj,page){
           if (this.table2globalList.length > 0) {
     
     
           const query = obj.key ? { "Combo-Code": obj.key } : null;
           const filter = {
             data: this.table2globalArray,
             limit: 50,
             page: page,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${obj.collection}`;
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query	} : { filter } );
             if (response.data.status === 'success') {
               this.tableTwo = response.data.data[0]?.data;
               this.table2Total(response.data.data[0]?.totalCount[0]?.count)
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globalfilterObjecttable2 = {};
             this.globalfilterObjecterrortable2 = {};
     
           }
             
           } else {
             this.ailoadingimage = true;
           let data = {
             collection: obj.collection
           }
           await this.axios
             .post("/v2/financialclose/analysis/getdatabycombocode/" + obj.key, {
               data:data,
               filter:{limit: 50,page:page },
             })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.tableTwo = dt.data.data[0]?.data;
                 this.table2Total(dt.data.data[0]?.totalCount[0]?.count)
                 
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           }
           
         },
       async pagenationtsingleManuel(object,page){
         const query = object.key ? { "Combo-Code": object.key } : null;
         if (this.manualglobalArray.length > 0) {
           const filter = {
             data: this.manualglobalArray,
             limit: 50,
             page:page, 
             };
           try {
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${object.collection}`;
             this.GlobaleCollection = {collection:object.collection,id:null,key:object.key}
             const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
             if (response.data.status === 'success') {
                 this.formateddataforfilterNew = response.data.data[0].data;
                 this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.ailoadingimage = false;
             
           }
         } else {
     
           let data = {
             collection: object.collection
           }
           await this.axios
             .post("/v2/financialclose/analysis/getdatabycombocode/" + object.key, {
               data:data,
               filter:{limit: 50,page:page },
             })
             .then((dt) => {
               if (dt.data.status === "success") {
               this.formateddataforfilterNew = dt.data.data[0].data;
               this.storeTotalnewmatchnew(dt.data.data[0].totalCount[0]?.count);
               } else throw new Error(dt.data.message || "Error Getting table");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         }
         
         
       },
       async pagenationIManuel(obj,page){
         if (this.manualglobaltwofileArray.length > 0) {
             const filter = {
             data: this.manualglobaltwofileArray,
             limit: 50,
             page:page, 
             };
           try {
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${obj.collection}`;
             this.GlobaleCollection = {collection:obj.collection,id:obj.id}
             const response = await this.axios.post(Apipath, { filter });
             if (response.data.status === 'success') {
                 this.formateddataforfilter = response.data.data[0].data;
                 this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.ailoadingimage = false;
             
           }
     
         } else {
           this.compareloading = true;
           await this.axios.post("/v2/bot/analysis/readannexure/"+ obj.botid + "/c/" + obj.collection + "/v/" + obj.id, {
                 filter: { limit: 50, page: page }
               })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.formateddataforfilter =  dt.data.data[0]?.data,
                 this.storeTotalnewmatchnew(dt.data.data[0]?.totalCount[0]?.count);					
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.compareloading = false;
             });
           }
         
       },

      pagenationAI(collection,page){

         this.selectedcollection = collection.collection;
           this.selectedannexure = collection.collection;
           this.ailoadingimage = true;
           const filter = {
             limit: 50,
             page:page,
           };
           this.GlobaleCollection = {collection:collection.collection,id:collection.id,page:1,key:null,}
           this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + collection.collection + "/v/" + collection.id,{filter})
             .then((dt) => {
               if (dt.data.status === "success") {
               if (this.analysisDetails.filecount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
                   this.currentannexuredata = dt.data.data[0].data;
                   this.storeTotal(dt.data.data[0].totalCount[0]?.count);
                 } else {
                   this.newAnnexersdata = dt.data.data[0].data;
                   this.storeTotalnew(dt.data.data[0].totalCount[0]?.count);
                 }
               } else { 
                 this.newAnnexersdata = [];
                 this.currentannexuredata = [];
                 throw new Error(dt.data.message || "Error Get AI") ; 
               }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
        
      },
       async pagenationsingleAPI(object,page){
         
         this.ailoadingimage = true; 
         if (this.globalArray.length > 0) {
           const query = object.key ? { "combo-code": object.key } : null;
           this.globalfilterquery = query;
           this.querydetails = object.key;
           const filter = {
             data: this.globalArray,
             limit: 50,
             page:1, 
             };
     
           try {
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${object.collection}`;
             this.GlobaleCollection = {collection:object.collection,id:null,key:object.key}
             const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
             if (response.data.status === 'success') {
               this.newAnnexersdata = response.data.data[0].data;
               this.storeTotalnew(response.data.data[0].totalCount[0]?.count);
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.ailoadingimage = false;
             
           }
           
           
           // const Apipath = `/v2/bot/analysis/readannexurewithfilter/${object.collection}`;
     
         } else {
           this.ailoadingimage = true;
           let data = {
             collection: object.collection
           }
           this.querydetails = object.key;
           await this.axios
             .post("/v2/financialclose/analysis/getdatabycombocode/" + object.key, {
               data:data,
               filter:{limit: 50,page:page },
             })
             .then((dt) => {
               if (dt.data.status === "success") {
                 // this.newAnnexersdata = dt.data.data;
                 this.newAnnexersdata = dt.data.data[0].data;
                 this.storeTotalnew(dt.data.data[0].totalCount[0]?.count);
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           
         }
         },
     
         generateAnnexurelistAI(collection,id){
           this.selectedcollection = collection;
           this.selectedannexure = collection;
           this.ailoadingimage = true;
           const filter = {
             limit: 50,
             page:1,
           };
           this.GlobaleCollection = {collection:collection,id:id,page:1,key:null,}
           this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + collection + "/v/" + id,{filter})
             .then((dt) => {
               if (dt.data.status === "success") {
               if (this.analysisDetails.filecount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
                   this.currentannexuredata = dt.data.data[0].data;
                   this.storeTotal(dt.data.data[0].totalCount[0]?.count);
                 } else {
                   this.newAnnexersdata = dt.data.data[0].data;
                   this.storeTotalnew(dt.data.data[0].totalCount[0]?.count);
                 }
               } else { 
                 this.newAnnexersdata = [];
                 this.currentannexuredata = [];
                 throw new Error(dt.data.message || "Error Get AI") ; 
               }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         },
         async generateAnnexurelistCompo(collectionname, slectcolection) {
         const collection1 = collectionname[0].collection;
         const collection2 = collectionname[1].collection;
     
           const data = {
           collection1: collection1,
           collection2: collection2
           }
           this.selectedCollectionAll = data;
     
           this.ailoadingimage = true;
           this.axios.post("/v2/bot/analysis/getalluniquecombocodefromallcollection",{data})
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.newannexerCombodata = dt.data.data;
                 this.selectedCombo = slectcolection;
                 this.gettabledata(this.selectedCombo);
                 
               }
               })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
           },
     
         twofileAllcollection(collectionsList,id){
     
           // Reset table data to prevent showing stale data from previous tab
           this.tableOnetwofile = [];
           this.tableOneCopytwofile = [];
           this.tableTwotwofile = [];
           this.tableTwoCopytwofile = [];
           this.table1Totaltwofile(0);
           this.table2Totaltwofile(0);
           this.stagetable1twofile = [];
           this.stagetable2twofile = [];
           this.table1twofile = 1;
           this.table2twofile = 1;
     
           const collection1 = collectionsList[0].collection;
           const collection2 = collectionsList[1].collection;
           const data = {
           collection1: collection1,
           collection2: collection2
           }
           this.selectAllcollection = data;
     
          const requestData = [
            {
              collection: this.selectAllcollection.collection1,
            },
            {
              collection: this.selectAllcollection.collection2,
            }
          ];
          const fetchTableData = async (data, tableIndex) => {
            try {
              const response = await this.axios.post(
                "/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + data.collection + "/v/" + id,
                { filter: { limit: 50, page: 1 } }
              );
              
              if (response.data.status === "success") {
                const tableData = response.data.data[0].data;
                const totalCount = response.data.data[0].totalCount[0]?.count;
                
                // Check if table has data
                if (!tableData || tableData.length === 0) {
                  console.warn(`No data found for table ${tableIndex}`);
                  this.$store.commit("sbError", `No data found for table ${tableIndex}`);
                }
                
                if (tableIndex === 1) {
                  this.tableOnetwofile = tableData || [];
                  this.tableOneCopytwofile = [...this.tableOnetwofile];
                  this.table1Totaltwofile(totalCount || 0);
                  this.table1collectiontwofile = {
                    collection: this.selectAllcollection.collection1,
                    id: id,
                    botid: this.analysisDetails.bpid,
                    table: 'table1'
                  };
                } else {
                  this.tableTwotwofile = tableData || [];
                  this.tableTwoCopytwofile = [...this.tableTwotwofile];
                  this.table2Totaltwofile(totalCount || 0);
                  this.table2collectiontwofile = {
                    collection: this.selectAllcollection.collection2,
                    id: id,
                    botid: this.analysisDetails.bpid,
                    table: 'table2'
                  };
                }
              }
            } catch (err) {
              this.$store.commit("sbError", `Error loading table ${tableIndex}: ${err.message || err || "Unknown error!"}`);
              console.log(err);
              // Reset table data on error
              if (tableIndex === 1) {
                this.tableOnetwofile = [];
                this.tableOneCopytwofile = [];
                this.table1Totaltwofile(0);
              } else {
                this.tableTwotwofile = [];
                this.tableTwoCopytwofile = [];
                this.table2Totaltwofile(0);
              }
            }
          };           // Make both requests concurrently using Promise.all
           Promise.all([
             fetchTableData(requestData[0], 1),
             fetchTableData(requestData[1], 2)
           ])
           .finally(() => {
             this.ailoadingimage = false;
           });
           
         },
       async twofilesinglecollection(obj){
           this.ailoadingimage = true;
           await this.axios.post("/v2/bot/analysis/readannexure/"+ obj.botid + "/c/" + obj.collection + "/v/" + obj.id, {
                 filter: { limit: 50, page: 1 }
               })
             .then((dt) => {
               if (dt.data.status === "success") {
                 const tableData = dt.data.data[0]?.data;
                 const totalCount = dt.data.data[0]?.totalCount[0]?.count;
                 
                 if (obj.table === 'table1') {
                   // Check if table has data
                   if (!tableData || tableData.length === 0) {
                     console.warn('No data found for table 1');
                     this.$store.commit("sbError", "No data found for table 1");
                   }
                   this.tableOnetwofile = tableData || [];
                   this.table1Totaltwofile(totalCount || 0);	
                 } else {
                   // Check if table has data
                   if (!tableData || tableData.length === 0) {
                     console.warn('No data found for table 2');
                     this.$store.commit("sbError", "No data found for table 2");
                   }
                   this.tableTwotwofile = tableData || [];
                   this.table2Totaltwofile(totalCount || 0);	
                 }
                         
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               const tableNum = obj.table === 'table1' ? '1' : '2';
               this.$store.commit("sbError", `Error loading table ${tableNum}: ${err.message || err || "Unknown error!"}`);
               console.log(err);
               // Reset table data on error
               if (obj.table === 'table1') {
                 this.tableOnetwofile = [];
                 this.table1Totaltwofile(0);
               } else {
                 this.tableTwotwofile = [];
                 this.table2Totaltwofile(0);
               }
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         },
         twoFileannexureSummary(collection,id){
           this.pageinationEmpty();
           this.selectedannexure = collection;
           this.ailoadingimage = true;
           const filter = {
             limit: 50,
             page:1,
           };
           this.GlobaleCollection = {collection:collection,id:id,page:1,key:null,}
           this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + collection + "/v/" + id,{filter})
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.currentannexuredata = this.indexOrder(dt.data.data[0].data);
                 this.storeTotal(dt.data.data[0].totalCount[0]?.count);
                 this.originalcurrentannexuredata = [...this.currentannexuredata]
               } else  { 
                 this.newAnnexersdata  = [];
                 this.currentannexuredata  = [];
                 throw new Error(dt.data.message || "Error Get Combo Code"); 
                 }
             })
             .catch((err) => {
               this.$store.commit("sbError", `Error loading table data: ${err.message || err || "Unknown error!"}`);
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
         },
         generateAnnexurelist(index = 0) {
           
           let ndata = [];
           let selectedverification = this.verificationlist[index] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           this.annexurenames = {};
           if (Object.keys(rundata).length > 0) {
             let annxdata = rundata;
             let hasannexure = false;
             for (let i = 0; i < (annxdata.annexure || []).length; i++) {
               const el = (annxdata.annexure || [])[i];
               ndata.push({
                 name: el.name,
                 value: el.collection,
                 displayName: el.displayName,
     
               });
               this.annexurenames[el.collection] = el.name;
               if (el.collection === this.selectedannexure) hasannexure = true;
             }
             if (!hasannexure) this.selectedannexure = ndata[0]?.value;
     
             this.readAnnexure(this.selectedannexure, selectedverification._id);
           }
           this.annexurelist = ndata;
         },
         readAnnexure(c, v) {
           
           this.filtertwofile = '';
           if (c) {
             this.selectedannexure = c;
             let filterannexure = "";
             this.filterselectkey = "";
             if (this.annexurenames[this.selectedannexure] === 'Table 1 Unmatched') {
               filterannexure = this.collectionnames["Table 2 Unmatched"];
               this.filterselectkey = "Table2_SNo"
             } else if (this.annexurenames[this.selectedannexure] === 'Table 2 Unmatched') {
               filterannexure = this.collectionnames["Table 1 Unmatched"];
               this.filterselectkey = "Table1_SNo"
     
             }
             if (this.analysisDetails.filecount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
               this.loading = true;
             const filter = {
               limit: 50,
               page:1,
             };
             Promise.resolve()
               .then(() => {
                 return this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + c + "/v/" + v, {
                   filter
                 });
               })
               .then((dt) => {
                 if (dt.data.status === "success") {
                   
                   this.pageinationEmpty();
                   this.loading = false;
                   this.annexuredata[c] = dt.data.data;
                   
                   if (this.analysisDetails.filecount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
                   this.currentannexuredata = this.indexOrder(dt.data.data[0]?.data);
                   this.storeTotal(dt.data.data[0].totalCount[0]?.count);
                 } else {
                   this.newAnnexersdata = this.indexOrder(dt.data.data[0]?.data);
                   this.storeTotalnew(dt.data.data[0].totalCount[0]?.count);
                 }
                   if (filterannexure) return this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + filterannexure + "/v/" + v, {
                     filter
                   });
                 } else throw new Error(dt.data.message || "Error reading annexure");
               })
               .then((dt) => {
                 if (filterannexure) {
                   if (dt.data.status === "success") {
                     this.dataforfilter = dt.data.data;
                     
                     this.selectedids = [];
                   } else throw new Error(dt.data.message || "Error reading annexure");
                 }
               })
               .catch((err) => {
                 this.$store.commit("sbError", err.message || err || "Unknown error!");
                 this.currentannexuredata = [];
                 console.log(err);
               })
               .finally(() => {
                 this.globalfilterpopClose();
                 this.loading = false;
               });
             } else {
                // single file annexure read               
             }
             
             // }
           }
         },
        /**
         * Fetch file upload status from the fileuploadstatus API and store in fileUploadStatusFiles.
         * Used by isRunDisabled to check if any file is still queued/processing.
         * @returns {Promise} Resolves when status data is fetched
         */
        async fetchFileUploadStatus() {
          try {
            const response = await this.axios.post('/v2/bot/analysis/fileuploadstatus/' + this.anaid);
            if (response?.data?.status === 'success') {
              this.fileUploadStatusFiles = response.data.data?.files || [];
            }
          } catch (err) {
            console.error('[TransactionMatchingConfigNew]: fetchFileUploadStatus error', err);
          }
        },
        /**
         * Load bot configuration and details
         * @returns {Promise} Resolves when bot data is loaded
         */
          async loadBot(options = {}) {
            const { skipRulesRefresh = false } = options;
           this.selectFifo = [];
           try {
             const data = await this.loadAnalysisConfig({ tmaid: this.anaid, pid: this.id, bid: this.botid });
             if (data && Object.keys(data).length > 0) {
               this.botdetails = data.metadata || {};
               // Fetch download history now that botdetails is available
               this.fetchDownloadHistoryBackground();
               this.uploaddetails = data.uploaddetails || {};
               await this.fetchFileUploadStatus();
               this.setupList = data.setup || {};
               this.verificationlist = this.setupList.verification || [];
               this.inputparametersvalues = this.botdetails.inputparameter || {};
               this.fileConfigInfo = this.setupList?.fileconfig || {};

               // Check for v2 mode
               let configsList = Object.values(this.fileConfigInfo);
               let hasV2 = configsList.some(c => c.version === 'v2');
               let allV2 = configsList.length > 0 && configsList.every(c => c.version === 'v2');
               
               this.isV2Mode = false;
               if (hasV2) {
                   if (!allV2) {
                       this.$store.commit('sbError', 'Error: All file configurations must be version v2.', { root: true });
                       throw new Error('Mixed file configuration versions. All file configurations must be version v2.');
                   } else {
                       this.isV2Mode = true;
                   }
               }
               
               this.rundetailsall = data.rundetails;

               // Merge actiondetails from loadAnalysisConfig response into analysisDetails
               if (data.actiondetails) {
                 this.analysisDetails = { ...this.analysisDetails, actiondetails: data.actiondetails };
               }
               
               this.rundetails = {};
               for (const i of data.rundetails || []) {
                 this.rundetails[i.verification] = i;
               }
               const selectedverification = this.verificationlist[0] || {};
               this.statusCheck = (this.rundetails[selectedverification._id] || {}).stage;
               this.runDetailUpdatedAt = (this.rundetails[selectedverification._id] || {}).updated_at;  
               this.polingstatges = this.statusCheck;
               
               this.stageMassage = (this.rundetails[selectedverification._id] || {}).message;
               this.generateAnnexurelist();
               this.collectionnames = {};
               for (const k in this.annexurenames) {
                 if (Object.hasOwnProperty.call(this.annexurenames, k)) {
                   const el = this.annexurenames[k];
                   this.collectionnames[el] = k;
                 }
               }
               // to check if inputparameter are available and if the same is saved
               this.savedParameters = false;
               if ((this.setupList.parameter || []).length > 0) {
                 this.hasParameters = true;
                 let allparametersaved = true;
                 for (const ele of this.setupList.parameter) {
                   if (!Object.hasOwnProperty.call((this.botdetails.inputparameter || {}), ele._id)) {
                     allparametersaved = false;
                   }
                 }
                 this.savedParameters = allparametersaved;
               } else {
                 this.hasParameters = false;
               }
     
               // set default input parameter values
               const availableids = [];
               for (const k in this.inputparametersvalues) {
                 if (Object.hasOwnProperty.call(this.inputparametersvalues, k)) {
                   availableids.push(k);
                 }
               }
               for (const i of this.setupList.parameter || []) {
                 if (availableids.indexOf(i._id) === -1) {
                   if (
                     Object.hasOwnProperty.call(
                       this.setupList.inputparameterconfig || {},
                       i._id
                     )
                   )
                     this.inputparametersvalues[i._id] = (
                       this.setupList.inputparameterconfig[i._id] || {}
                     ).value;
                   else this.inputparametersvalues[i._id] = i?.value;
                 }
               }
     
               if (this.inputparametersvalues) {
                 const firstKey = Object.keys(this.inputparametersvalues)[0];
                 this.rulesdata = (this.inputparametersvalues[firstKey] || []).filter(x => x != null);
                 this.rulesdataCopy  = JSON.parse(JSON.stringify(this.rulesdata));
     
               } else {
                 this.rulesdata = [];
               }
     
               this.loadColumns(this.setupList);
             }
           } catch (err) {
             console.error('[TransactionMatchingConfigNew]: loadBot error', err);
             this.$store.commit("sbError", err.message || err || "Unknown error!");
           }
           if (!skipRulesRefresh) {
             // Refresh key-normalized rules after analysis details are already available.
             await this.loadRulesV2();
           }
         },
         loadBotAi() {
           this.loading = true;
           this.selectFifo = [];
           return this.loadAnalysisConfig({ tmaid: this.anaid, pid: this.id, bid: this.botid }).then(data => {
             if (data && Object.keys(data).length > 0) {
               this.botdetails = data.metadata || {};
               // Fetch download history now that botdetails is available
               this.fetchDownloadHistoryBackground();
               this.uploaddetails = data.uploaddetails || {};
               this.fetchFileUploadStatus();
               this.setupList = data.setup || {};
               this.verificationlist = this.setupList.verification || [];
               this.inputparametersvalues = this.botdetails.inputparameter || {};
               this.fileConfigInfo = this.setupList?.fileconfig || {};

               // Check for v2 mode
               let configsList = Object.values(this.fileConfigInfo);
               let hasV2 = configsList.some(c => c.version === 'v2');
               let allV2 = configsList.length > 0 && configsList.every(c => c.version === 'v2');
               
               this.isV2Mode = false;
               if (hasV2) {
                   if (!allV2) {
                       this.$store.commit('sbError', 'Error: All file configurations must be version v2.', { root: true });
                       throw new Error('Mixed file configuration versions. All file configurations must be version v2.');
                   } else {
                       this.isV2Mode = true;
                   }
               }
               
               this.rundetailsall = data.rundetails;

               // Merge actiondetails from loadAnalysisConfig response into analysisDetails
               if (data.actiondetails) {
                 this.analysisDetails = { ...this.analysisDetails, actiondetails: data.actiondetails };
               }
               
               this.rundetails = {};
               for (const i of data.rundetails || []) {
                 this.rundetails[i.verification] = i;
               }
               let selectedverification = this.verificationlist[0] || {};
               this.statusCheck = (this.rundetails[selectedverification._id] || {}).stage;
               this.polingstatges = this.statusCheck;
               
               this.stageMassage = (this.rundetails[selectedverification._id] || {}).message;
              //  this.generateAnnexurelist();
               this.collectionnames = {};
               for (const k in this.annexurenames) {
                 if (Object.hasOwnProperty.call(this.annexurenames, k)) {
                   const el = this.annexurenames[k];
                   this.collectionnames[el] = k
                 }
               }
               // to check if inputparameter are available and if the same is saved
               this.savedParameters = false;
               if ((this.setupList.parameter || []).length > 0) {
                 this.hasParameters = true;
                 let allparametersaved = true;
                 for (let ele of this.setupList.parameter) {
                   if (!Object.hasOwnProperty.call((this.botdetails.inputparameter || {}), ele._id)) {
                     allparametersaved = false;
                   }
                 }
                 this.savedParameters = allparametersaved;
               } else {
                 this.hasParameters = false;
               }
     
               // set default input parameter values
               let availableids = [];
               for (const k in this.inputparametersvalues) {
                 if (Object.hasOwnProperty.call(this.inputparametersvalues, k)) {
                   availableids.push(k);
                 }
               }
               for (const i of this.setupList.parameter || []) {
                 if (availableids.indexOf(i._id) === -1) {
                   if (
                     Object.hasOwnProperty.call(
                       this.setupList.inputparameterconfig || {},
                       i._id
                     )
                   )
                     this.inputparametersvalues[i._id] = (
                       this.setupList.inputparameterconfig[i._id] || {}
                     ).value;
                   else this.inputparametersvalues[i._id] = i?.value;
                 }
               }
     
               if (this.inputparametersvalues) {
                 const firstKey = Object.keys(this.inputparametersvalues)[0];
                 this.rulesdata = (this.inputparametersvalues[firstKey] || []).filter(x => x != null);
                 this.rulesdataCopy  = JSON.parse(JSON.stringify(this.rulesdata));
     
               } else {
                 this.rulesdata = [];
               }
     
               this.loadColumns(this.setupList);
             }
           }).catch(err => {
             console.log(err);
             this.$store.commit("sbError", err.message || err || "Unknown error!");
           }).finally(() => {
             this.loading = false;
             // Fetch key-normalized rules from new V2 GET API (non-blocking).
             if (this.isV2Mode) this.loadRulesV2();
           })
         },
         openAddEditRuleDialog(d = {}, type = "add", k) {
           this.addeditdata = {
             type: "1:1"
           };
           this.aggregationError = '';
           this.copycolumns = Object.values(this.columns);
           const newAddeditdatacols = {};
           for (const k in this.columns) {
             if (Object.hasOwnProperty.call(this.columns, k)) {
               newAddeditdatacols[k] = {
                 type: 0,
                 negative: false,
               };
             }
           }
           this.addeditdatacols = newAddeditdatacols;
           this.selectedrulecolumn = [];
           this.aggregationtypesection = {};
           if (type === "add") {
             this.addeditdata.nature = "Automated";
             this.addeditdatakey = (this.rulesdata.length || 0) + 1;
           } else {
             this.addeditdata = {
               ...this.addeditdata,
               ...d
             };
             // For V2 mode cols is an array on addeditdata — preserve it.
             // For legacy mode, move cols into addeditdatacols (object format).
             if (!this.isV2Mode) {
               delete this.addeditdata?.cols;
             }
             this.addeditdatacols = {
               ...this.addeditdatacols,
               ...(Array.isArray(d.cols) ? {} : d.cols)
             };
             this.addeditdatakey = k;
             for (const k in d.cols) {
               if (Object.hasOwnProperty.call(d.cols, k)) {
                 // const el = this.addeditdatacols[k];
                 this.selectedrulecolumn.push(k);
               }
             }
             const newAggSection = {};
             for (let i = 0; i < (d.agg_cols || []).length; i++) {
               newAggSection[d.agg_cols[i]] = d.agg_type[i];
             }
             this.aggregationtypesection = newAggSection;
           }
           this.addeditdata.__type = type;
           this.addeditdialog = true;
           this.copycolumns = Object.values(this.columns);
         },
         updateRuleV2(type, ruleData) {
           if (!ruleData || !Object.keys(ruleData.cols || {}).length) {
             return this.$store.commit('sbError', 'Add at least one rule');
           }
           const dt = { ...ruleData };
           delete dt.__type;
           if (type === 'add') {
             dt.number = (this.rulesdata.length + 1).toString();
             this.rulesdata.push(dt);
           } else if (type === 'edit') {
             dt.number = this.addeditdata.number;
             // Preserve the server-assigned key so the upsert endpoint updates the existing item.
             if (this.addeditdata.key) dt.key = this.addeditdata.key;
             this.$set(this.rulesdata, this.addeditdatakey, dt);
           }
           this.addeditdialog = false;
           // Auto-save via key-based V2 API (falls back to bulk if keys unavailable).
           this.$nextTick(() => {
             this.saveRuleV2(dt);
           });
         },
         updateRule(type) {
           let dt = {
             ...this.addeditdata
           };
           let colsdt = {
             ...this.addeditdatacols
           };
           let newcoldt = {}
           let groupcols = [];
           for (const k in colsdt) {
             if (Object.hasOwnProperty.call(colsdt, k)) {
               const el = colsdt[k];
               if (this.selectedrulecolumn.indexOf(k) > -1) {
                 newcoldt[k] = el;
                 if (this.columns[k].type === 'string' || this.columns[k].type === 'date') groupcols.push(k)
               }
             }
           }
           if (dt.type === "1:M" || dt.type === "M:1" || dt.type === "M:M") {
             dt.group_by = groupcols;
             dt.agg_cols = [];
             dt.agg_type = [];
             for (const k in this.aggregationtypesection) {
               if (Object.hasOwnProperty.call(this.aggregationtypesection, k)) {
                 const el = this.aggregationtypesection[k];
                 if (el) {
                   dt.agg_cols.push(k);
                   dt.agg_type.push(el);
                 }
               }
             }
           }
           this.copycolumns = Object.values(this.columns);
           
           dt.cols = newcoldt;
           if (Object.keys(newcoldt).length === 0) {
             return this.$store.commit("sbError", "Add at least one rule");
           }
           // Validate aggregation when mandatory
           if (this.isAggregationMandatory && !this.hasValidAggregation) {
             this.aggregationError = 'Provide at both aggregation value (amount1 or amount2).';
             return this.$store.commit("sbError", this.aggregationError);
           }
           this.aggregationError = '';
           delete dt.__type;
           if (type === "add") {
             dt.number = (this.addeditdatakey || "").toString();	
             this.rulesdata.push(dt);
           } else if (type === "edit") {
             dt.number = this.addeditdata.number;
             this.$set(this.rulesdata, this.addeditdatakey, dt);
           }
           this.addeditdialog = false;
         },
         async startRun() {
           this.loading = true;
           let data = {
             isittm: true,
             tmaid: this.analysisDetails._id,
             toprocess: this.analysisDetails.to_process,
             ...this.botAccessFields(),
           }
           await this.axios
             .post("/v2/bot/analysis/startrun/" + this.analysisDetails.bpid, {
               pid: data.pid,
               data
             })
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.$store.commit("sbSuccess", "Run Started");
                 this.loadBot();
               } else throw new Error("Error starting run");
             })
             .catch((err) => {
               this.$store.commit("sbError", err?.response?.data?.message || err?.message || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.loading = false;
             });
         },
         loadColumns(dt) {
           let tcols = {};
           let dcount = 1;
           const newFilenames = [];
           for (const i of dt.file) {
             newFilenames[dcount - 1] = i.name || ("Data " + dcount + " Columns");
             for (const j of i.columns) {
               tcols[j.name] = j;
             }
             dcount++;
           }
           this.filenames = [...newFilenames];
           dcount = 1;
           for (const j in dt.fileconfig) {
             if (Object.hasOwnProperty.call(dt.fileconfig, j)) {
               const el = dt.fileconfig[j];
               for (const k of el.columnmap || []) {
                 // Support both legacy array format [colKey, colLabel] and v2 object format { label }
                 const colKey = Array.isArray(k) ? k[0] : k.label;
                 const colVal = Array.isArray(k) ? k[1] : k.label;
                 if (colKey && tcols[colKey]) {
                   tcols[colKey]["data" + dcount] = colVal;
                 }
               }
               dcount++;
             }
           }
           this.columns = {};
           for (const k in tcols) {
             if (Object.hasOwnProperty.call(tcols, k)) {
               const el = tcols[k];
               if (el.data1 && el.data2) this.columns[k] = el;
             }
           }
           this.copycolumns = Object.values(this.columns);
         },
         initializeSetupAiObj() {
       if (!this.setupaiObj || typeof this.setupaiObj !== 'object') {
       this.setupaiObj = {};
         }
       this.setupaiObj = {};
           Object.values(this.columns).forEach((column) => {
              if (column.type === "string") {
               this.$set(this.setupaiObj, column.name, { matchpercentage: 80 }); // Default for string
             } else if (column.type === "number") {
               this.$set(this.setupaiObj, column.name,{ matchpercentage: 5, oppositesignmatch: false }); // Default for number
             } else if (column.type === "date") {
               this.$set(this.setupaiObj, column.name, { matchpercentage: 30, }); // Default for date
             }
           });
         },
         saveInputParameters() {
           this.loading = true;
           let ipval = {};
           ipval[Object.keys(this.inputparametersvalues)[0]] = this.rulesdata;
           
           ipval[Object.keys(this.inputparametersvalues)[0]] = ipval[Object.keys(this.inputparametersvalues)[0]].map((item, index) => {
              return { ...item, number: (index + 1).toString() }; // Assign index + 1 as string
          });
           let objectInput = {
             inputparameter: ipval,
             ...this.botAccessFields(),
           };
           const url = "/v2/bot/analysis/storeinputparameter/" + this.analysisDetails.bpid;
           this.axios
             .post(url, {
               pid: objectInput.pid,
               data: objectInput,
             })
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.$store.commit("sbSuccess", "Input Parameters Updated");
                 this.refreshData();
               } else throw new Error("Error updating input parameters");
             })
             .catch((err) => {
               console.error('[RulesV2]: saveInputParameters (legacy bulk) — error', err);
               this.$store.commit("sbError", err.message || err || "Unknown error!");
             })
             .finally(() => {
               this.loading = false;
             });
         },

         /** Return the tmaid (first key of inputparametersvalues) for item-level rule APIs */
         _getRulesTmaid() {
           return Object.keys(this.inputparametersvalues || {})[0] || null;
         },

         /**
          * Load rules via new GET API (returns items with `key` stamped by server).
          * Falls back to the already-loaded rulesdata if the GET endpoint is unavailable.
          */
         async loadRulesV2() {
           const tmaid = this.analysisDetails._id;
           const bpid = this.analysisDetails.bpid;
           if (!bpid) {
             console.log('[RulesV2]: loadRulesV2 — skipped, missing bpid:', bpid);
             return;
           }
           const url = `/v2/bot/analysis/inputparameter/get/${bpid}`;
           try {
             const response = await this.axios.post(url);
             if (response.data.status === 'success') {
               this.rulesdata = (response.data.data || []).filter(x => x != null);
               this.rulesdataCopy = JSON.parse(JSON.stringify(this.rulesdata));
               this.$set(this.inputparametersvalues, tmaid, this.rulesdata);
             }
           } catch (err) {
             console.warn('[RulesV2]: loadRulesV2 — error (non-fatal, using loaded data):', err.message);
           }
         },

         /**
          * V2: upsert one rule via key-based API (add = no key, edit = has key).
          * Called after updateRuleV2 pushes / sets into rulesdata.
          */
         async saveRuleV2(ruleItem) {
           const tmaid = this._getRulesTmaid();
           const bpid = this.analysisDetails.bpid;
           if (!tmaid || !bpid) {
             this.saveInputParameters();
             return;
           }
           const url = `/v2/bot/analysis/inputparameter/upsert/${bpid}`;
           const requestBody = this.botAccessBody({ inputparameter: ruleItem });
           this.loading = true;
           try {
             const response = await this.axios.post(url, requestBody);
             if (response.data.status === 'success') {
               await this.loadRulesV2();
               this.$store.commit('sbSuccess', 'Rule saved successfully');
             } else {
               throw new Error(response.data.message || 'Failed to save rule');
             }
           } catch (err) {
             console.error('[RulesV2]: saveRuleV2 — error, falling back to legacy bulk save:', err);
             this.saveInputParameters();
           } finally {
             this.loading = false;
           }
         },

         /**
          * V2: delete one rule item.
          * Uses key-based DELETE API when item has `key`; falls back to legacy bulk save.
          * @param {number} index - index in rulesdata
          */
         async deleteRuleV2(index) {
           const item = this.rulesdata[index];
           if (!item) return;

           const tmaid = this._getRulesTmaid();
           const bpid = this.analysisDetails.bpid;

           if (item.key && bpid) {
             const url = `/v2/bot/analysis/inputparameter/delete/${bpid}/${item.key}`;
             this.loading = true;
             try {
               const response = await this.axios.delete(url, { data: this.botAccessBody() });
               if (response.data.status === 'success') {
                 this.rulesdata.splice(index, 1);
                 this.rulesdataCopy = JSON.parse(JSON.stringify(this.rulesdata));
                 this.$set(this.inputparametersvalues, tmaid, this.rulesdata);
                 this.$store.commit('sbSuccess', 'Rule deleted successfully');
                 return;
               } else {
                 throw new Error(response.data.message || 'Failed to delete rule');
               }
             } catch (err) {
               this.$store.commit('sbError', err.message || err || 'Unknown error!');
               console.error('[RulesV2]: deleteRuleV2 — error', err);
               return;
             } finally {
               this.loading = false;
             }
           }

           // Legacy: no key — remove locally then bulk-save.
           this.rulesdata.splice(index, 1);
           this.saveInputParameters();
         },

         /**
          * V2: persist current rule drag-drop order.
          * Uses PUT /reorder when all items have keys; falls back to legacy bulk save.
          */
         async saveRuleOrderV2() {
           const tmaid = this._getRulesTmaid();
           const bpid = this.analysisDetails.bpid;
           const hasAllKeys = this.rulesdata.every(r => !!r.key);
           console.log('[RulesV2]: saveRuleOrderV2 — tmaid:', tmaid, '| bpid:', bpid, '| hasAllKeys:', hasAllKeys);

           if (hasAllKeys && bpid) {
             this.loading = true;
             // Backend expects a plain array of key strings in the desired order.
             const orderedFilters = this.rulesdata.map(r => r.key);
             const url = `/v2/bot/analysis/inputparameter/reorder/${bpid}`;
             const requestBody = this.botAccessBody({ inputparameter: orderedFilters });
             try {
               const response = await this.axios.put(url, requestBody);
               if (response.data.status === 'success') {
                 this.$store.commit('sbSuccess', 'Rule order saved successfully');
                 this.rulesdataCopy = JSON.parse(JSON.stringify(this.rulesdata));
               } else {
                 throw new Error(response.data.message || 'Failed to save rule order');
               }
             } catch (err) {
               this.$store.commit('sbError', err.message || err || 'Unknown error!');
               console.error('[RulesV2]: saveRuleOrderV2 — error', err);
             } finally {
               this.loading = false;
             }
             return;
           }

           // Legacy: missing keys — bulk save the current order.
           this.saveInputParameters();
         },
         moveTransaction(d, type,select) {
           let ndt = {}
           let summarycollection = this.collectionnames["Summary"];
           let allanexures = {
             "a": this.collectionnames["Table 1 Matched"],
             "b": this.collectionnames["Table 2 Matched"],
             "c": this.collectionnames["Table 1 Unmatched"],
             "d": this.collectionnames["Table 2 Unmatched"],
           }
           if (this.annexurenames[select.collection] === 'Table 1 Matched') {
             if (type === "approve") {
               ndt = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   mycol: "Table1_SNo",
                   countercol: "Table2_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: select.collection,
                   fromcollection2: this.collectionnames["Table 2 Matched"]
                 }
               }
             } else if (type === "reject") {
               ndt = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   mycol: "Table1_SNo",
                   countercol: "Table2_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: select.collection,
                   tocollection1: this.collectionnames["Table 1 Unmatched"],
                   fromcollection2: this.collectionnames["Table 2 Matched"],
                   tocollection2: this.collectionnames["Table 2 Unmatched"]
                 }
               }
             }
           } else if (this.annexurenames[select.collection] === 'Table 2 Matched') {
             if (type === "approve") {
               ndt = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   mycol: "Table2_SNo",
                   countercol: "Table1_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: select.collection,
                   fromcollection2: this.collectionnames["Table 1 Matched"]
                 }
               }
             } else if (type === "reject") {
               ndt = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   mycol: "Table2_SNo",
                   countercol: "Table1_SNo",
                   summarycollection: summarycollection,
                   fromcollection2: this.collectionnames["Table 1 Matched"],
                   tocollection2: this.collectionnames["Table 1 Unmatched"],
                   fromcollection1: select.collection,
                   tocollection1: this.collectionnames["Table 2 Unmatched"]
                 }
               }
             }
           } else if (this.annexurenames[select.collection] === 'Table 1 Unmatched') {
             if (type === "match") {
               ndt = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   countercodes: this.selectedids,
                   mycol: "Table2_SNo",
                   countercol: "Table1_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: this.collectionnames["Table 2 Unmatched"],
                   fromcollection2: select.collection,
                   tocollection1: this.collectionnames["Table 2 Matched"],
                   tocollection2: this.collectionnames["Table 1 Matched"]
                 }
               }
             }
           } else if (this.annexurenames[select.collection] === 'Table 2 Unmatched') {
             if (type === "match") {
               ndt = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   countercodes: this.selectedids,
                   mycol: "Table1_SNo",
                   countercol: "Table2_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: this.collectionnames["Table 1 Unmatched"],
                   fromcollection2: select.collection,
                   tocollection1: this.collectionnames["Table 1 Matched"],
                   tocollection2: this.collectionnames["Table 2 Matched"]
     
                 }
               }
             }
           }
           let success = false;
           // this.loading = true;
           this.ailoadingimage = true;
           this.axios
             .post("/v2/financialclose/analysis/updatetransactionmatching/" + this.metadata._id, ndt)
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.comparedialog = false;
     
                 this.$store.commit("sbSuccess", "Matched Successfully");
                 success = true;
               } else throw new Error("Error updating");
             })
     
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
               if (success) this.twofilesinglecollection(select);
             });
         },
         moveTransactionNew(d, type,select) {
           
           let ndtnew = {}
           this.compareloadingNew = true;
           let summarycollection = this.collectionnames["Summary"];
           let allanexures = {
             "a": this.collectionnames["Table 1 Matched"],
             "b": this.collectionnames["Table 2 Matched"],
             "c": this.collectionnames["Table 1 Unmatched"],
             "d": this.collectionnames["Table 2 Unmatched"],
           }
           if (this.annexurenames[select.collection] === 'Table 1 Matched') {
             if (type === "approve") {
             ndtnew = {
               filter: {
                 id: d._id,
                 type: type,
                 allanexures: allanexures,
                 countercodes: this.selectedidsNew,
                 mycol: "Table1_SNo",
                 countercol: "Table2_SNo",
                 summarycollection: summarycollection,
                 fromcollection1: this.collectionnames["Table 1 Matched"],
                 tocollection1: this.collectionnames["Table 1 Unmatched"],
                 fromcollection2: this.collectionnames["Table 2 Matched"],
                 tocollection2: this.collectionnames["Table 2 Unmatched"]
               }
             }
             } else if (type === "reject") {
               ndtnew = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   mycol: "Table1_SNo",
                   countercol: "Table2_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: select.collection,
                   tocollection1: this.collectionnames["Table 1 Unmatched"],
                   fromcollection2: this.collectionnames["Table 2 Matched"],
                   tocollection2: this.collectionnames["Table 2 Unmatched"]
                 }
               }
             }
     
           } else if (this.annexurenames[select.collection] === 'Table 2 Matched') {
             if (type === "approve") {
             ndtnew = {
               filter: {
                 id: d._id,
                 type: type,
                 allanexures: allanexures,
                 countercodes: this.selectedidsNew,
                 mycol: "Table2_SNo",
                 countercol: "Table1_SNo",
                 summarycollection: summarycollection,
                 fromcollection1: this.collectionnames["Table 2 Matched"],
                 tocollection1: this.collectionnames["Table 1 Unmatched"],
                 fromcollection2: this.collectionnames["Table 1 Matched"],
                 tocollection2: this.collectionnames["Table 2 Unmatched"]
               }
             }
             } else if (type === "reject") {
               ndtnew = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   mycol: "Table1_SNo",
                   countercol: "Table2_SNo",
                   summarycollection: summarycollection,
                   fromcollection1: select.collection,
                   tocollection1: this.collectionnames["Table 1 Unmatched"],
                   fromcollection2: this.collectionnames["Table 2 Matched"],
                   tocollection2: this.collectionnames["Table 2 Unmatched"]
                 }
               }
             }
           } else if (this.annexurenames[select.collection] === 'Table 1 Unmatched') {
             if (type === "match") {
               ndtnew = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   countercodes: this.selectedidsNew,
                   mycol: "Table1_SNo",
                   countercol: "Table2_SNo",
                   summarycollection: summarycollection,
                   fromcollection2: select.collection,
                   tocollection1: this.collectionnames["Table 1 Matched"],
                   fromcollection1: this.collectionnames["Table 2 Unmatched"],
                   tocollection2: this.collectionnames["Table 2 Matched"]
                 }
               }
             }
           } else if (this.annexurenames[select.collection] === 'Table 2 Unmatched') {
             if (type === "match") {
               ndtnew = {
                 filter: {
                   id: d._id,
                   type: type,
                   allanexures: allanexures,
                   countercodes: this.selectedidsNew,
                   mycol: "Table2_SNo",
                   countercol: "Table1_SNo",
                   summarycollection: summarycollection,
                   fromcollection2: select.collection,
                   tocollection1: this.collectionnames["Table 2 Matched"],
                   fromcollection1: this.collectionnames["Table 1 Unmatched"],
                   tocollection2: this.collectionnames["Table 1 Matched"]
                 }
               }
             }
           }
     
           let success = false;
           this.loading = true;
           this.axios
             .post("/v2/financialclose/analysis/updatetransactionmatching/" + this.metadata._id, ndtnew)
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.comparedialogNew = false;
                 this.$store.commit("sbSuccess", "Updated");
                 success = true;
               } else throw new Error("Error updating");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.compareloadingNew = false;
               this.loading = false;
               if (success) this.generateAnnexurelistCompo(this.collectionNameList, 0)
             });
         },
         bulkManualMatchingOnefile(){
          this.bulkMatchonefile = true;
          this.fileuplaoded = null; 
          this.resetFlag = !this.resetFlag;
          this.fileuplaoded = [];
          this.file = null;
         },
         /**
          * Trigger bulk manual matching for consolidated annexure via child component ref
          */
         bulkManualMatchingConsolidated(){
          const consolidatedComponent = this.$refs.consolidatedResultsComponent;
          if (consolidatedComponent && typeof consolidatedComponent.openBulkManualMatchingDialog === 'function') {
            consolidatedComponent.openBulkManualMatchingDialog();
          } else {
            console.error('[TransactionMatchingConfigNew]: Consolidated results component ref not found or method not available');
            this.$store.commit('sbError', 'Unable to open bulk manual matching dialog');
          }
         },
        bulkManualMatching(){
          this.bulkMatchtwofile = true;
          this.fileuplaoded = null; 
          this.resetFlag = !this.resetFlag;
          this.fileuplaoded = [];
          this.file = null;
          
        },
        async GenerateResult(item){
           this.loading = true;
           const section = typeof item === 'object' ? item.section : item;
           const tables = typeof item === 'object' ? item.tables : undefined;
           const filters = typeof item === 'object' ? item.filters : undefined;
           const sort = typeof item === 'object' ? item.sort : undefined;
           const dataFilters = typeof item === 'object' ? item.dataFilters : undefined;
           const collection = typeof item === 'object' ? item.collection : undefined;
           const version = typeof item === 'object' ? item.version : undefined;
           const field = typeof item === 'object' ? item.field : undefined;
           const runId = typeof item === 'object' ? item.runId : undefined;
           const reportDate = typeof item === 'object' ? item.reportDate : undefined;
           const reportTable = typeof item === 'object' ? item.reportTable : undefined;
           const dateScope = typeof item === 'object' ? item.dateScope : undefined;
           try {
             await this.$store.dispatch('transactionMatching/generateResult', { 
               item: section,
               tables,
               filters,
               sort,
               dataFilters,
               collection,
               version,
               field,
               runId,
               reportDate,
               reportTable,
               dateScope,
               botId: this.botdetails._id 
             });
             // Refresh download history after successful generation
             await this.fetchDownloadHistoryBackground();
           } catch (err) {
             console.error('[TransactionMatchingConfigNew]: GenerateResult error', err);
           } finally {
             this.loading = false;
           }
        },
         async downloadResult(item) {
           this.loading = true;
           try {
             await this.$store.dispatch('transactionMatching/downloadResult', { 
               item, 
               botId: this.botdetails._id 
             });
           } catch (err) {
             console.error(err);
           } finally {
             this.loading = false;
           }
         },
         /**
          * Start background polling for Excel download history
          * Polls every 30 seconds to keep the cache fresh
          */
         startExcelHistoryPolling() {
           // Clear any existing interval first to prevent leaks
           this.stopExcelHistoryPolling();
           // Initial fetch
           this.fetchDownloadHistoryBackground();
           
           // Set up polling interval (every 30 seconds)
           this.excelHistoryPollingInterval = setInterval(() => {
             this.fetchDownloadHistoryBackground();
           }, 30000); // 30000 ms = 30 seconds
         },
         /**
          * Stop background polling for Excel download history
          */
         stopExcelHistoryPolling() {
           if (this.excelHistoryPollingInterval) {
             clearInterval(this.excelHistoryPollingInterval);
             this.excelHistoryPollingInterval = null;
           }
         },
         /**
          * Start background polling for file ingestion status every 30 seconds.
          * Keeps fileUploadStatusFiles fresh so isRunDisabled reacts to stage changes.
          */
         startFileStatusPolling() {
           this.stopFileStatusPolling();
           this.fileStatusPollingInterval = setInterval(() => {
             this.fetchFileUploadStatus();
           }, 30000);
         },
         /**
          * Stop background polling for file ingestion status
          */
         stopFileStatusPolling() {
           if (this.fileStatusPollingInterval) {
             clearInterval(this.fileStatusPollingInterval);
             this.fileStatusPollingInterval = null;
           }
         },
         /**
          * Background fetch for download history with loading state
          */
         async fetchDownloadHistoryBackground() {
           // Guard: ensure botdetails._id is available
           if (!this.botdetails?._id) return;
           
           try {
             await this.$store.dispatch('transactionMatching/fetchDownloadHistory', this.botdetails._id);
           } catch (err) {
             console.error('[TransactionMatchingConfigNew]: Background polling error', err);
           }
         },
         /**
          * Fetch download history files from API
          */
         async fetchDownloadHistory() {
           await this.fetchDownloadHistoryBackground();
         },
         /**
          * Download a file from history
          * @param {Object} file - File object with upload property
          */
         downloadHistoryFile(file) {
           if (file && file.upload) {
             this.$nova.downloadFile(file.upload);
           } else {
             this.$store.commit("sbError", "File not available for download");
           }
         },
         openRecoincilationDialogue() {
           this.recoincilationItem = {}
           this.recoincilationErrorItem = {}
           this.recoincilationDialogue = true;
         },
         addRecoincilationItem(collection) {
           this.recoincilationItem.attachmentfile = [];
           this.recoincilationItem.index = this.recoincilationSummaryData.length + 5;
           this.axios.post('v2/financialclose/analysis/updateAnnexure/' + collection, {
             'data': this.recoincilationItem,
             // pid: this.id
           }).then(dt => {
             if (dt.data.status === 'success') {
               this.$store.commit("sbSuccess", dt.data.message || "Added Successfully");
               this.recoincilationDialogue = false;
               this.recoinSummary();
               // this.getRecoincialtionData()
             } else {
               this.recoincilationErrorItem = dt.data.data[0].index0
               throw new Error(dt.data.message || "Not added")
             }
           }).catch(err => {
             this.$store.commit("sbError", err.message || err || "Unknown error!");
             console.log(err);
           }).finally(() => {
             this.loading = false;
           })
         },
         addfilterbox() {
           this.addfilterdialog = true;
           this.filterobject = {
             tableid: [],
             column: [],
             operator: [],
             description: undefined,
             excludetype: 'Exclude',
             tag: '',
             status: '',
             filterword: '',
           }
           this.filterobjecterror = {};
           this.isEditing = false;
         },
         validateFilterObject() {
           this.filterobjecterror = {};
           if (!this.filterobject.description) {
             this.$set(this.filterobjecterror, 'description', "Description cannot be blank.");
           }
           if (!this.filterobject.tag) {
             this.$set(this.filterobjecterror, 'tag', "Tag cannot be blank.");
           }
           if (!this.filterobject.filterword) {
             this.$set(this.filterobjecterror, 'filterword', "Filter text cannot be blank.");
           }
           if (this.filterobject.column === null || this.filterobject.column.length === 0) {
             this.$set(this.filterobjecterror, 'column', "Column cannot be blank.");
           }
           if (this.filterobject.operator === null || this.filterobject.operator.length === 0) {
             this.$set(this.filterobjecterror, 'operator', "Operator cannot be blank.");
           }
           if (this.filterobject.tableid === null || this.filterobject.tableid.length === 0) {
             this.$set(this.filterobjecterror, 'tableid', "Table cannot be blank.");
           }
           if (this.filterobject.excludetype === undefined) {
             this.$set(this.filterobjecterror, 'excludetype', "Exclude type cannot be blank.");
           }
     
           return Object.keys(this.filterobjecterror).length === 0;
         },
         async addfilterPush() {
           if (this.validateFilterObject()) {
             this.addfilterdialogloading = true;
             const filterPayload = {
               ...this.filterobject,
               status: this.filterobject.status || 'active',
             };
             try {
               await this.upsertFilter({ bpid: this.analysisDetails.bpid, filterData: filterPayload, pid: this.analysisDetails.pid || this.id });
               await this.getfilterlist();
               this.$store.commit('sbSuccess', 'Filter added successfully');
               this.resetFilter();
               this.addfilterdialog = false;
             } catch (err) {
               console.error('[Filter]: addfilterPush — error', err);
             } finally {
               this.addfilterdialogloading = false;
             }
           }
         },
         /** Add one new filter item via upsert store action */
         async addfilterV2(formData) {
           this.addfilterdialogloading = true;
           // eslint-disable-next-line no-unused-vars
           const { filetype, ...rest } = formData;
           const [fc1 = '', fc2 = ''] = (filetype || '').split('_vs_');
           const filterPayload = {
             ...rest,
             fileConfig1: fc1 || rest.fileConfig1 || '',
             fileConfig2: fc2 || rest.fileConfig2 || '',
             status: rest.status || 'active',
           };
           try {
             await this.upsertFilter({ bpid: this.analysisDetails.bpid, filterData: filterPayload, pid: this.analysisDetails.pid || this.id });
             await this.getfilterlist();
             this.$store.commit('sbSuccess', 'Filter added successfully');
           } catch (err) {
             console.error('[Filter]: addfilterV2 — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         /** Update a single filter item via upsert store action */
         async updatefilterV2(item) {
           this.addfilterdialogloading = true;
           // eslint-disable-next-line no-unused-vars
           const { _index, indexno, filetype, ...rest } = item;
           const [fc1 = '', fc2 = ''] = (filetype || '').split('_vs_');
           const filterPayload = {
             ...rest,
             fileConfig1: fc1 || rest.fileConfig1 || '',
             fileConfig2: fc2 || rest.fileConfig2 || '',
             status: rest.status || 'inactive',
           };
           try {
             await this.upsertFilter({ bpid: this.analysisDetails.bpid, filterData: filterPayload, pid: this.analysisDetails.pid || this.id });
             await this.getfilterlist();
             this.$store.commit('sbSuccess', 'Filter updated successfully');
           } catch (err) {
             console.error('[Filter]: updatefilterV2 — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         /** Toggle a filter's enabled/disabled status via store action */
         async toggleFilterStatusV2(item, enabled) {
           if (!item.key) {
             this.$store.commit('sbError', 'Cannot toggle status: filter has no key. Please reload and try again.');
             return;
           }
           this.addfilterdialogloading = true;
           try {
             await this.toggleFilterStatus({ bpid: this.analysisDetails.bpid, key: item.key, enable: enabled, pid: this.analysisDetails.pid || this.id });
             await this.getfilterlist();
             this.$store.commit('sbSuccess', `Filter ${enabled ? 'enabled' : 'disabled'} successfully`);
           } catch (err) {
             console.error('[Filter]: toggleFilterStatusV2 — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         /** Delete a single filter item via store action */
         async deletefilterV2(item) {
           if (!item.key) {
             this.$store.commit('sbError', 'Unable to delete filter: no key found. Please reload and try again.');
             return;
           }
           this.addfilterdialogloading = true;
           try {
             await this.deleteFilter({ bpid: this.analysisDetails.bpid, key: item.key, pid: this.analysisDetails.pid || this.id });
             await this.getfilterlist();
             this.$store.commit('sbSuccess', 'Filter deleted successfully');
           } catch (err) {
             console.error('[Filter]: deletefilterV2 — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         /** Persist current filter order via store action */
         async saveOrderV2() {
           this.addfilterdialogloading = true;
           const orderedKeys = this.formatedData.map(f => ({ key: f.key }));
           try {
             await this.reorderFilters({ bpid: this.analysisDetails.bpid, orderedKeys, pid: this.analysisDetails.pid || this.id });
             this.$store.commit('sbSuccess', 'Filter order saved successfully');
             this.filterListCopy = JSON.parse(JSON.stringify(this.filterList));
           } catch (err) {
             console.error('[Filter]: saveOrderV2 — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         editfilterboxPush(item) {
           this.filterobjecterror = {};
           this.addfilterdialog = true;
           this.filterobject = {
             ...item
           };
           this.isEditing = true;
           this.currentIndex = item.indexno;
         },
         async deletefilterboxPush(item) {
           if (!item.key) {
             this.$store.commit('sbError', 'Unable to delete filter: no key found. Please reload and try again.');
             return;
           }
           this.addfilterdialogloading = true;
           try {
             await this.deleteFilter({ bpid: this.analysisDetails.bpid, key: item.key, pid: this.analysisDetails.pid || this.id });
             await this.getfilterlist();
             this.$store.commit('sbSuccess', 'Filter deleted successfully');
           } catch (err) {
             console.error('[Filter]: deletefilterboxPush — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         async updatefilterPush() {
           if (this.validateFilterObject()) {
             this.addfilterdialogloading = true;
             const filterPayload = {
               ...this.filterobject,
               status: this.filterobject.status || 'active',
             };
             try {
               await this.upsertFilter({ bpid: this.analysisDetails.bpid, filterData: filterPayload, pid: this.analysisDetails.pid || this.id });
               await this.getfilterlist();
               this.$store.commit('sbSuccess', 'Filter updated successfully');
               this.addfilterdialog = false;
               this.resetFilter();
             } catch (err) {
               console.error('[Filter]: updatefilterPush — error', err);
             } finally {
               this.addfilterdialogloading = false;
             }
           }
         },
         resetFilter() {
           this.filterobject.tag = '',
             this.filterobject.status = '',
             this.filterobject.tableid = '',
             this.filterobject.operator = '',
             this.filterobject.filterword = '',
             this.filterobject.excludetype = '',
             this.filterobject.description = '',
             this.filterobject.column = ''
           this.isEditing = false;
           this.currentIndex = null;
         },
         addfilterConfimation(){
     
           this.checkChangesFilter();
         },
         addrulesConfimation(){
           this.checkChangesRules();
         },
         
         async addfilter(type) {
           this.addfilterconfimloading = true;
           try {
             await this.getfilterlist();
             this.filterobject = {};
             this.$store.commit('sbSuccess', 'Filters saved successfully');
             this.addfilterconfim = false;
             if (type === 'continue') {
               this.e1 = 3;
               this.filterListCopy = JSON.parse(JSON.stringify(this.filterList));
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || err || 'Unknown error!');
             console.error('[Filter]: addfilter — error', err);
           } finally {
             this.addfilterconfimloading = false;
           }
         },
         /** Update a single filter via upsert store action */
         async updatefilter() {
           this.addfilterdialogloading = true;
           const filterPayload = {
             ...this.filterobject,
             status: this.filterobject.status || 'active',
           };
           try {
             await this.upsertFilter({ bpid: this.analysisDetails.bpid, filterData: filterPayload, pid: this.analysisDetails.pid || this.id });
             this.$store.commit('sbSuccess', 'Filter successfully updated');
             this.addfilterdialog = false;
             this.filterobject = {};
             await this.getfilterlist();
           } catch (err) {
             console.error('[Filter]: updatefilter — error', err);
           } finally {
             this.addfilterdialogloading = false;
           }
         },
         /** Toggle filter enabled/disabled status via store action */
         async enablefilterbox(key, action) {
           if (!key) {
             this.$store.commit('sbError', 'Cannot toggle status: filter has no key. Please reload and try again.');
             return;
           }
           this.loading = true;
           const enable = action === 'Enable';
           try {
             await this.toggleFilterStatus({ bpid: this.analysisDetails.bpid, key, enable, pid: this.analysisDetails.pid || this.id });
             await this.getfilterlist();
             this.$store.commit('sbSuccess', `${action.toLowerCase()} successfully`);
           } catch (err) {
             console.error('[Filter]: enablefilterbox — error', err);
           } finally {
             this.loading = false;
           }
         },
         enablemiddlewarebox(id, action) {
           this.loading = true;
           this.$store.dispatch('transactionMatching/enableMiddleware', {
             id,
             action,
             pid: this.analysisDetails.pid,
             tmaid: this.analysisDetails._id,
             bpid: this.analysisDetails.bpid,
           })
             .then(() => {
               this.getmiddlewarelist();
             })
             .catch((error) => {
               console.log(error);
             })
             .finally(() => {
               this.loading = false;
             });
         },
         changeMid(id, it) {
           let type = '';
           if (it) {
             type = 'Enable';
           } else {
             type = 'Disable';
           }
           this.enablemiddlewarebox(id, type);
         },
         addMiddlewarebox() {
           this.addMiddlewaredialog = true;
           this.middlewareObject = { filecomparison: '', basefile: '', middlewarename: '' };
           this.middlewareObjecterror = {};
           this.middFile = null;
         },
         middlewareValition(){
           this.middlewareObjecterror = {};
           if (!this.middlewareObject.middlewarename) {
             this.$set(this.middlewareObjecterror, 'name', "Name text cannot be blank.");
           }
           if (this.middlewareObject.basefile === undefined  || this.middlewareObject.basefile === null || this.middlewareObject.basefile.length === 0) {
             this.$set(this.middlewareObjecterror, 'basefile', "Basefile cannot be blank.");
           }
           if (this.middFile === null && !this.hasExistingMiddlewareFile()) {
             this.$set(this.middlewareObjecterror, 'middFile', "File is required");
             return
           }
           return Object.keys(this.middlewareObjecterror).length === 0;
         },
         async addMiddleware() {

           if (this.middlewareValition()) {
             
           
           if (this.middFile === null) {
             this.$store.commit("sbError", "Middleware File required");
             return
           }
           this.addMiddlewaredialogloading = true;
           this.middlewareObject.bpid = this.analysisDetails.bpid;
           this.middlewareObject.pid = this.analysisDetails.pid;
           this.middlewareObject.tmaid = this.analysisDetails._id;
           this.middlewareObject.bid = this.analysisDetails.bid;
          // Parse filecomparison dropdown value ("configId1_vs_configId2") into fileConfig1/fileConfig2
          if (this.middlewareObject.filecomparison) {
            const [fc1, fc2] = this.middlewareObject.filecomparison.split('_vs_');
            this.middlewareObject.fileConfig1 = fc1 || '';
            this.middlewareObject.fileConfig2 = fc2 || '';
          } else {
            const _mwConfigKeys = Object.keys(this.fileConfigInfo || {});
            this.middlewareObject.fileConfig1 = _mwConfigKeys[0] || '';
            this.middlewareObject.fileConfig2 = _mwConfigKeys[1] || '';
          }

          const uploadResponse = await this.$nova.uploadFile(this.axios, this.middFile, {});
          if (uploadResponse.data.status === "success") {
             const middlewarfile = uploadResponse.data.data[0]._id;
             this.middlewareObject.middlewarefile = [middlewarfile];
             this.middlewareObject.middlewarefilepath = uploadResponse.data.data[0].path;
             try {
               const result = await this.$store.dispatch('transactionMatching/addMiddleware', {
                 middlewareData: this.middlewareObject,
                 pid: this.analysisDetails.pid,
                 tmaid: this.analysisDetails._id,
                 bpid: this.analysisDetails.bpid,
               });
               if (result.status === 'validation_error') {
                 this.middlewareObjecterror = result.errors;
               } else {
                 this.addMiddlewaredialog = false;
                 this.getmiddlewarelist();
               }
             } catch (err) {
               console.log(err);
             } finally {
               this.addMiddlewaredialogloading = false;
             }
             }
           }
         },
         async updateMiddleware(id) {
           if (this.middlewareValition()) {
           this.addMiddlewaredialogloading = true;
           // Parse filecomparison dropdown value into fileConfig1/fileConfig2
           if (this.middlewareObject.filecomparison) {
             const [fc1, fc2] = this.middlewareObject.filecomparison.split('_vs_');
             this.middlewareObject.fileConfig1 = fc1 || '';
             this.middlewareObject.fileConfig2 = fc2 || '';
           }
           try {
             if (this.middFile !== null) {
               const uploadResponse = await this.$nova.uploadFile(this.axios, this.middFile, {});
               if (uploadResponse.data.status !== "success") {
                 this.$store.commit("sbError", "Failed to upload mapping master file");
                 return;
               }
               const middlewarfile = uploadResponse.data.data[0]._id;
               this.middlewareObject.middlewarefilepath = uploadResponse.data.data[0].path;
               this.middlewareObject.middlewarefile = [middlewarfile];
             } else {
               delete this.middlewareObject.middlewarefilepath;
             }
             const result = await this.$store.dispatch('transactionMatching/updateMiddleware', {
               id,
               middlewareData: this.middlewareObject,
               pid: this.analysisDetails.pid,
               tmaid: this.analysisDetails._id,
               bpid: this.analysisDetails.bpid,
             });
             if (result.status === 'validation_error') {
               this.middlewareObjecterror = result.errors;
             } else {
               this.addMiddlewaredialog = false;
               this.getmiddlewarelist();
             }
           } catch (err) {
             console.log('[TransactionMatchingConfigNew]:', err);
           } finally {
             this.addMiddlewaredialogloading = false;
           }
           }

         },
         editmiddlewarebox(item) {
           this.addMiddlewaredialog = true;
           this.middlewareObject = { filecomparison: '', basefile: '', middlewarename: '', ...item };
           this.middlewareObject.basefile = item.basefile || '';
           // Derive filecomparison from stored fileConfig1/fileConfig2
           if (item.fileConfig1 && item.fileConfig2) {
             this.middlewareObject.filecomparison = `${item.fileConfig1}_vs_${item.fileConfig2}`;
           } else {
             this.middlewareObject.filecomparison = item.filecomparison || '';
           }
           this.middlewareObject.middlewarefile = item.middlewarefile;
           this.middlewareObject.middlewarefilepath = item.middlewarefilepath || item.filepath || '';
           this.middlewareObject.uploadFileName = item.uploadFileName || item.filename || '';
           this.middlewareObject.filename = this.middlewareObject.uploadFileName;
           this.middFile = null;
           this.middlewareObjecterror = {};
         },
         hasExistingMiddlewareFile() {
           const existing = this.middlewareObject?.middlewarefile;
           return Array.isArray(existing) ? existing.length > 0 : Boolean(existing);
         },
         async deletemiddlewarebox(item) {
           this.loading = true;
           try {
             await this.$store.dispatch('transactionMatching/deleteMiddleware', {
               id: item._id,
               pid: this.analysisDetails.pid,
               tmaid: this.analysisDetails._id,
               bpid: this.analysisDetails.bpid,
             });
             await this.getmiddlewarelist();
           } catch (err) {
             console.error('[TransactionMatchingConfigNew]: deletemiddlewarebox error', err);
           } finally {
             this.loading = false;
           }
         },
         storeTotal(len) {
           this.total = len;	
           this.pagecount = Math.ceil(this.total / this.perpage);
         },
         storeTotalnew(len) {
           this.totalnew = len;
           this.pagecountnew = Math.ceil(this.totalnew / this.perpagenew);
         },
         storeTotalnewmatchnew(len) {
           this.totalnewmatchnew = len;
           this.pagecountnewmatchnew = Math.ceil(this.totalnewmatchnew / this.perpagenewmatchnew);
         },
         storeTotalfifo(len) {
           this.totalfifo = len;
           this.pagecountfifo = Math.ceil(this.totalfifo / this.perpagefifo);
         },
         pageItemsfifo() {
           this.storeTotalfifo((this.fifoannexData || []).length)
           return this.fifoannexData.slice((this.pagefifo - 1) * this.perpagefifo, (this.pagefifo) * this.perpagefifo);
         },
         tableOneItem(){
           return this.tableOne;
         },
         tableTwoItem(){
           return this.tableTwo;
         },
         tableOneItemtwofile(){
           return this.tableOnetwofile;
         },
         tableTwoItemtwofile(){
           return this.tableTwotwofile;
         },
         table1Totaltwofile(len) {
           this.table1totaltwofile = len;
           this.table1pagecounttwofile = Math.ceil(this.table1totaltwofile / this.table1perpagtwofile);
         },
         table2Totaltwofile(len) {
           this.table2totaltwofile = len;
           this.table2pagecounttwofile = Math.ceil(this.table2totaltwofile / this.table2perpagtwofile);
         },
         table1Total(len) {
           this.table1total = len;
           this.table1pagecount = Math.ceil(this.table1total / this.table1perpag);
         },
         table2Total(len) {
           this.table2total = len;
           this.table2pagecount = Math.ceil(this.table2total / this.table2perpag);
         },
         gettabledata(d) {
     
           this.ailoadingimage = true;
           let combokey = this.newannexerCombodata[d]?.comboCode;
           const requestData = [
             {
               collection: this.selectedCollectionAll.collection1,
             },
             {
               collection: this.selectedCollectionAll.collection2,
             }
           ];
           this.stagefiltertable1 = [];
           this.stagefiltertable2 = [];
     
           // Function to make API requests
           const fetchTableData = (data, tableIndex) => {
             return this.axios
               .post(`/v2/financialclose/analysis/getdatabycombocode/${combokey}`, {
                 data,
                 filter: { limit: 50, page: 1 }
               })
               .then((dt) => {
                 if (dt.data.status === "success") {
                   const tableData = dt.data.data[0].data;
                   const totalCount = dt.data.data[0].totalCount[0]?.count;
                   
                   // Set data for the tables dynamically based on tableIndex
                   if (tableIndex === 1) {
                     this.tableOne = tableData;
                     this.tableOneCopy = [...this.tableOne];
                     this.table1Total(totalCount);
                     this.table1collection = { collection : this.selectedCollectionAll.collection1, key : combokey};
                   } else {
                     this.tableTwo = tableData;
                     this.tableTwoCopy = [...this.tableTwo];
                     this.table2Total(totalCount);
                     this.table2collection = { collection : this.selectedCollectionAll.collection2, key : combokey};
                   }
                 }
               })
               .catch((err) => {
                 this.$store.commit("sbError", err.message || err || "Unknown error!");
                 console.log(err);
               });
           };
     
           // Make both requests concurrently using Promise.all
           Promise.all([
             fetchTableData(requestData[0], 1),
             fetchTableData(requestData[1], 2)
           ])
           .finally(() => {
             // Set loading to false after both requests are finished
             this.ailoadingimage = false;
           });
         },
     
         pageItemsNew() {
           // this.storeTotalnew((this.newAnnexersdata || []).length)
           // return this.newAnnexersdata.slice((this.pagenew - 1) * this.perpagenew, (this.pagenew) * this.perpagenew);
           return this.newAnnexersdata;
         },
         pageItems() {
           return this.currentannexuredata;
         },
         setupAI(){
           this.setupAIdilog = true;
           this.setupaiObj = {};
           this.initializeSetupAiObj();
           // this.setupAIdilogloading = true;
         },
         resetAI(){
           this.copycolumns = Object.values(this.columns);
         },
         MatchedAIitem(item){
           let mathedIds = item.map((item) => item._id)
           let summarycollection = this.collectionnames["Summary"];
           let ai_collection = this.collectionnames["AI_Matches"];
           let allanexures = {
             "a": this.collectionnames["Table 1 Matched"],
             "b": this.collectionnames["Table 2 Matched"],
             "c": this.collectionnames["Table 1 Unmatched"],
             "d": this.collectionnames["Table 2 Unmatched"],
           };
           let filter = {
           'ids': mathedIds,
           'allanexures': allanexures,
           'summarycollection': summarycollection,
           'aicollection':ai_collection,
           };
           let success = false
           this.loading = true;
           this.axios
             .post("/v2/financialclose/analysis/updateaitransactionmatching", {
               filter
             })
             .then((ele) => {
               if (ele.data.status === "success") {
                 success = true
                 this.$store.commit("sbSuccess", "Matched Items Added Successfully ");
               } else {
                 throw new Error("Error Matched Items")
               }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               if (success) {
                 this.generateAnnexurelistAI(this.selectedannexure,this.verificationlist[0]?._id)
               }
               this.loading = false;
             });
         },
         startRunAI(item){
           this.setupAIdilogloading = true;
           let data = {
             isAI: true,
             isittm: true,
             tmaid: this.analysisDetails._id,
             toprocess: this.analysisDetails.to_process,
             ai_setup:item,
             ...this.botAccessFields(),
           }
           this.axios
             .post("/v2/bot/analysis/startrun/" + this.botdetails._id, {
               pid: data.pid,
               data
             })
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.$store.commit("sbSuccess", "Run AI Started");
                 this.setupAIdilogloading = false;
                 this.executionbtnAI = true;
                 this.loadBotAi();
                 let aiCollection = '';
                 if (this.tabletype === 'AI') {
                 aiCollection = this.collectionNameList.find((x) => x.collection?.includes('annexure_AI'));
                 let selectedverification = this.verificationlist[0] || {};
                 if (aiCollection) {
                  this.generateAnnexurelistAI(aiCollection.collection,selectedverification._id);
                 } else {
                   if (this.analysisDetails.filecount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
                   this.currentannexuredata = [];
                 } else {
                   this.newAnnexersdata = [];
                 }
                 }
                 }
               } else throw new Error("Error starting AI run");
             })
             .catch((err) => {
               this.$store.commit("sbError", err?.response?.data?.message || err?.message || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.setupAIdilog = false;
               this.setupAIdilogloading = false;
             });
         },
         startRunAIExecution(count) {
           this.ailoadingimage = true;
           let selectedverification = this.verificationlist[0] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           this.comboAnnexer = rundata.annexure || [];
           this.collectionNameList = [];
             this.collectionNameList = this.comboAnnexer.filter(item => {
             return item && item.collection ? item.collection.includes("annexure_AIMatched") : false;
             });
           if (this.collectionNameList.length > 0) {
             const filter = {
               limit: 50,
               page:1,
             };
             this.axios.post("/v2/bot/analysis/readannexure/" + this.botdetails._id + "/c/" + this.collectionNameList[0].collection + "/v/" + this.verificationlist[0]._id,{filter})
               .then((ele) => {
                 if (ele.data.status === "success") {
                   this.newAnnexersdata = [];
                   this.currentannexuredata = [];
                   if (count === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
                     this.aiexecutionResult = ele.data.data;
                     this.currentannexuredata = ele.data.data;
                     this.currentannexuredata = ele.data.data[0].data;
                     this.storeTotal(ele.data.data[0].totalCount[0]?.count);
                   } else {
                     this.aiexecutionResult = ele.data.data;
                     this.newAnnexersdata = ele.data.data[0].data;
                     this.storeTotalnew(ele.data.data[0].totalCount[0]?.count);
                   }
                   
                 } else throw new Error("Error starting run");
               })
               .catch((err) => {
                 this.$store.commit("sbError", err.message || err || "Unknown error!");
                 console.log(err);
               })
               .finally(() => {
                 this.ailoadingimage = false;
               });
           } else {
             this.$store.commit("sbError", "AI Result has not yet been run");
             this.ailoadingimage = false;
           }
     
         },
         toggleSelectAll(checked){
           this.selectallfifo = checked;
           if (this.selectallfifo) {
             this.selectFifo = this.pageItemsfifo().map((item, index) => index); 
           } else {
             this.selectFifo = []; 
           }
           
         },
         startRunFifo() {
           this.loading = true;
           let data = {
             isFIFO: true,
             isittm: true,
             tmaid: this.analysisDetails._id,
             toprocess: this.analysisDetails.to_process,
             ...this.botAccessFields(),
           }
           this.axios
             .post("/v2/bot/analysis/startrun/" + this.botdetails._id, {
               pid: data.pid,
               data
             })
             .then((ele) => {
               if (ele.data.status === "success") {
                 this.$store.commit("sbSuccess", "Run FIFO Started");
                 this.executionbtn = true;
                 this.loadBot();
               } else throw new Error("Error starting fifo run");
             })
             .catch((err) => {
               this.$store.commit("sbError", err?.response?.data?.message || err?.message || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.loading = false;
             });
         },
         startRunFifoExecution() {
           let selectedverification = this.verificationlist[0] || {};
           let rundata = this.rundetails[selectedverification._id] || {};
           this.comboAnnexer = rundata.annexure || [];
           this.collectionNameList = this.comboAnnexer.filter(item => item.collection.includes("annexure_FIFO"));
     
           if (this.collectionNameList.length > 0) {
             this.axios.post("/v2/bot/analysis/readannexure/" + this.botdetails._id + "/c/" + this.collectionNameList[0].collection + "/v/" + this.verificationlist[0]._id)
               .then((ele) => {
                 if (ele.data.status === "success") {
                   this.fifoexecutionResult = ele.data.data;
                   this.runfifoppop = true;
                   this.executionbtn = false;
                 } else throw new Error("Error starting run");
               })
               .catch((err) => {
                 this.$store.commit("sbError", err.message || err || "Unknown error!");
                 console.log(err);
               })
               .finally(() => {
                 this.loading = false;
               });
           } else {
             this.$store.commit("sbError", "FIFO Analysis has not yet been run");
           }
     
         },

         handleReorderFilters({ from, to }) {
           const draggedItem = this.formatedData[from];
           this.formatedData.splice(from, 1);
           this.formatedData.splice(to, 0, draggedItem);
           // Re-assign indexno so S.No reflects the new visual order.
           this.formatedData.forEach((f, i) => { f.indexno = i; });
           this.saveNewOrder(this.formatedData, 'filter');
         },
         handleReorderRules({ from, to }) {
           // Build sorted view (same order as sortedRulesData in the child)
           const sorted = [...this.rulesdata]
             .sort((a, b) => Number(a.number) - Number(b.number));
           // Reorder within the sorted space
           const [draggedItem] = sorted.splice(from, 1);
           sorted.splice(to, 0, draggedItem);
           // Renumber to match new visual positions and replace rulesdata
           sorted.forEach((item, idx) => {
             this.$set(item, 'number', String(idx + 1));
           });
           this.rulesdata = sorted;
           this.saveNewOrder(this.rulesdata, 'rules');
         },
         saveNewOrder(newOrder, type) {
           if (type === 'filter') {
             this.filterList = newOrder;
           } else {
             this.rulesdata = newOrder;
           }
         },
         getcomboList(d) {
           if (d === null || d === undefined) {
             return;
           }
           const key = Object.keys(this.newannexerCombodata[d])[0];
           const value = Object.values(this.newannexerCombodata[d])[0];
           this.selectedannexure = value;
           let data = {
             collection: value
           }
           this.ailoadingimage = true;
           this.GlobaleCollection = {collection:value,id:null,key:key}
           
           this.querydetails = key;
           this.axios
             .post("/v2/financialclose/analysis/getdatabycombocode/" + key, {
               data: data,
               filter:{limit: 50,page:1}
             })
             .then((dt) => {
               if (dt.data.status === "success") {
                 this.newpageinationEmpty();
                 // this.newAnnexersdata = dt.data.data;
                 this.newAnnexersdata = dt.data.data[0].data;
                 this.storeTotalnew(dt.data.data[0].totalCount[0]?.count);
                 this.originalAnnexersdata = [...this.newAnnexersdata];
               } else throw new Error(dt.data.message || "Error Get Combo Code");
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
     
         },
         newpageinationEmpty() {
           this.pagenew = 1;
           this.pagecountnew = 1;
           this.perpagenew = 50;
           this.totalnew = 0;
         },
         pageinationEmpty() {
           this.page = 1;
           this.pagecount = 1;
           this.perpage = 50;
           this.total = 0;
         },
         table1pageinationEmpty() {
           this.table1 = 1;
           this.table1pagecount = 1;
           this.table1perpag = 50;
           this.table1total = 0; 
         },
         table2pageinationEmpty() {
           this.table2 = 1;
           this.table2pagecount = 1;
           this.table2perpag = 50;
           this.table2total = 0; 
         },
         createEmptyObject(templateObject) {
           return Object.keys(templateObject).reduce((acc, key) => {
             acc[key] = '';
             return acc;
           }, {});
         },
         // parsedAmount - MOVED to TransactionMatchingReconciliation.vue component
         table1global(value,collection){
           if (value) {
             this.table1globalInfo = {};
             this.globalfilterObjecttable1 = {};
             this.globalfilterObjecterrortable1 = {};
             this.table1globalCollections = collection;
             this.table1selectColumn = value;
             this.table1typedata = '';
     
             if (this.tableOne && this.tableOne.length > 0) {
               const table1data = this.tableOne[0][value];
               if (typeof table1data === 'number') {
                 this.table1typedata = "Number";
               } else if (typeof table1data === 'string') {
                 this.table1typedata = this.isValidDatetrue(table1data) ? "Date" : "Text";
               } else {
                 this.table1typedata = "Text";
               }
             } else {
               this.table1typedata = "Text";
             }
             this.globalfilter1 = true;
     
             this.table1globalInfo = {collection:collection.collection,key:collection.key,selectecolumn:value,dataoftype:this.table1typedata}
           } else {
              // this.$store.commit("sbError", "Nothing Selected Header");
           }
           
           
         },
         table1globaltwofile(value,collection){
           this.table1twofileglobalInfo = {};
           this.globaltwofilter1 = {},
           this.globaltwofilter1error = {},
           this.selectedtable1collection = collection;
           this.table1twoselectColumn = value;
           this.datatypetable1 = '';
           if (this.tableOnetwofile && this.tableOnetwofile.length > 0) {
             const table1data = this.tableOnetwofile[0][value];
             if (typeof table1data === 'number') {
               this.datatypetable1 = "Number";
             } else if (typeof table1data === 'string') {
               this.datatypetable1 = this.isValidDatetrue(table1data) ? "Date" : "Text";
             } else {
               this.datatypetable1 = "Text";
             }
           } else {
             this.datatypetable1 = "Text";
           }
           this.globalfilter1twofile = true;
           this.table1twofileglobalInfo = {collection:collection.collection,id:collection.id,selectecolumn:value,dataoftype:this.datatypetable1};
           
         },
         table2globaltwofile(value,collection){
           this.table2twofileglobalInfo = {};
           this.globaltwofilter2 = {},
           this.globaltwofilter2error = {},
           this.selectedtable2collection = collection.collection;
           this.table2twoselectColumn = value;
           this.datatypetable2 = '';
           if (this.tableTwotwofile && this.tableTwotwofile.length > 0) {
             const table2data = this.tableTwotwofile[0][value];
             if (typeof table2data === 'number') {
               this.datatypetable2 = "Number";
             } else if (typeof table2data === 'string') {
               this.datatypetable2 = this.isValidDatetrue(table2data) ? "Date" : "Text";
             } else {
               this.datatypetable2 = "Text";
             }
           } else {
             this.datatypetable2 = "Text";
           }
           this.globalfilter2twofile = true;
           this.table2twofileglobalInfo = {collection:collection.collection,id:collection.id,selectecolumn:value,dataoftype:this.datatypetable2};
           
         },
     
         table2global(value,collection){
           if (value) {
             this.table2globalInfo = {};
             this.globalfilterObjecttable2 = {};
             this.globalfilterObjecterrortable2 = {};
             this.table2globalCollections = collection;
             this.table2selectColumn = value;
             this.table2typedata = '';
     
             if (this.tableTwo && this.tableTwo.length > 0) {
               const table2data = this.tableTwo[0][value];
               if (typeof table2data === 'number') {
                 this.table2typedata = "Number";
               } else if (typeof table2data === 'string') {
                 this.table2typedata = this.isValidDatetrue(table2data) ? "Date" : "Text";
               } else {
                 this.table2typedata = "Text";
               }
             } else {
               this.table2typedata = "Text";
             }
             this.globalfilter2 = true;
     
             this.table2globalInfo = {collection:collection.collection,key:collection.key,selectecolumn:value,dataoftype:this.table2typedata}
             
           } else {
             // this.$store.commit("sbError", "Nothing Selected Header");
     
           }
           
           
         },
     
         globalfilterpopNew(value){
           if (value) {
           this.oneormorefile = true;
           const key = Object.keys(this.newannexerCombodata[this.selectedCombo])[0];	
           const values = Object.values(this.newannexerCombodata[this.selectedCombo])[0];	
           this.selectFilter = value;
           this.globalfilterObject = {};
           this.globalfilterObjecterror = {};
           this.selectcollection = values;
           this.selectkey = key;
           if (this.newAnnexersdata && this.newAnnexersdata.length > 0) {
             const data = this.newAnnexersdata[0][value];
             if (typeof data === 'number') {
               this.typeOffilter = "Number";
             } else if (typeof data === 'string') {
               this.typeOffilter = this.isValidDatetrue(data) ? "Date" : "Text";
             } else {
               this.typeOffilter = "Text";
             }
           } else {
             this.typeOffilter = "Text";
           }
           this.selectfilterdialog = true;
           } else {
             // this.$store.commit("sbError", "Nothing Selected Header");
           }
           
         },
         manualGlobal(value,collection){
           this.formateddataforfilterNew
           if (value) {
             this.manualglobalInfo = {};
             this.globalfilterobjectmanual = {};
             this.globalfilterobjectmanualerror = {};
             this.manualCollection = collection;
             this.manualselectColumn = value;
             this.manualtypedata = '';
             const manualdatatype = this.formateddataforfilterNew[0][value];
             if (typeof manualdatatype === 'number') {	
               this.manualtypedata = "Number";
               this.globalfiltermanual = true;
               } else if (typeof manualdatatype === 'string') {
               if (this.isValidDatetrue(manualdatatype)) {
                 this.manualtypedata = "Date";
                 this.globalfiltermanual = true;
               } else {
                 this.manualtypedata = "Text";
                 this.globalfiltermanual = true;
               }
             }
     
             this.manualglobalInfo = {collection:collection.collection,key:collection.key,selectecolumn:value,dataoftype:this.manualtypedata}
             
           } else {
             // this.$store.commit("sbError", "Nothing Selected Header Manual");
     
           }
           
         },
         manualGlobaltwofile(value,collection){
           if (value) {
           this.manualglobalInfotwofile = {},
           this.globalfilterobjectmanualtwofile = {},
           this.globalfilterobjectmanualerrortwofile = {},
           this.manualCollectiontwofile = collection,
           this.manualselectColumntwofile = value,
           this.manualtypedatatwofile = '';
     
           const manualdatatype = this.formateddataforfilter[0][value];
             if (typeof manualdatatype === 'number') {	
               this.manualtypedatatwofile = "Number";
               this.globalfiltertwofilemanual = true;
               } else if (typeof manualdatatype === 'string') {
               if (this.isValidDatetrue(manualdatatype)) {
                 this.manualtypedatatwofile = "Date";
                 this.globalfiltertwofilemanual = true;
               } else {
                 this.manualtypedatatwofile = "Text";
                 this.globalfiltertwofilemanual = true;
               }
             }
     
           
     
           this.manualglobalInfotwofile =  {collection:collection.collection,id:collection.id,selectecolumn:value,dataoftype:this.manualtypedatatwofile,botid:collection.botid};
           } else {
             // this.$store.commit("sbError", "Nothing Selected Header Manual twofile");
           }
           
         },
     
         async runmaunalfiltertwofile(filterlist,manualinfotwofile){
           if (!this.validateFiltermanualtwofile(filterlist,manualinfotwofile)) {
           return;
         }
         const filtermenualtwofile = this.createFiltertable(filterlist,manualinfotwofile.dataoftype,manualinfotwofile.selectecolumn);
         this.manualglobaltwofileArray.push(filtermenualtwofile);
     
           const filter = {
             data: this.manualglobaltwofileArray,
             limit: 50,
             page: 1,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${manualinfotwofile.collection}`;
           try {
             const response = await this.axios.post(Apipath, { filter } );
             if (response.data.status === 'success') {
               this.manualglobaltwofileList.push(filtermenualtwofile);
               this.formateddataforfilter= response.data.data[0].data;
               this.storeTotalnewmatchnew(response.data.data[0]?.totalCount[0]?.count);
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globalfilterobjectmanualtwofile = {},
             this.globalfilterobjectmanualerrortwofile = {};
     
           }
     
         },
         async runmaunalfilter(filterlist,manualinfo){
         this.pagenewmatchnew = 1;
         if (!this.validateFiltermanual(filterlist,manualinfo)) {
           return;
         }
         
         const filtermenual = this.createFiltertable(filterlist,manualinfo.dataoftype,manualinfo.selectecolumn);
         this.manualglobalArray.push(filtermenual);
     
           const query = manualinfo.key ? { "Combo-Code": manualinfo.key } : null;
           const filter = {
             data: this.manualglobalArray,
             limit: 50,
             page: 1,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${manualinfo.collection}`;
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query	} : { filter } );
             if (response.data.status === 'success') {
               this.manualglobalList.push(filtermenual);
               this.formateddataforfilterNew = response.data.data[0].data;
               this.storeTotalnewmatchnew(response.data.data[0]?.totalCount[0]?.count);
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globalfilterobjectmanual = {};
             this.globalfilterobjectmanualerror = {};
     
           }
         
         },
         
     
         globalfilterpopMatch(value,collection,recedata,match,matchtype){
           this.mathcFilter = match;
           
           this.matchTypeFilter = matchtype;
           if (value) {
             this.selectFilter = value;
             this.globalfilterObject = {};
             this.globalfilterObjecterror = {};
             this.selectcollection = collection;
             if (recedata && recedata.length > 0) {
               const data = recedata[0][value];
               if (typeof data === 'number') {
                 this.typeOffilter = "Number";
               } else if (typeof data === 'string') {
                 this.typeOffilter = this.isValidDatetrue(data) ? "Date" : "Text";
               } else {
                 this.typeOffilter = "Text";
               }
             } else {
               this.typeOffilter = "Text";
             }
             this.selectfilterdialog = true;
           } else {
             // this.$store.commit("sbError", "Nothing Selected");
           }
     
           
         },
         // globalfilterpopMatch(value,collection){
           
         // 	if (value) {
         // 		this.selectFilter = value;
         // 		this.globalfilterObject = {};
         // 		this.globalfilterObjecterror = {};
         // 		this.selectcollection = collection;
         // 		const data = this.currentannexuredata[0][value];
         // 		if (typeof data === 'number') {
         // 			this.typeOffilter = "Number";
         // 			this.selectfilterdialog = true;
         // 		} else if (typeof data === 'string') {
         // 			if (this.isValidDatetrue(data)) {
         // 				this.typeOffilter = "Date";
         // 				this.selectfilterdialog = true;
         // 			} else {
         // 				this.typeOffilter = "Text";
         // 				this.selectfilterdialog = true;
         // 			}
         // 		}
         // 	} else {
         // 		// this.$store.commit("sbError", "Nothing Selected");
         // 	}
           
         // },
         globalfilterpop(value, collection) {
           if (value) {
             this.selectFilter = value;
             this.globalfilterObject = {};
             this.globalfilterObjecterror = {};
             this.selectcollection = collection;
             if (this.currentannexuredata && this.currentannexuredata.length > 0) {
               const data = this.currentannexuredata[0][value];
               if (typeof data === 'number') {
                 this.typeOffilter = "Number";
               } else if (typeof data === 'string') {
                 this.typeOffilter = this.isValidDatetrue(data) ? "Date" : "Text";
               } else {
                 this.typeOffilter = "Text";
               }
             } else {
               this.typeOffilter = "Text";
             }
             this.selectfilterdialog = true;
           } else {
             // this.$store.commit("sbError", "Nothing Selected");
           }
         },
         createFilterObject(ope, range, range1, range2, col, type) {
           if (type === 'Number') {
             return {
               operation: ope,
               value: range,
               value1: range1,
               value2: range2,
               column: col,
               type: type
             };
           } else if (type === 'Date') {
             return {
               operation: ope,
               value: range,
               value1: this.convertToDateTimeString(range1),
               value2: this.convertToDateTimeString(range2),
               column: col,
               type: type
             };
           } else {
             return {
               operation: ope,
               value1: range,
               column: col,
               type: type
             };
           }
     
         },
         validateFilter(operation, range, typeOffilter, range1, range2) {
           var isValid = true;
           this.globalfilterObjecterror = {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           };
           if (typeOffilter === 'Text') {
             if (!operation || operation.length === 0 || operation == null) {
               this.globalfilterObjecterror.operation = 'Operation is required';
               isValid = false;
             }
             if (!range || range == null) {
               this.globalfilterObjecterror.range = 'Range is required';
               isValid = false;
             }
           }
           if (typeOffilter === 'Number') {
     
             if (!operation || operation.length === 0 || operation == null) {
               this.globalfilterObjecterror.operation = 'Operation is required';
               isValid = false;
             }
             if (!(typeof range1 === 'number')) {
               this.globalfilterObjecterror.range1 = 'Range 1 is required';
               isValid = false;
             }
             if (this.globalfilterObject.operation === 'Between' && !(typeof range1 === 'number')) {
               this.globalfilterObjecterror.range2 = 'Range 2 is required for "Between" operation';
               isValid = false;
             }
             if (this.globalfilterObject.operation === 'Between' && (!(range1 < range2))) {
               this.globalfilterObjecterror.range2 = 'Range 1 is Greater than Range 2';
               isValid = false;
             }
     
           }
           if (typeOffilter === 'Date') {
             if (!operation || operation.length === 0 || operation == null) {
               this.globalfilterObjecterror.operation = 'Operation is required';
               isValid = false;
             }
     
     
             if (!range1) {
               this.globalfilterObjecterror.range1 = 'Range 1 is required';
               isValid = false;
             }
             if (this.globalfilterObject.operation === 'Between' && (!range2)) {
               this.globalfilterObjecterror.range2 = 'Range 2 is required for "Between" operation';
               isValid = false;
             }
           }
           return isValid;
         },
         createFiltertable(object,type,colum){
           if (type === 'Number') {
             return {
               operation: object.operation,
               value: object.range,
               value1: object.range1,
               value2: object.range2,
               column: colum,
               type: type
             };
           } else if (type === 'Date') {
             return {
               operation: object.operation,
               value: object.range,
               value1: this.convertToDateTimeString(object.range1),
               value2: this.convertToDateTimeString(object.range2),
               column: colum,
               type: type
             };
           } else {
             return {
               operation: object.operation,
               value1: object.range,
               column: colum,
               type: type
             };
           }
           
           
         },
    validateFiltertable1twofilter(filterlist, table1info) {
    var isValid = true;

    // Initialize error object
    this.globaltwofilter1error = {
        operation: '',
        range: '',
        range1: '',
        range2: ''
    };

    // Validate filterlist input
    if (!filterlist || typeof filterlist !== 'object') {
        console.error('Invalid filterlist:', filterlist);
        return false;
    }

    // Validation for Text
    if (table1info.dataoftype === 'Text') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this.globaltwofilter1error.operation = 'Operation is required';
            isValid = false;
        }
        if (!filterlist?.range) {
            this.globaltwofilter1error.range = 'Range is required';
            isValid = false;
        }
    }

    // Validation for Number
    if (table1info.dataoftype === 'Number') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this.globaltwofilter1error.operation = 'Operation is required';
            isValid = false;
        }
        if (typeof filterlist.range1 !== 'number') {
            this.globaltwofilter1error.range1 = 'Range 1 is required';
            isValid = false;
        }
        if (filterlist.operation === 'Between') {
            if (typeof filterlist.range2 !== 'number') {
                this.globaltwofilter1error.range2 = 'Range 2 is required for "Between" operation';
                isValid = false;
            }
            if (!(filterlist.range1 < filterlist.range2)) {
                this.globaltwofilter1error.range2 = 'Range 1 is Greater than Range 2';
                isValid = false;
            }
        }
    }

    // Validation for Date
    if (table1info.dataoftype === 'Date') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this.globaltwofilter1error.operation = 'Operation is required';
            isValid = false;
        }
        if (!filterlist?.range1) {
            this.globaltwofilter1error.range1 = 'Range 1 is required';
            isValid = false;
        }
        if (filterlist.operation === 'Between' && !filterlist?.range2) {
            this.globaltwofilter1error.range2 = 'Range 2 is required for "Between" operation';
            isValid = false;
        }
    }

    return isValid;
},
         validateFiltertab2e1twofilter(filterlist, table2info) {
    var isValid = true;

    // Initialize error object
    this.globaltwofilter2error = {
        operation: '',
        range: '',
        range1: '',
        range2: ''
    };

    // Validate filterlist input
    if (!filterlist || typeof filterlist !== 'object') {
        console.error('Invalid filterlist:', filterlist);
        return false;
    }

    // Validation for Text
    if (table2info.dataoftype === 'Text') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this.globaltwofilter2error.operation = 'Operation is required';
            isValid = false;
        }
        if (!filterlist?.range) {
            this.globaltwofilter2error.range = 'Range is required';
            isValid = false;
        }
    }

    // Validation for Number
    if (table2info.dataoftype === 'Number') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this.globaltwofilter2error.operation = 'Operation is required';
            isValid = false;
        }
        if (typeof filterlist.range1 !== 'number') {
            this.globaltwofilter2error.range1 = 'Range 1 is required';
            isValid = false;
        }
        if (filterlist.operation === 'Between') {
            if (typeof filterlist.range2 !== 'number') {
                this.globaltwofilter2error.range2 = 'Range 2 is required for "Between" operation';
                isValid = false;
            }
            if (!(filterlist.range1 < filterlist.range2)) {
                this.globaltwofilter2error.range2 = 'Range 1 is Greater than Range 2';
                isValid = false;
            }
        }
    }

    // Validation for Date
    if (table2info.dataoftype === 'Date') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this.globaltwofilter2error.operation = 'Operation is required';
            isValid = false;
        }
        if (!filterlist?.range1) {
            this.globaltwofilter2error.range1 = 'Range 1 is required';
            isValid = false;
        }
        if (filterlist.operation === 'Between' && !filterlist?.range2) {
            this.globaltwofilter2error.range2 = 'Range 2 is required for "Between" operation';
            isValid = false;
        }
    }

    return isValid;
},

validateFilterall(filterlist, tableInfo, errorObjectKey) {
    var isValid = true;

    // Initialize error object
    this[errorObjectKey] = {
        operation: '',
        range: '',
        range1: '',
        range2: ''
    };

    // Validate filterlist input
    if (!filterlist || typeof filterlist !== 'object') {
        console.error('Invalid filterlist:', filterlist);
        return false;
    }

    // Validate based on data type
    if (tableInfo.dataoftype === 'Text') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this[errorObjectKey].operation = 'Operation is required';
            isValid = false;
        }
        if (!filterlist?.range) {
            this[errorObjectKey].range = 'Range is required';
            isValid = false;
        }
    }

    if (tableInfo.dataoftype === 'Number') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this[errorObjectKey].operation = 'Operation is required';
            isValid = false;
        }
        if (typeof filterlist.range1 !== 'number') {
            this[errorObjectKey].range1 = 'Range 1 is required';
            isValid = false;
        }
        if (filterlist.operation === 'Between') {
            if (typeof filterlist.range2 !== 'number') {
                this[errorObjectKey].range2 = 'Range 2 is required for "Between" operation';
                isValid = false;
            }
            if (!(filterlist.range1 < filterlist.range2)) {
                this[errorObjectKey].range2 = 'Range 1 is Greater than Range 2';
                isValid = false;
            }
        }
    }

    if (tableInfo.dataoftype === 'Date') {
        if (!filterlist?.operation || filterlist.operation.length === 0) {
            this[errorObjectKey].operation = 'Operation is required';
            isValid = false;
        }
        if (!filterlist?.range1) {
            this[errorObjectKey].range1 = 'Range 1 is required';
            isValid = false;
        }
        if (filterlist.operation === 'Between' && !filterlist?.range2) {
            this[errorObjectKey].range2 = 'Range 2 is required for "Between" operation';
            isValid = false;
        }
    }

    return isValid;
},
validateFiltertable1(filterlist, table1info) {
    return this.validateFilterall(filterlist, table1info, 'globalfilterObjecterrortable1');
},

validateFiltertable2(filterlist, table2info) {
    return this.validateFilterall(filterlist, table2info, 'globalfilterObjecterrortable2');
},
         validateFiltermanual(filterlist,manualinfo){
           var isValid = true;
           this.globalfilterobjectmanualerror = {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           };
     
           if (manualinfo.dataoftype === 'Text') {
             if (!filterlist.operation || filterlist.operation.length === 0 || filterlist.operation == null) {
               this.globalfilterobjectmanualerror.operation = 'Operation is required';
               isValid = false;
             }
             if (!filterlist.range || filterlist.range == null) {
               this.globalfilterobjectmanualerror.range = 'Range is required';
               isValid = false;
             }
           }
           if (manualinfo.dataoftype === 'Number') {
     
             if (!filterlist.operation || filterlist.operation.length === 0 || filterlist.operation == null) {
               this.globalfilterobjectmanualerror.operation = 'Operation is required';
               isValid = false;
             }
             if (!(typeof filterlist.range1 === 'number')) {
               this.globalfilterobjectmanualerror.range1 = 'Range 1 is required';
               isValid = false;
             }
             if (filterlist.operation === 'Between' && !(typeof filterlist.range1 === 'number')) {
               this.globalfilterobjectmanualerror.range2 = 'Range 2 is required for "Between" operation';
               isValid = false;
             }
             if (filterlist.operation === 'Between' && (!(filterlist.range1 < filterlist.range2))) {
               this.globalfilterobjectmanualerror.range2 = 'Range 1 is Greater than Range 2';
               isValid = false;
             }
     
           }
           if (manualinfo.dataoftype === 'Date') {
             if (!filterlist.operation || filterlist.operation.length === 0 || filterlist.operation == null) {
               this.globalfilterobjectmanualerror.operation = 'Operation is required';
               isValid = false;
             }
     
     
             if (!filterlist.range1) {
               this.globalfilterobjectmanualerror.range1 = 'Range 1 is required';
               isValid = false;
             }
             if (filterlist.operation === 'Between' && (!filterlist.range2)) {
               this.globalfilterobjectmanualerror.range2 = 'Range 2 is required for "Between" operation';
               isValid = false;
             }
           }
           return isValid;
         },
         validateFiltermanualtwofile(filterlist,manualinfo){
           var isValid = true;
           this.globalfilterobjectmanualerrortwofile = {
             operation: '',
             range: '',
             range1: '',
             range2: ''
           };
           if (manualinfo.dataoftype === 'Text') {
             if (!filterlist.operation || filterlist.operation.length === 0 || filterlist.operation == null) {
               this.globalfilterobjectmanualerrortwofile.operation = 'Operation is required';
               isValid = false;
             }
             if (!filterlist.range || filterlist.range == null) {
               this.globalfilterobjectmanualerrortwofile.range = 'Range is required';
               isValid = false;
             }
           }
           if (manualinfo.dataoftype === 'Number') {
     
             if (!filterlist.operation || filterlist.operation.length === 0 || filterlist.operation == null) {
               this.globalfilterobjectmanualerrortwofile.operation = 'Operation is required';
               isValid = false;
             }
             if (!(typeof filterlist.range1 === 'number')) {
               this.globalfilterobjectmanualerrortwofile.range1 = 'Range 1 is required';
               isValid = false;
             }
             if (filterlist.operation === 'Between' && !(typeof filterlist.range1 === 'number')) {
               this.globalfilterobjectmanualerrortwofile.range2 = 'Range 2 is required for "Between" operation';
               isValid = false;
             }
             if (filterlist.operation === 'Between' && (!(filterlist.range1 < filterlist.range2))) {
               this.globalfilterobjectmanualerrortwofile.range2 = 'Range 1 is Greater than Range 2';
               isValid = false;
             }
     
           }
           if (manualinfo.dataoftype === 'Date') {
             if (!filterlist.operation || filterlist.operation.length === 0 || filterlist.operation == null) {
               this.globalfilterobjectmanualerrortwofile.operation = 'Operation is required';
               isValid = false;
             }
     
     
             if (!filterlist.range1) {
               this.globalfilterobjectmanualerrortwofile.range1 = 'Range 1 is required';
               isValid = false;
             }
             if (filterlist.operation === 'Between' && (!filterlist.range2)) {
               this.globalfilterobjectmanualerrortwofile.range2 = 'Range 2 is required for "Between" operation';
               isValid = false;
             }
           }
           return isValid;
         },
         async runtable1filtertwofile(filterlist, table1info){
     
           if (!this.validateFiltertable1twofilter(filterlist, table1info)) {
             return;
           }
           const filterObj1 = this.createFiltertable(filterlist, table1info.dataoftype, table1info.selectecolumn);
           this.table1twoglobalArray.push(filterObj1);
     
           const filter = {
             data: this.table1twoglobalArray,
             limit: 50,
             page: 1,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${table1info.collection}`;
     
           try {
             const response = await this.axios.post(Apipath,{ filter } );
             if (response.data.status === 'success') {
               this.table1twoglobalList.push(filterObj1);
               this.tableOnetwofile = response.data.data[0]?.data;
               this.table1Totaltwofile(response.data.data[0]?.totalCount[0]?.count);
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globaltwofilter1 = {};
             this.globaltwofilter1error = {};
             this.table1twofile = 1;
           }
     
           // table1twoglobalList:[],
           // table1twoglobalArray:[],
           
         },
         async runtable2filtertwofile(filterlist, table2info){
     
           if (!this.validateFiltertab2e1twofilter(filterlist, table2info)) {
             return;
           }
           const filterObj2 = this.createFiltertable(filterlist, table2info.dataoftype, table2info.selectecolumn);
           this.table2twoglobalArray.push(filterObj2);
           const filter = {
             data: this.table2twoglobalArray,
             limit: 50,
             page: 1,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${table2info.collection}`;
     
           try {
             const response = await this.axios.post(Apipath,{ filter } );
             if (response.data.status === 'success') {
               this.table2twoglobalList.push(filterObj2);
               this.tableTwotwofile = response.data.data[0]?.data,
               this.table2Totaltwofile(response.data.data[0]?.totalCount[0]?.count);	
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globaltwofilter2 = {};
             this.globaltwofilter2error = {};
             this.table2twofile = 1;
           }
           
         },
         async runtable1filter(filterlist, table1info) {
           this.table1 = 1;
     
           if (!this.validateFiltertable1(filterlist, table1info)) {
             return;
           }
           const filterObj1 = this.createFiltertable(filterlist, table1info.dataoftype, table1info.selectecolumn);
           this.table1globalArray.push(filterObj1);
           const query = table1info.key ? { "Combo-Code": table1info.key } : null;
           const filter = {
             data: this.table1globalArray,
             limit: 50,
             page: 1,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${table1info.collection}`;
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query	} : { filter } );
             if (response.data.status === 'success') {
               this.table1globalList.push(filterObj1);
               this.tableOne = response.data.data[0]?.data;
               this.table1Total(response.data.data[0]?.totalCount[0]?.count)
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globalfilterObjecttable1 = {};
             this.globalfilterObjecterrortable1 = {};
     
           }     
     
         },
         async runtable2filter(filterlist,table2info){
         this.table1 = 1;
         if (!this.validateFiltertable2(filterlist,table2info)) {
           return;
         }
         const filterObj2 = this.createFiltertable(filterlist,table2info.dataoftype,table2info.selectecolumn);
         this.table2globalArray.push(filterObj2);
     
           const query = table2info.key ? { "Combo-Code": table2info.key } : null;
           const filter = {
             data: this.table2globalArray,
             limit: 50,
             page: 1,
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${table2info.collection}`;
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query	} : { filter } );
             if (response.data.status === 'success') {
               this.table2globalList.push(filterObj2);
               this.tableTwo = response.data.data[0]?.data;
               this.table2Total(response.data.data[0]?.totalCount[0]?.count)
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.globalfilterObjecttable2 = {};
             this.globalfilterObjecterrortable2 = {};
     
           }
         
         },
         clearFilterall(collection){
         this.selectedcollection = collection.collection;
           this.selectedannexure = collection.collection;
           this.ailoadingimage = true;
           const filter = { data: [], limit: 50, page:1 };
           this.GlobaleCollection = {collection:collection.collection,id:collection.id,page:1,key:null,}
           this.axios.post("/v2/bot/analysis/readannexure/" + this.analysisDetails.bpid + "/c/" + collection.collection + "/v/" + collection.id,{filter})
             .then((dt) => {
               if (dt.data.status === "success") {
               if (this.analysisDetails.filecount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
                   this.currentannexuredata = dt.data.data[0].data;
                   this.storeTotal(dt.data.data[0].totalCount[0]?.count);
                 } else {
                   this.newAnnexersdata = dt.data.data[0].data;
                   this.storeTotalnew(dt.data.data[0].totalCount[0]?.count);
                 }
               } else { 
                 this.newAnnexersdata = [];
                 this.currentannexuredata = [];
                 throw new Error(dt.data.message || "Error Get AI") ; 
               }
             })
             .catch((err) => {
               this.$store.commit("sbError", err.message || err || "Unknown error!");
               console.log(err);
             })
             .finally(() => {
               this.ailoadingimage = false;
             });
          
         },
         async clearFiltertwofile(collection,table){
         const filter = { data: [], limit: 50, page:1 };
         const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection.collection}`;
         
         try {
           const response = await this.axios.post(Apipath,{ filter });
           if (table === 'table1') {
             this.table1twoglobalList = [];
             this.table1twoglobalArray = [];
             this.tableOnetwofile = response.data.data[0]?.data;
             this.table1Totaltwofile(response.data.data[0]?.totalCount[0]?.count);
             this.table1twofile = 1;
           } else{
             this.table2twoglobalList = [];
             this.table2twoglobalArray = [];
             this.tableTwotwofile = response.data.data[0]?.data,
             this.table2Totaltwofile(response.data.data[0]?.totalCount[0]?.count);
             this.table2twofile = 1;
           }
           }
         catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfilter2twofileloading = false;
           this.globalfilter2twofile = false;
           this.globalfilter1twofileloading = false;
           this.globalfilter1twofile = false;
         }
           
         },
         
         async clearFilter(collection,table){
         const query = collection.key ? { "Combo-Code": collection.key } : null;
         const filter = { data: [], limit: 50, page:1 };
         const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection.collection}`;
         
         try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (table === 'table1') {
             this.table1globalArray = [];
             this.table1globalList = [];
             this.tableOne = response.data.data[0]?.data;
             this.table1Total(response.data.data[0]?.totalCount[0]?.count);
             this.table1 = 1;
           } else{
             this.table2globalArray = [];
             this.table2globalList = [];
             this.tableTwo = response.data.data[0]?.data;
             this.table2Total(response.data.data[0]?.totalCount[0]?.count);
             this.table2 = 1;
           }
           }
         catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfilter2loading = false;
           this.globalfilter2 = false;
           this.globalfilter1loading = false;
           this.globalfilter1 = false;
         }
           
         },
         async clearFilterManual(collection){
         this.globalfiltermanualloading = true;
         const query = collection.key ? { "Combo-Code": collection.key } : null;
         const filter = { data: [], limit: 50, page:1 };
         const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection.collection}`;
         
         try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
             this.formateddataforfilterNew = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0]?.totalCount[0]?.count);
             this.manualglobalArray = [];
             this.manualglobalList = [];
           }
           }
         catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfiltermanualloading = false;
           this.globalfiltermanual = false;
           this.pagenewmatchnew = 1;
         }
           
         },
         async clearFilterManualtwofile(collection){
         this.globalfiltermanualloading = true;
         const filter = { data: [], limit: 50, page:1 };
         const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection.collection}`;
         try {
           const response = await this.axios.post(Apipath,{ filter });
           if (response.data.status === 'success') {
             this.formateddataforfilter = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0]?.totalCount[0]?.count);
             this.manualglobaltwofileArray = [];
             this.manualglobaltwofileList = [];
           }
           }
         catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfiltermanualloading = false;
           this.globalfiltermanual = false;
           this.pagenewmatchnew = 1;
         }
           
         },
         async deletetable1(filterList,index,info){
           this.globalfilter1loading = true;
           filterList.splice(index, 1);
           const filterData = filterList.length > 0 ? filterList : [];	
           const query = info.key ? { "Combo-Code": info.key } : null;
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${info.collection}`;
           try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
     
             this.table1globalList = [...filterList];
             this.table1globalArray = [...filterList];
             this.tableOne = response.data.data[0]?.data;
             this.table1Total(response.data.data[0]?.totalCount[0]?.count)
             
           }
           } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfilter1loading = false;
         }
     
         },
         async deletetable2(filterList,index,info){
           this.globalfilter2loading = true;
           filterList.splice(index, 1);
           const filterData = filterList.length > 0 ? filterList : [];	
           const query = info.key ? { "Combo-Code": info.key } : null;
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${info.collection}`;
           try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
     
             this.tableTwo = response.data.data[0]?.data;
             this.table2Total(response.data.data[0]?.totalCount[0]?.count)
             this.table2globalList = [...filterList];
             this.table2globalArray = [...filterList];
             
           }
           } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfilter2loading = false;
         }
         },
         async deletetable1twofile(filterList,index,info){
     
           this.globalfilter1twofileloading = true;
           filterList.splice(index, 1);
           const filterData = filterList.length > 0 ? filterList : [];	
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${info.collection}`;
           try {
           const response = await this.axios.post(Apipath,{ filter });
           if (response.data.status === 'success') {
             this.table1twoglobalList = [...filterList];
             this.table1twoglobalArray = [...filterList];
             this.tableOnetwofile = response.data.data[0]?.data;
             this.table1Totaltwofile(response.data.data[0]?.totalCount[0]?.count)
             
           }
           } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfilter1twofileloading = false;
         }
           
         },
         async deletetable2twofile(filterList,index,info){
     
           this.globalfilter2twofileloading = true;
           filterList.splice(index, 1);
           const filterData = filterList.length > 0 ? filterList : [];	
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${info.collection}`;
           try {
           const response = await this.axios.post(Apipath,{ filter });
           if (response.data.status === 'success') {
             this.table2twoglobalList = [...filterList];
             this.table2twoglobalArray = [...filterList];
             this.tableTwotwofile = response.data.data[0]?.data;
             this.table2Totaltwofile(response.data.data[0]?.totalCount[0]?.count)
             
           }
           } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfilter2twofileloading = false;
         }
           
         },
         async deletemanual(filterList,index,info){
           this.globalfiltermanualloading = true;
           filterList.splice(index, 1);
           const filterData = filterList.length > 0 ? filterList : [];	
           const query = info.key ? { "Combo-Code": info.key } : null;
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${info.collection}`;
           try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
             this.manualglobalList = [...filterList];
             this.manualglobalArray = [...filterList];
             this.formateddataforfilterNew = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0]?.totalCount[0]?.count);
             
           }
           } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfiltermanualloading = false;
         }
         },
       async deletemanualtwofile(filterList,index,info){
           this.globalfiltermanualtwofileloading = true;
           filterList.splice(index, 1);
           const filterData = filterList.length > 0 ? filterList : [];	
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${info.collection}`;
           try {
           const response = await this.axios.post(Apipath,{ filter });
           if (response.data.status === 'success') {
             this.manualglobaltwofileList = [...filterList];
             this.manualglobaltwofileArray = [...filterList];
             this.formateddataforfilter = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0]?.totalCount[0]?.count);
             
           }
           } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.globalfiltermanualtwofileloading = false;
         }
         },
         
     
         async runfilter(operation, range, range1, range2, selectFilter, typeOffilter, selectcollection, selectkey,mathcfilter,matchtypefilter) {
           this.page = 1;
           this.pagenew = 1;
           
           if (!this.validateFilter(operation, range, typeOffilter, range1, range2)) {
             return;
           }
           const filterObj = this.createFilterObject(operation, range, range1, range2, selectFilter, typeOffilter);
           this.globalArray.push(filterObj);
                      
           if ((mathcfilter === 'Manual') && (matchtypefilter === 'twofile' || 'single')) {
           const query = selectkey ? { "combo-code": selectkey } : null;
           this.globalfilterquery = query;
           const filter = {
             data: this.globalArray,
             limit: 50,
             page:1, 
             };
             
     
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${selectcollection}`;
           
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
             if (response.data.status === 'success') {
             this.filterArray.push(filterObj);
             if (this.matchTypeFilter === 'twofile') {
               this.GlobaleCollection = {collection:selectcollection,id:null,key:query,manual:"ManualMatch",type:"Old"}
               this.formateddataforfilter = response.data.data[0].data;
               this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
               // this.formateddataforfilter = response.data.data;
             } else {
               // this.formateddataforfilterNew = response.data.data;
               this.GlobaleCollection = {collection:selectcollection,id:null,key:query,manual:"ManualMatch",type:"New"}
               this.formateddataforfilterNew = response.data.data[0].data;
               this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
             }
             this.globalfilterObjecterror = {};
             } else {
             throw new Error(response.data.message || 'Error fetching filter properties');
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.selectfilterdialogloading = false;
             this.globalfilterObject = {};
             this.globalfilterObjecterror = {};
             
           }
               
           } else {
           const query = selectkey ? { "combo-code": selectkey } : null;
           const filter = { 
             data: this.globalArray,
             limit: 50,
             page:1, 
             };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${selectcollection}`;
     
           try {
             const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
             if (response.data.status === 'success') {
             this.filterArray.push(filterObj);
             if (selectkey) {
               // this.newAnnexersdata = response.data.data;
               this.newAnnexersdata = response.data.data[0].data;
               this.storeTotalnew(response.data.data[0].totalCount[0]?.count);
             } else {
               this.currentannexuredata = response.data.data[0].data;
               this.storeTotal(response.data.data[0].totalCount[0]?.count);
             }
             this.globalfilterObjecterror = {};
             } else {
             throw new Error(response.data.message || 'Error fetching filter properties');
             }
           } catch (err) {
             this.$store.commit('sbError', err.message || 'Unknown error');
             console.error(err);
           } finally {
             this.selectfilterdialogloading = false;
             this.globalfilterObject = {};
             this.globalfilterObjecterror = {};
             
           }
           }
           },
         globalfilterpopClose() {
           this.globalfilterObject = {};
           this.globalfilterObjecterror = {};
           this.filterArray = [];
           this.globalArray = [];
           this.selectfilterdialog = false;
         },
         async reomveGobalefilter(collection,selectkey,mathcfilter,matchtypefilter) {
           this.pagenew = 1;
           
           
         if ((mathcfilter === 'Manual') && (matchtypefilter === 'twofile' || 'single')) {
           const query = selectkey ? { "combo-code": selectkey } : null;
         const filter = { data: [],
           limit: 50,
           page:1, 
         };
         const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection}`;
     
         try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           
           if (response.data.status === 'success') {
           this.filterArray = [];
           this.globalArray = [];
           this.globalfilterObjecterror = {};
           this.selectfilterdialog = false;
           if ('single' !== matchtypefilter) {
             this.GlobaleCollection = {collection:collection,id:null,key:query,manual:"ManualMatch",type:"Old"}
             // this.formateddataforfilter = response.data.data;
             this.formateddataforfilter = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
           } else {
             this.GlobaleCollection = {collection:collection,id:null,key:this.querydetails,manual:"ManualMatch",type:"New"}
             
             // this.formateddataforfilterNew = response.data.data;
             this.formateddataforfilterNew = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
           }
           } else {
           this.newAnnexersdata = [];
           this.currentannexuredata = [];
           throw new Error(response.data.message || 'Error fetching filter properties');
           }
         } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.selectfilterdialogloading = false;
           this.selectfilterdialog = false;
         }
         } else {
           const query = selectkey ? { "combo-code": selectkey } : null;
         const filter = { data: [],
                 limit: 50,
                 page:1,  
               };
         const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection}`;
     
         try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
           this.filterArray = [];
           this.globalArray = [];
           this.globalfilterObjecterror = {};
           this.selectfilterdialog = false;
           if (selectkey) {
             // this.newAnnexersdata = response.data.data;
             this.newAnnexersdata = response.data.data[0].data;
             this.storeTotalnew(response.data.data[0].totalCount[0]?.count);
           } else {
             // this.currentannexuredata = response.data.data;
             this.currentannexuredata = response.data.data[0].data;
             this.storeTotal(response.data.data[0].totalCount[0]?.count);
           }
           } else {
           this.newAnnexersdata = [];
           this.currentannexuredata = [];
           throw new Error(response.data.message || 'Error fetching filter properties');
           }
         } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.selectfilterdialogloading = false;
           this.selectfilterdialog = false;
           this.page = 1;
         }
         }
         
         },
         async deleteGobalefilter(filarr, index, collection, selectkey,mathcfilter,matchtypefilter) {
           
           if (this.mathcFilter === 'Manual') {
             
           this.selectedannexure = collection;
           filarr.splice(index, 1);
           const query = selectkey ? { "combo-code": selectkey } : null;
           const filterData = filarr.length > 0 ? filarr : [];	
           const filter = {
             data: filterData,
             limit: 50,
             page:1,  
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection}`;
           this.selectfilterdialogloading = true;
           try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
             this.filterArray = [...filterData];
             this.globalArray = [...filterData];
           if ('single' !== matchtypefilter) {
             this.GlobaleCollection = {collection:collection,id:null,key:query,manual:"ManualMatch",type:"Old"}
             // this.formateddataforfilter = response.data.data;
             this.formateddataforfilter = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
             } else {
             this.GlobaleCollection = {collection:collection,id:null,key:query,manual:"ManualMatch",type:"New"}
             // this.formateddataforfilterNew = response.data.data;
             this.formateddataforfilterNew = response.data.data[0].data;
             this.storeTotalnewmatchnew(response.data.data[0].totalCount[0]?.count);
             }
             this.selectfilterdialogloading = false;
           } else {
           this.newAnnexersdata = [];
           this.currentannexuredata = [];
           throw new Error(response.data.message || 'Error fetching filter properties');
           }
         } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.selectfilterdialogloading = false;
         }
           } else {
           this.selectedannexure = collection;
           filarr.splice(index, 1);
           const query = selectkey ? { "combo-code": selectkey } : null;
           const filterData = filarr.length > 0 ? filarr : [];	
           const filter = {
             data: filterData,
             limit: 50,
             page:1,
             
           };
           const Apipath = `/v2/bot/analysis/readannexurewithfilter/${collection}`;
           this.selectfilterdialogloading = true;
           try {
           const response = await this.axios.post(Apipath, query ? { filter, query } : { filter });
           if (response.data.status === 'success') {
             this.filterArray = [...filterData];
             this.globalArray = [...filterData];
             if (selectkey) {
               
             // this.newAnnexersdata = response.data.data;
             this.newAnnexersdata = response.data.data[0].data;
             this.storeTotalnew(response.data.data[0].totalCount[0]?.count);
             } else {
             // this.currentannexuredata = response.data.data;
             this.currentannexuredata = response.data.data[0].data;
             this.storeTotal(response.data.data[0].totalCount[0]?.count);
             }
             this.selectfilterdialogloading = false;
           } else {
           this.newAnnexersdata = [];
           this.currentannexuredata = [];
           throw new Error(response.data.message || 'Error fetching filter properties');
           }
         } catch (err) {
           this.$store.commit('sbError', err.message || 'Unknown error');
           console.error(err);
         } finally {
           this.selectfilterdialogloading = false;
         }
           }
         
         },
     isValidDatetrue(dateString) {
         const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
     
         if (isoDateTimeRegex.test(dateString)) {
             let date = new Date(dateString);
             if (!isNaN(date.getTime())) {
                 return true; 
             }
         }
         return false;
     },
     convertToDateTimeString(dateString) {
         if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
             return `${dateString}T00:00:00`;
         }
         return dateString;
     },
     deleteAIsetup(name,index,type){
     
       if (type === "string") {
              this.setupaiObj[name] = { matchpercentage: 0 }; // Default for string
             } else if (type === "number") {
               this.setupaiObj[name] = { matchpercentage: 500, oppositesignmatch: false }; // Default for number
             } else if (type === "date") {
               this.setupaiObj[name] =  { matchpercentage: 365, }; // Default for date
             }
     
       
     //  if (this.setupaiObj[name]) {
     //         this.$delete(this.setupaiObj, name); 
     //       }
           if (this.copycolumns[index]) {
             this.$delete(this.copycolumns, index); 
           }
     },
     startPolling(value) {
           if (this.intervalId) return; // Avoid multiple intervals
           this.intervalId = setInterval(() => {
            if (value === 9) {
             this.loadBotAi();
            } else {
             this.loadBot();
            }
          // Replace with your actual function
           }, 30000);
         },
     stopPolling() {
           if (this.intervalId) {
             clearInterval(this.intervalId);
             this.intervalId = null;
           }
         },
 FilesUpload(files) {
  try {
    this.file = files;

    // Validate total size of all files
    const totalSize = files.reduce((size, file) => size + (file.size || 0), 0);
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

    if (totalSize > MAX_FILE_SIZE) {
      this.file = [];
      this.$store.commit("sbError", "Total file size exceeds the limit (50 MB).");
      return;
    }

    // Validate individual file extensions
    const allowedExtensions = ["xlsx", "xls", "xlsm", "xlsb"];
    if (Array.isArray(files) && files.length > 0) {
      this.loading = true;

      const validationPromises = files.map((file) => {
        const fileExtension = (file?.name || "").split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
          throw new Error(
            `File '${file.name}' has an unsupported extension. Only ${allowedExtensions.join(", ")} are allowed.`
          );
        }
      });

      // Process validations
      Promise.all(validationPromises)
        .then(() => {
          this.file = files;
        })
        .catch((error) => {
          this.file = [];
          this.$store.commit("sbError", error.message || "Error validating files.");
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  } catch (error) {
    this.file = [];
    this.$store.commit("sbError", error.message || "Unexpected error occurred.");
    console.error("Unexpected error:", error);
  }
},
 async manualmatchingFunTwofile() {
    this.loading = true;
    let files = this.file;
  
    let arrayPush = [];
    if (Array.isArray(files) && files.length > 0) {
          const uploadPromises = files.map(async (file) => {
                        try {
                          let dt = await this.$nova.uploadFile(this.axios, file, {});
                           arrayPush.push(dt);
                        } catch (err) {
                            this.$store.commit("sbError", err.message || err || "Unknown error!");
                            console.error(err);
                        }
                    });
                    await Promise.all(uploadPromises);
                    if (arrayPush.some((response) => response.data.status === "success")) {
                    this.dataArray = arrayPush.flatMap((response) => response.data.data.map((item) => {
                       return { "filename": item.uploadname, "file_id": item._id , "file_path":item.path}
                    })
                    );
                  }
                  
                  try {
                    await this.bulkManualMatchTwoFile({
                      uploadData: this.dataArray,
                      tmaId: this.analysisDetails._id,
                      runid: this.rundetails[this.verificationlist[0]?._id]?._id,
                      clientCode: this.$store.state.clientCode?.toString() || '',
                    });
                    this.bulkMatchtwofile = false;
                    this.refreshData();
                  } catch (err) {
                    console.error('[TransactionMatchingConfigNew]: bulkManualMatchTwoFile error', err);
                  } finally {
                    this.loading = false;
                  }

    } else{
      this.loading = false;
      this.$store.commit("sbError", "No file select or worng file seleced");
    }
},

FilesUploadOne(files) {
  try {
    this.file = files;

    // Validate total size of all files
    const totalSize = files.reduce((size, file) => size + (file.size || 0), 0);
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

    if (totalSize > MAX_FILE_SIZE) {
      this.file = [];
      this.$store.commit("sbError", "Total file size exceeds the limit (50 MB).");
      return;
    }

    // Validate individual file extensions
    const allowedExtensions = ["xlsx", "xls", "xlsm", "xlsb"];
    if (Array.isArray(files) && files.length > 0) {
      this.loading = true;

      const validationPromises = files.map((file) => {
        const fileExtension = (file?.name || "").split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
          throw new Error(
            `File '${file.name}' has an unsupported extension. Only ${allowedExtensions.join(", ")} are allowed.`
          );
        }
      });

      // Process validations
      Promise.all(validationPromises)
        .then(() => {
          this.file = files;
        })
        .catch((error) => {
          this.file = [];
          this.$store.commit("sbError", error.message || "Error validating files.");
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  } catch (error) {
    this.file = [];
    this.$store.commit("sbError", error.message || "Unexpected error occurred.");
    console.error("Unexpected error:", error);
  }
},
 async manualmatchingFunOnefile() {
   this.loading = true;
    let files = this.file;
  
    let arrayPush = [];
    if (Array.isArray(files) && files.length > 0) {
          const uploadPromises = files.map(async (file) => {
                        try {
                          let dt = await this.$nova.uploadFile(this.axios, file, {});
                           arrayPush.push(dt);
                        } catch (err) {
                            this.$store.commit("sbError", err.message || err || "Unknown error!");
                            console.error(err);
                        }
                    });
                    await Promise.all(uploadPromises);
                    if (arrayPush.some((response) => response.data.status === "success")) {
                    this.dataArray = arrayPush.flatMap((response) => response.data.data.map((item) => {
                      return { "filename": item.uploadname, "file_id": item._id , "file_path":item.path}
                    })
                    );
                  }
                  let data = {
                        upload: this.dataArray,
                        tmaid: this.analysisDetails._id,
                    };
                  this.axios.post("/v2/financialclose/analysis/bulkupdatetransactionmatching/"+this.metadata._id, {
                 data: data
               })
               .then((ele) => {
                 if (ele.data.status === "success") {
                  this.bulkMatchonefile = false;
                   this.$store.commit("sbSuccess", "Manual matching bulk upload successfully saved");
                   this.refreshData();
                 } else {
                     throw new Error(ele.data.message);
                 }
               })
               .catch((err) => {
                 this.$store.commit("sbError", err.message || err || "Unknown error!");
                 console.log(err);
               })
               .finally(() => {
                  this.loading = false;
               });

    } else{
      this.loading = false;
      this.$store.commit("sbError", "No file select or worng file seleced");
    }
},
// recoinSummaryResultCombo - MOVED to TransactionMatchingReconciliation.vue component
     
       },
       computed: {
         backlink() {
           return "/financialclose/automationtransactionmatching/" + (this.$route?.params?.id || '');
         },
         ...mapGetters('transactionMatching', {
           downloadExcelHistory: 'downloadHistory',
           loadingExcelHistory: 'isLoadingExcelHistory',
           formData: 'formData',
           // Note: You might map isLoading if needed, but component has its own 'loading' used elsewhere.
         }),
         /**
          * Check if consolidated annexure collection exists in comboAnnexer
          * @returns {Boolean} True if consolidated annexure is present
          */
         hasConsolidatedAnnexure() {
           return this.comboAnnexer.some(item => 
             item.name === 'Consolidated Annexure' && item.collection
           );
         },
         /**
          * Get the first V2 rundetail object from the raw rundetails array.
          * Used to pass to TransactionMatchingResultsV2 which expects a single object.
          * @returns {Object} V2 rundetail object or empty object
          */
         rundetailsV2() {
           if (!this.rundetailsall || !Array.isArray(this.rundetailsall)) return {};
           return this.rundetailsall.find(r => r.version === 'v2') || this.rundetailsall[0] || {};
         },
         /**
          * Check if aggregation is mandatory based on selected number columns
          * @returns {Boolean} True if any number column is selected
          */
         isAggregationMandatory() {
           // Only applicable for M:1, 1:M, M:M relations
           if (!['M:1', '1:M', 'M:M'].includes(this.addeditdata.type)) {
             return false;
           }
           // Check if any selected column is a number type
           for (const k of this.selectedrulecolumn) {
             const col = this.columns[k];
             if (col && col.type === 'number') {
               return true;
             }
           }
           return false;
         },
         /**
          * Check if aggregation validation passes
          * @returns {Boolean} True if validation passes (at least one aggregation value when mandatory)
          */
         hasValidAggregation() {
           if (!this.isAggregationMandatory) {
             return true;
           }
           // Check if at least one aggregation value exists
           for (const k in this.aggregationtypesection) {
             if (Object.hasOwnProperty.call(this.aggregationtypesection, k)) {
               const val = this.aggregationtypesection[k];
               // Handle string values (from dropdown) and array values
               if (val && (typeof val === 'string' ? val.length > 0 : (Array.isArray(val) ? val.length > 0 : Boolean(val)))) {
                 return true;
               }
             }
           }
           return false;
         },
         columnsArray() {
           // Convert columns object to an array for v-for usage
           return Object.values(this.columns);
         },
         compareObjects() {
           if (this.filterArray.length > 0) {
             return this.filterArray.some((obj) => obj.column === this.selectFilter);
           } else {
             return false
           }
     
         },
         filterItem() {
           return Object.keys(this.pageItems()[0] || []);
         },
         annexercomboKeys() {
           return this.newannexerCombodata;
         },
         sortedData() {
           const data = this.fifoexecutionResult.slice(1);
           return data.sort((a, b) => {
             const aValue = a[this.sortColumn];
             const bValue = b[this.sortColumn];
     
             if (typeof aValue === 'undefined' || aValue === null) return -1;
             if (typeof bValue === 'undefined' || bValue === null) return 1;
     
             // Convert values to numbers if possible
             const numA = parseFloat(aValue);
             const numB = parseFloat(bValue);
     
             if (!isNaN(numA) && !isNaN(numB)) {
               // Both values are numbers, sort numerically
               return this.sortDesc ? numB - numA : numA - numB;
             } else {
               // Use localeCompare for strings or mixed types
               return this.sortDesc ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
             }
           });
         },
         
         /**
          * Collect all unique keys from all records in tableOnetwofile
          * Ensures columns like 'Manual_Tag' are shown even if not present in first record
          */
         tableOneItemtwofileKeys() {
           if (!this.tableOnetwofile || this.tableOnetwofile.length === 0) return [];
           const allKeys = new Set();
           this.tableOnetwofile.forEach(item => {
             Object.keys(item).forEach(key => allKeys.add(key));
           });
           return Array.from(allKeys);
         },
         
         /**
          * Collect all unique keys from all records in tableTwotwofile
          * Ensures columns like 'Manual_Tag' are shown even if not present in first record
          */
         tableTwoItemtwofileKeys() {
           if (!this.tableTwotwofile || this.tableTwotwofile.length === 0) return [];
           const allKeys = new Set();
           this.tableTwotwofile.forEach(item => {
             Object.keys(item).forEach(key => allKeys.add(key));
           });
           return Array.from(allKeys);
         },
         
         /**
          * Collect all unique keys from all records in tableOne
          * Ensures columns like 'Manual_Tag' are shown even if not present in first record
          */
         tableOneItemKeys() {
           if (!this.tableOne || this.tableOne.length === 0) return [];
           const allKeys = new Set();
           this.tableOne.forEach(item => {
             Object.keys(item).forEach(key => allKeys.add(key));
           });
           return Array.from(allKeys);
         },
         
         /**
          * Collect all unique keys from all records in tableTwo
          * Ensures columns like 'Manual_Tag' are shown even if not present in first record
          */
         tableTwoItemKeys() {
           if (!this.tableTwo || this.tableTwo.length === 0) return [];
           const allKeys = new Set();
           this.tableTwo.forEach(item => {
             Object.keys(item).forEach(key => allKeys.add(key));
           });
           return Array.from(allKeys);
         },
         
         /**
          * Collect all unique keys from all records in pageItems
          * Ensures columns like 'Manual_Tag' are shown even if not present in first record
          */
         pageItemsKeys() {
           const items = this.pageItems();
           if (!items || items.length === 0) return [];
           const allKeys = new Set();
           items.forEach(item => {
             Object.keys(item).forEach(key => allKeys.add(key));
           });
           return Array.from(allKeys);
         },

         /**
          * Import/resync action details only — other actiondetails entries are ignored for status pills.
          */
         statusTrackedActionDetails() {
           const details = this.analysisDetails?.actiondetails;
           if (!details || !Array.isArray(details)) return [];
           return details.filter((item) => TM_STATUS_TRACKED_ACTIONS.includes(item.action));
         },

         /**
          * Most recent import/resync action (by created_at/createdAt).
          * This avoids showing an older FAILED item when a newer IN_PROGRESS/COMPLETED exists.
          */
         activeActionStatus() {
           const actions = this.statusTrackedActionDetails;
           if (actions.length === 0) return null;

           const toTime = (a) => {
             const raw = a?.created_at || a?.createdAt || a?.updated_at || a?.updatedAt;
             const t = raw ? Date.parse(raw) : NaN;
             return Number.isFinite(t) ? t : 0;
           };

           const hasAnyTimestamp = actions.some(a => toTime(a) > 0);
           const ordered = hasAnyTimestamp ? [...actions].sort((a, b) => toTime(b) - toTime(a)) : actions;

           // Only consider the most recent action after ordering.
           return ordered[0] || null;
         },

         /** Normalized stage from activeActionStatus (action queue uses TM_ACTION_STAGES, not TM_STATUS). */
         activeActionStage() {
           const stage = this.activeActionStatus?.stage;
           if (stage === null || stage === undefined || stage === '') return null;
           const n = Number(stage);
           return Number.isFinite(n) ? n : null;
         },

         shouldShowStageInfoSection() {
           if (this.activeActionStage === TM_ACTION_STAGES.FAILED) return true;
           return Object.keys(this.rundetails).length > 0;
         },

         shouldShowStageInfoControls() {
           if (this.activeActionStage === TM_ACTION_STAGES.FAILED) return true;
           if (this.statusCheck === TM_STATUS.FAILED) return true;
           return (this.processedFiles || !this.hasFiles) && (this.savedParameters || !this.hasParameters) && this.metadata.stage !== 3;
         },

         shouldShowStageInfoMenu() {
           if (this.activeActionStage === TM_ACTION_STAGES.FAILED) return true;
           if (this.statusCheck === TM_STATUS.FAILED) return true;
           return this.statusCheck !== TM_STATUS.FINISHED;
         },

         actionFailedStatusLabel() {
           const action = this.activeActionStatus?.action;
           if (action === 'import_analysis_data') return 'Import Failed';
           if (action === 'resync_analysis_data') return 'Resync Failed';
           return 'Import/Resync Failed';
         },

         stageInfoMessage() {
           if (this.activeActionStage === TM_ACTION_STAGES.FAILED) {
             const detail = this.activeActionStatus?.message || this.activeActionStatus?.error || '';
             return detail ? `${this.actionFailedStatusLabel}: ${detail}` : this.actionFailedStatusLabel;
           }
           if (this.statusCheck === TM_STATUS.FAILED) {
             return this.stageMassage ? `Run Failed: ${this.stageMassage}` : 'Run Failed';
           }
           return this.stageMassage;
         },

         /**
          * When an import/resync action is still running (queued/in-progress/failed),
          * its status must override the run status to avoid showing "Finished" prematurely.
          */
         shouldShowActionStatus() {
           // While refreshing, hide pills to avoid rendering stale run/action status snapshots.
           if (!this.initialDataLoaded) return false;
           if (!this.activeActionStatus) return false;

           // Always show non-completed action stages (these override run status)
           if ( this.activeActionStage != null && this.activeActionStage !== TM_ACTION_STAGES.COMPLETED) return true;

           // Show "action finished" only if there is no run status pill to show yet
           return Object.keys(this.rundetails).length === 0;
         },

         /** Show run status only when no blocking action is active. */
         shouldShowRunStatus() {
           // While refreshing, hide pills to avoid rendering stale run/action status snapshots.
           if (!this.initialDataLoaded) return false;
           if (Object.keys(this.rundetails).length === 0) return false;

           return !(
             this.activeActionStage != null && this.activeActionStage !== TM_ACTION_STAGES.COMPLETED
           );
         },

         /**
          * Check if run buttons should be disabled
          * Disables when statusCheck is 12 (Manual Matching In Progress) 
          * and run was updated less than 1 hour ago
          * @returns {Boolean} True if buttons should be disabled
          */
         isRunDisabled() {
             // Disable run if any file is still queued or processing (stage 16 or 17)
             if (this.fileUploadStatusFiles.some(
               f => f.stage === FILE_STAGES.QUEUED || f.stage === FILE_STAGES.PROCESSING
             )) {
               return true;
             }

             // Disable run if the most recent import/resync action is queued, in-progress, or failed
             if (
               this.activeActionStage === TM_ACTION_STAGES.QUEUED ||
               this.activeActionStage === TM_ACTION_STAGES.IN_PROGRESS ||
               this.activeActionStage === TM_ACTION_STAGES.FAILED
             ) {
               return true;
             }

             // Check if statusCheck is 12 (MANUAL_MATCHING_IN_PROGRESS)
             if (this.statusCheck !== TM_STATUS.MANUAL_MATCHING_IN_PROGRESS) {
             return false;
           }
           
           // Check if runDetailUpdatedAt exists
           if (!this.runDetailUpdatedAt) {
             return false;
           }
           
           // Calculate time difference in milliseconds
           const currentTime = new Date().getTime();
           const updatedTime = new Date(this.runDetailUpdatedAt).getTime();
           const timeDifferenceInMs = currentTime - updatedTime;
           
           // Disable if less than 1 hour has passed
           return timeDifferenceInMs < MS_PER_HOUR;
         },

         
       },
       watch: {
         '$route.params.id'(newId, oldId) {
           if (!newId || !oldId || newId === oldId) return;
           if (!this.$route.params.anaid) return;
           this.$nova.gotoLink({
             path: `/financialclose/automationtransactionmatching/${newId}`,
             query: this.$route.query,
           });
         },
         /**
          * Clear aggregation error when validation passes
          */
         hasValidAggregation(newVal) {
           if (newVal) {
             this.aggregationError = '';
           }
         },
         "selectedSummeryFilter"(d){
           if (d === null || d === undefined) {
             this.loading = false;
             return;
           } else if (d === 'Summary') {
             let selectedverification = this.verificationlist[0] || {};
             this.generateAnnexurelistSummery(this.selectedannexure,selectedverification._id);
             this.selectedSummery = 1;
           } else {
             const index = this.summeryCombo.findIndex(item => item['combo-code'] === d);
             this.selectedSummery = index;
             this.generateAnnexurelistSummeryList(this.selectedannexure,d);
           }
           
           
         },
         // "selectedReconciliationFilter" - MOVED to TransactionMatchingReconciliation component
         "selectedSummery"(d){
           if (d === null || d === undefined) {
             this.loading = false;
             return;
           } else if (d === 0) {
             let selectedverification = this.verificationlist[0] || {};
             this.generateAnnexurelistSummery(this.selectedannexure,selectedverification._id);
           } else {
             this.generateAnnexurelistSummeryList(this.selectedannexure,this.summeryCombo[d]['combo-code']);
           }
     
           
           
         },
         // "selectedReconciliation" - MOVED to TransactionMatchingReconciliation component
         "stagefilter"(d) {
     
           if (d.length === 0) {
             this.currentannexuredata = [...this.originalcurrentannexuredata];
             return;
           }
           const ndt = this.originalcurrentannexuredata.filter(item =>
             (d.includes(1) && item["Match Nature"] === "Automated") ||
             (d.includes(0) && item["Match Nature"] === "Suggested")
           );
           this.currentannexuredata = ndt.length > 0 ? ndt : [this.createEmptyObject(this.originalcurrentannexuredata[0])];
     
           // if (d.length === 0) {
           // 	this.currentannexuredata = this.originalcurrentannexuredata;
           // 	return;
           // }
     
           // const isTableMatched = ['Table 1 Matched', 'Table 2 Matched'].includes(this.annexurenames[this.selectedannexure]);
           // if (!isTableMatched) return;
     
           // const ndt = this.annexuredata[this.selectedannexure].filter(item =>
           // 	(d.includes(1) && item["Match Nature"] === "Automated") ||
           // 	(d.includes(0) && item["Match Nature"] === "Suggested")
           // );
     
           // this.currentannexuredata = ndt.length > 0 ? ndt : [this.createEmptyObject(this.annexuredata[this.selectedannexure][0])];
         },
         "stagetable1twofile"(d){
           // this.tableOnetwofile = tableData,
           // this.tableOneCopytwofile = [...this.tableOnetwofile],
           if (d.length === 0) {
           return this.tableOnetwofile = [...this.tableOneCopytwofile];
           }
           const ndt = this.tableOneCopytwofile.filter(item =>
             (d.includes(1) && item["Match Nature"] === "Automated") ||
             (d.includes(0) && item["Match Nature"] === "Suggested")
           );
           this.tableOnetwofile = ndt.length > 0 ? ndt : [this.createEmptyObject(this.tableOneCopytwofile[0])];
         },
         "stagetable2twofile"(d){
           // this.tableOnetwofile = tableData,
           // this.tableOneCopytwofile = [...this.tableOnetwofile],
           if (d.length === 0) {
           return this.tableTwotwofile = [...this.tableTwoCopytwofile];
           }
           const ndt = this.tableTwoCopytwofile.filter(item =>
             (d.includes(1) && item["Match Nature"] === "Automated") ||
             (d.includes(0) && item["Match Nature"] === "Suggested")
           );
           this.tableTwotwofile = ndt.length > 0 ? ndt : [this.createEmptyObject(this.tableTwoCopytwofile[0])];
         },
         "stagefiltertable1"(d){
           if (d.length === 0) {
           return this.tableOne = [...this.tableOneCopy];
           }
           const ndt = this.tableOneCopy.filter(item =>
             (d.includes(1) && item["Match Nature"] === "Automated") ||
             (d.includes(0) && item["Match Nature"] === "Suggested")
           );
           this.tableOne = ndt.length > 0 ? ndt : [this.createEmptyObject(this.tableOneCopy[0])];
         },
         "stagefiltertable2"(d){
           if (d.length === 0) {
           return this.tableTwo = this.tableTwoCopy;
           }
           const ndt = this.tableTwoCopy.filter(item =>
             (d.includes(1) && item["Match Nature"] === "Automated") ||
             (d.includes(0) && item["Match Nature"] === "Suggested")
           );
           this.tableTwo = ndt.length > 0 ? ndt : [this.createEmptyObject(this.tableTwoCopy[0])];
         },
         "stagefilterNew"(d) {
           if (d.length === 0) {
             this.newAnnexersdata = [...this.originalAnnexersdata];
             return;
           }
     
           const ndt = this.originalAnnexersdata.filter(item =>
             (d.includes(1) && item["Match Nature"] === "Automated") ||
             (d.includes(0) && item["Match Nature"] === "Suggested")
           );
     
           this.newAnnexersdata = ndt.length > 0 ? ndt : [this.createEmptyObject(this.originalAnnexersdata[0])];
         },
         "filterobject.tableid": function(d) {
           this.filtercolums = [];
           if (this.fileConfigInfo[d]) {
             for (let index = 0; index < this.fileConfigInfo[d].columnmap.length; index++) {
               this.filtercolums.push(this.fileConfigInfo[d].columnmap[index][1]);
             }
           }
     
         },
         selectedCombo: function(d) {
           this.table1pageinationEmpty();
           this.table2pageinationEmpty();
           this.ailoadingimage = true;
           if (d === null || d === undefined) {
             this.loading = false;
             this.ailoadingimage = false;
             return;
           }
           let combokey = this.newannexerCombodata[d]?.comboCode;
           const requestData = [
             { collection: this.selectedCollectionAll.collection1 },
             { collection: this.selectedCollectionAll.collection2 }
           ];
           this.stagefiltertable1 = [];
           this.stagefiltertable2 = [];
     
           const fetchTableData = (data, tableIndex) => {
             return this.axios
               .post(`/v2/financialclose/analysis/getdatabycombocode/${combokey}`, {
                 data,
                 filter: { limit: 50, page: 1 }
               })
               .then((dt) => {
                 if (dt.data.status === "success") {
                   const tableData = dt.data.data[0]?.data;
                   const totalCount = dt.data.data[0]?.totalCount[0]?.count;
                   if (tableIndex === 1) {
                     this.tableOne = tableData;
                     this.table1Total(totalCount);
                     this.tableOneCopy = [...this.tableOne];
                     this.table1collection = { collection : this.selectedCollectionAll.collection1, key : combokey};
                   } else {
                     this.tableTwo = tableData;
                     this.table2Total(totalCount);
                     this.tableTwoCopy = [...this.tableTwo];
                     this.table2collection = { collection : this.selectedCollectionAll.collection2, key : combokey};
                   }
                 }
               })
               .catch((err) => {
                 this.$store.commit("sbError", err.message || err || "Unknown error!");
                 console.log(err);
               });
           };
           Promise.all([
             fetchTableData(requestData[0], 1),
             fetchTableData(requestData[1], 2)
           ])
           .finally(() => {
             this.ailoadingimage = false;
           });
         },
         "selectedCombotwo": function(d) {
           if (d === null || d === undefined) {
             return;
           }
         },
         "filterCombo": function(d) {
           if (d === null || d === undefined) {
             return;
           }
           const index = this.newannexerCombodata.findIndex(item => item.comboCode === d);
           this.selectedCombo = index;
         },
         "searchvalueNew": function(d) {
           this.filterMatchItemsNewfilter(d);
         },
         "searchvalue": function(d) {
           this.filterMatchItemsNewfilterTwo(d);
         },
         polingstatges(newVal) {
          const pollableStatuses = [
            TM_STATUS.NOT_STARTED,
            TM_STATUS.IN_PROGRESS,
            TM_STATUS.QUEUED,
            TM_STATUS.AI_IN_PROGRESS,
            TM_STATUS.MANUAL_MATCHING_IN_PROGRESS,
          ];
          if (pollableStatuses.includes(newVal)) {
            this.startPolling(newVal);
          } else {
            this.stopPolling();
          }
        },
      }

    };
</script>

<style lang="scss" scoped>
@import './TransactionMatchingConfig.scss';
</style>
